/// <reference types="vite/client" />
import React, { useEffect, useRef, useState } from "react";
import { MORPHS, type Morph } from "./axolotl_morphs";
import { drawLippie, type Face } from "./axolotl_lippies_art";
import {
  BOSS_HP,
  LEVEL_CAP,
  WILD_HP,
  fighterFromOwned,
  gainXp,
  makeFighter,
  moveElement,
  newBattle,
  newOwned,
  ownedMaxHp,
  playTurn,
  roll,
  winXp,
  xpToNext,
  type Action,
  type BEvent,
  type Battle,
  type Fighter,
  type Side,
} from "./axolotl_lippies_battle";
import {
  AREAS,
  AREA_NAMES,
  BACKGROUNDS,
  CLASSES,
  ELEMENTS,
  FORMS,
  ITEMS,
  MOVES,
  SPECIES,
  DEFAULT_TAMER,
  HAIR_COLORS,
  HAIR_STYLES,
  HATS,
  MOONSTONE_LINES,
  NAME_MAX,
  NPCS,
  RIVAL_STARTER,
  STARTERS,
  STORY,
  OUTFIT_COLORS,
  SKIN_TONES,
  TAMER_NAMES,
  TOTAL_LIPPIES,
  WALKABLE,
  effectiveness,
  formName,
  isSaveData,
  speciesOf,
  newSave,
  type AreaDef,
  type AreaId,
  type Dir,
  type ElementId,
  type NpcPlacement,
  type OwnedLippie,
  type SaveData,
  type SpeciesDef,
  type TamerLook,
} from "./axolotl_lippies_data";

// A small creature-collecting adventure. See PLAN_lippies.md for the full design.

const W = 960;
const H = 540;
const STEP = 1 / 120;
const TAU = Math.PI * 2;
const SLOTS = 3;
const slotKey = (i: number) => `axolotl_lippies_slot${i + 1}_v1`;
const TIP_KEY = "axolotl_lippies_ipad_tip_v1";
const CODE_PREFIX = "LIPPIES1:";
const AUTOSAVE_EVERY = 60;

// ---------- math / noise ----------

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);


function hash1(i: number, seed: number) {
  const x = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

function noise1(x: number, seed: number) {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f);
  return hash1(i, seed) * (1 - u) + hash1(i + 1, seed) * u;
}

function fbm(x: number, seed: number, oct = 4) {
  let amp = 0.5;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < oct; o++) {
    sum += amp * noise1(x, seed + o * 17);
    norm += amp;
    x *= 2.03;
    amp *= 0.5;
  }
  return sum / norm;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0.4, r), 0, TAU);
  ctx.fill();
}

// ---------- saves ----------

let persistAsked = false;

function loadSlot(i: number): SaveData | null {
  try {
    const v = JSON.parse(localStorage.getItem(slotKey(i)) || "null");
    return isSaveData(v) ? v : null;
  } catch {
    return null;
  }
}

function writeSlot(i: number, d: SaveData) {
  try {
    localStorage.setItem(slotKey(i), JSON.stringify(d));
  } catch (e) {
    console.warn("Lippies: could not save", e);
  }
  if (!persistAsked) {
    persistAsked = true;
    void navigator.storage?.persist?.().catch(() => false);
  }
}

function deleteSlot(i: number) {
  try {
    localStorage.removeItem(slotKey(i));
  } catch {
    // nothing to clean up
  }
}

function encodeSave(d: SaveData) {
  const bytes = new TextEncoder().encode(JSON.stringify(d));
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return CODE_PREFIX + btoa(bin);
}

// Accepts a pasted code or the contents of an exported .json file.
function decodeSave(raw: string): SaveData | null {
  const s = raw.trim();
  try {
    let json = s;
    if (!s.startsWith("{")) {
      const b64 = (s.startsWith(CODE_PREFIX) ? s.slice(CODE_PREFIX.length) : s).replace(/\s+/g, "");
      const bin = atob(b64);
      json = new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
    }
    const v = JSON.parse(json);
    return isSaveData(v) ? v : null;
  } catch {
    return null;
  }
}

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  if (m < 1) return "< 1 min";
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true || window.matchMedia?.("(display-mode: standalone)").matches;
}

function shouldShowTip() {
  try {
    return isIOS() && !isStandalone() && !localStorage.getItem(TIP_KEY);
  } catch {
    return false;
  }
}

// ---------- state ----------

type Mode = "title" | "create" | "starter" | "world" | "dialog" | "battle" | "party" | "journal" | "evolve" | "ending" | "gallery";

interface Game {
  mode: Mode;
  modeT: number;
  t: number;
  fade: number;
  slots: (SaveData | null)[];
  slot: number;
  save: SaveData | null;
  lastSave: number;
  sel: number;
  kbd: boolean;
  press: string | null;
  tip: boolean;
  touch: boolean;
  area: AreaId;
  p: Player;
  held: Dir[];
  pad: Dir | null;
  padId: number;
  aDown: number;
  dialog: Dialog | null;
  transit: Transit | null;
  banner: { text: string; t0: number } | null;
  toast: { text: string; t0: number } | null;
  npcFace: Record<string, Dir>;
  npcWalk: Record<string, { fx: number; fy: number; t: number }>;
  gal: { sel: number; face: Face; flip: boolean; mono: boolean; big: boolean; midnight: boolean };
  bv: BattleView | null;
  stepsSinceBattle: number;
  evoQueue: { from: string; to: string; name: string; slot: number }[];
  evo: { from: string; to: string; name: string; slot: number; t: number } | null;
  pendingWin: string | null;
  ending: { t: number } | null;
  partySel: { kind: "party" | "pond"; i: number } | null;
  pondPage: number;
  journalSel: number | null;
  starterSel: number;
  musicT: number;
  musicStep: number;
}

function newGame(): Game {
  const slots = Array.from({ length: SLOTS }, (_, i) => loadSlot(i));
  const firstFilled = slots.findIndex((s) => s);
  return {
    mode: "title",
    modeT: 0,
    t: 0,
    fade: 1,
    slots,
    slot: -1,
    save: null,
    lastSave: 0,
    sel: Math.max(0, firstFilled),
    kbd: false,
    press: null,
    tip: shouldShowTip(),
    touch: isIOS() || !!window.matchMedia?.("(pointer: coarse)").matches,
    area: "village",
    p: newPlayer(0, 0, "down"),
    held: [],
    pad: null,
    padId: -1,
    aDown: -1,
    dialog: null,
    transit: null,
    banner: null,
    toast: null,
    npcFace: {},
    npcWalk: {},
    gal: { sel: 0, face: "normal", flip: false, mono: false, big: false, midnight: false },
    bv: null,
    stepsSinceBattle: 0,
    evoQueue: [],
    evo: null,
    pendingWin: null,
    ending: null,
    partySel: null,
    pondPage: 0,
    journalSel: null,
    starterSel: 1,
    musicT: 0,
    musicStep: 0,
  };
}

function enter(S: Game, mode: Mode) {
  S.mode = mode;
  S.modeT = 0;
  S.fade = 1;
  S.press = null;
}

function saveCurrent(S: Game) {
  if (!S.save || S.slot < 0) return;
  S.save.savedAt = Date.now();
  writeSlot(S.slot, S.save);
  S.slots[S.slot] = S.save;
  S.lastSave = S.t;
}

function step(S: Game, dt: number, audio: Audio) {
  S.t += dt;
  S.modeT += dt;
  S.fade = Math.max(0, S.fade - dt * 3);
  if (S.mode === "world" && S.save) {
    S.save.playTime += dt;
    stepWorld(S, dt, audio);
    if (S.t - S.lastSave > AUTOSAVE_EVERY) saveCurrent(S);
  } else if (S.mode === "battle" && S.save) {
    S.save.playTime += dt;
    stepBattle(S, dt, audio);
  } else if (S.mode === "evolve" && S.evo) S.evo.t += dt;
  else if (S.mode === "ending" && S.ending) S.ending.t += dt;
  stepMusic(S, dt, audio);
}

// ---------- audio ----------

function makeAudio() {
  let ac: AudioContext | null = null;
  let master: GainNode | null = null;
  let noise: AudioBuffer | null = null;
  const hiss = (type: BiquadFilterType, freq: number, dur: number, vol: number, attack = 0.01) => {
    if (!ac || !master || !noise) return;
    const now = ac.currentTime;
    const src = ac.createBufferSource();
    src.buffer = noise;
    const f = ac.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(vol, now + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(f).connect(g).connect(master);
    src.start(now);
    src.stop(now + dur + 0.05);
  };
  const tone = (type: OscillatorType, f0: number, f1: number, dur: number, vol: number, delay = 0) => {
    if (!ac || !master) return;
    const now = ac.currentTime + delay;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, now);
    o.frequency.exponentialRampToValueAtTime(f1, now + dur);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(vol, now + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    o.connect(g).connect(master);
    o.start(now);
    o.stop(now + dur + 0.05);
  };
  return {
    unlock() {
      if (!ac) {
        const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return;
        ac = new AC();
        master = ac.createGain();
        master.connect(ac.destination);
        noise = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
        const d = noise.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      }
      if (ac.state === "suspended") void ac.resume();
    },
    setMuted(m: boolean) {
      if (master && ac) master.gain.setTargetAtTime(m ? 0 : 1, ac.currentTime, 0.05);
    },
    tap() {
      tone("triangle", 660, 880, 0.08, 0.12);
    },
    confirm() {
      tone("sine", 523, 528, 0.14, 0.16);
      tone("sine", 784, 790, 0.22, 0.16, 0.09);
    },
    back() {
      tone("triangle", 520, 330, 0.14, 0.12);
    },
    step(alt: boolean) {
      hiss("lowpass", alt ? 700 : 600, 0.06, 0.025);
    },
    rustle() {
      hiss("bandpass", 2600, 0.2, 0.07, 0.03);
    },
    bump() {
      tone("sine", 150, 80, 0.12, 0.14);
    },
    blip() {
      tone("square", 740, 740, 0.025, 0.018);
    },
    whoosh() {
      hiss("bandpass", 900, 0.45, 0.07, 0.18);
    },
    chime() {
      [523, 659, 784, 1046].forEach((f, i) => tone("sine", f, f * 1.01, 0.3, 0.12, i * 0.09));
    },
    dice() {
      for (let i = 0; i < 6; i++) tone("square", 900 + Math.random() * 500, 700, 0.03, 0.03, i * 0.07 + Math.random() * 0.03);
    },
    hit(big: boolean) {
      hiss("lowpass", big ? 900 : 600, 0.25, big ? 0.3 : 0.2, 0.005);
      tone("sine", big ? 200 : 160, 60, 0.2, 0.25);
      if (big) tone("triangle", 1200, 1600, 0.2, 0.08, 0.05);
    },
    heal() {
      [660, 880, 1100].forEach((f, i) => tone("sine", f, f * 1.05, 0.2, 0.09, i * 0.07));
    },
    faint() {
      tone("triangle", 600, 150, 0.6, 0.14);
    },
    levelUp() {
      [523, 659, 784, 1046, 1318].forEach((f, i) => tone("square", f, f, 0.12, 0.05, i * 0.08));
    },
    evolve() {
      for (let i = 0; i < 10; i++) tone("sine", 600 + i * 90, 700 + i * 90, 0.15, 0.06, i * 0.06);
    },
    encounter() {
      [392, 523, 392, 659].forEach((f, i) => tone("square", f, f, 0.1, 0.06, i * 0.09));
    },
    wobble() {
      tone("triangle", 520, 440, 0.08, 0.1);
      hiss("highpass", 3000, 0.05, 0.04);
    },
    jarPop() {
      tone("sine", 880, 1760, 0.15, 0.14);
      [1046, 1318, 1568].forEach((f, i) => tone("sine", f, f, 0.2, 0.08, 0.12 + i * 0.07));
    },
    jarBurst() {
      hiss("highpass", 2400, 0.3, 0.16, 0.005);
      tone("triangle", 600, 200, 0.25, 0.1);
    },
    pickup() {
      [784, 1046, 1318].forEach((f, i) => tone("triangle", f, f, 0.12, 0.08, i * 0.06));
    },
    note(f: number, type: OscillatorType) {
      tone(type, f, f, type === "sine" ? 0.5 : 0.14, type === "sine" ? 0.025 : 0.018);
    },
  };
}

type Audio = ReturnType<typeof makeAudio>;

// ---------- layout / hit-testing ----------

interface Btn {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const CARD_W = 270;
const CARD_H = 220;
const CARD_Y = 192;
const cardX = (i: number) => 45 + i * (CARD_W + 30);
const TIP_BOX = { x: 180, y: 456, w: 600, h: 64 };

function cardButtons(S: Game, i: number): Btn[] {
  const x = cardX(i);
  const y = CARD_Y + 160;
  if (S.slots[i]) {
    return [
      { id: `export:${i}`, x: x + 162, y, w: 44, h: 48 },
      { id: `delete:${i}`, x: x + 212, y, w: 44, h: 48 },
      { id: `play:${i}`, x: x + 14, y, w: 140, h: 48 },
    ];
  }
  return [
    { id: `import:${i}`, x: x + 182, y, w: 74, h: 48 },
    { id: `new:${i}`, x: x + 14, y, w: 160, h: 48 },
  ];
}

function buttons(S: Game): Btn[] {
  if (S.mode === "title") {
    const out: Btn[] = [];
    if (S.tip) out.push({ id: "tip", x: TIP_BOX.x + TIP_BOX.w - 108, y: TIP_BOX.y + 10, w: 96, h: 44 });
    for (let i = 0; i < SLOTS; i++) {
      out.push(...cardButtons(S, i));
      out.push({ id: `${S.slots[i] ? "play" : "new"}:${i}`, x: cardX(i), y: CARD_Y, w: CARD_W, h: CARD_H });
    }
    return out;
  }
  if (S.mode === "world") return S.dialog ? [] : [{ id: "quit", x: W - 194, y: 14, w: 180, h: 48 }];
  if (S.mode === "battle") return battleButtons(S).filter((b) => !b.disabled);
  if (S.mode === "starter") return starterButtons(S);
  if (S.mode === "party") return partyButtons(S).filter((b) => !b.disabled);
  if (S.mode === "journal") return journalButtons(S);
  if (S.mode === "evolve" && S.evo && S.evo.t > 4.2) return [{ id: "ev:next", x: 0, y: 0, w: W, h: H }];
  if (S.mode === "ending" && S.ending && S.ending.t > 6) return [{ id: "end:next", x: 0, y: 0, w: W, h: H }];
  if (S.mode === "gallery") {
    const out: Btn[] = [{ id: "galBack", x: W - 134, y: 10, w: 120, h: 44 }];
    if (S.gal.big) out.push({ id: `gal:${S.gal.sel}`, x: 0, y: 60, w: W, h: H - 60 });
    else FORMS.forEach((_, i) => out.push({ id: `gal:${i}`, ...galCell(i) }));
    return out;
  }
  return [];
}

function hitTest(S: Game, x: number, y: number) {
  return buttons(S).find((b) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) ?? null;
}

// ---------- drawing helpers ----------

function text(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, font: string, color: string, align: CanvasTextAlign = "left") {
  ctx.font = font;
  ctx.textAlign = align;
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.fillText(s, x + 1.5, y + 2);
  ctx.fillStyle = color;
  ctx.fillText(s, x, y);
}

function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r = 12, fill = "rgba(10,20,30,0.45)") {
  ctx.fillStyle = fill;
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();
}

function drawButton(ctx: CanvasRenderingContext2D, S: Game, b: Btn, label: string, color: string, font = "800 18px system-ui, sans-serif") {
  const down = S.press === b.id;
  const dy = down ? 2 : 0;
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  roundRect(ctx, b.x, b.y + 3, b.w, b.h, 14);
  ctx.fill();
  ctx.fillStyle = color;
  roundRect(ctx, b.x, b.y + dy, b.w, b.h, 14);
  ctx.fill();
  if (down) {
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.fill();
  }
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  roundRect(ctx, b.x + 4, b.y + dy + 3, b.w - 8, b.h * 0.38, 10);
  ctx.fill();
  text(ctx, label, b.x + b.w / 2, b.y + dy + b.h / 2 + 7, font, "#ffffff", "center");
}

function drawGill(ctx: CanvasRenderingContext2D, m: Morph, bx: number, by: number, ang: number, len: number, w: number) {
  const ex = bx + Math.cos(ang) * len;
  const ey = by + Math.sin(ang) * len;
  for (let pass = 0; pass < 2; pass++) {
    const col = pass === 0 ? m.outline : m.gill;
    const grow = pass === 0 ? w * 0.5 : 0;
    ctx.strokeStyle = col;
    ctx.fillStyle = col;
    ctx.lineWidth = w + grow;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(ex, ey);
    ctx.stroke();
    for (let j = 0; j < 3; j++) {
      const u = 0.45 + j * 0.22;
      const side = j % 2 === 0 ? 1 : -1;
      const px = bx + Math.cos(ang) * len * u - Math.sin(ang) * w * side;
      const py = by + Math.sin(ang) * len * u + Math.cos(ang) * w * side;
      ctx.beginPath();
      ctx.ellipse(px, py, w * 1.2 + grow / 2, w * 0.7 + grow / 2, ang + side * 0.6, 0, TAU);
      ctx.fill();
    }
    ctx.fillStyle = pass === 0 ? m.outline : m.gillTip;
    ctx.beginPath();
    ctx.arc(ex, ey, w * 0.9 + grow / 2, 0, TAU);
    ctx.fill();
  }
}

// Front-facing axolotl head poking out of the water. waterY = surface line, s = scale (1 ≈ 60px tall head).
function drawAxoPeek(ctx: CanvasRenderingContext2D, m: Morph, x: number, waterY: number, s: number, t: number, phase: number) {
  const bob = Math.sin(t * 1.8 + phase) * 3;
  const blink = Math.sin(t * 1.1 + phase * 3) > 0.985;
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, W, waterY);
  ctx.clip();
  ctx.translate(x, waterY - 20 * s + bob);
  ctx.scale(s, s);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const flap = Math.sin(t * 4 + phase) * 0.08;
  for (let k = 0; k < 3; k++) {
    const a = [-0.75, -0.3, 0.15][k];
    drawGill(ctx, m, -20, -4 + k * 6, Math.PI - a - flap, 18 - k * 2, 3.2);
    drawGill(ctx, m, 20, -4 + k * 6, a + flap, 18 - k * 2, 3.2);
  }
  ctx.fillStyle = m.outline;
  ctx.beginPath();
  ctx.ellipse(0, 0, 27, 22, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = m.body;
  ctx.beginPath();
  ctx.ellipse(0, 0, 25, 20, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.4)";
  ctx.beginPath();
  ctx.ellipse(-8, -12, 9, 4, -0.2, 0, TAU);
  ctx.fill();
  for (const ex of [-9, 9]) {
    if (blink) {
      ctx.strokeStyle = m.eye;
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.arc(ex, 2, 3.5, Math.PI * 1.1, Math.PI * 1.9);
      ctx.stroke();
    } else {
      circle(ctx, ex, 0, 4.2, m.eye);
      circle(ctx, ex - 1.3, -1.5, 1.5, "#ffffff");
      circle(ctx, ex + 1.2, 1.4, 0.7, "#ffffff");
    }
  }
  ctx.globalAlpha = 0.55;
  circle(ctx, -15, 7, 3.5, m.blush);
  circle(ctx, 15, 7, 3.5, m.blush);
  ctx.globalAlpha = 1;
  ctx.strokeStyle = m.outline;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(-5, 8);
  ctx.quadraticCurveTo(-2.5, 11, 0, 8);
  ctx.quadraticCurveTo(2.5, 11, 5, 8);
  ctx.stroke();
  ctx.restore();

  const rip = (t * 0.6 + phase) % 1;
  ctx.strokeStyle = `rgba(255,255,255,${0.55 * (1 - rip)})`;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(x, waterY + 2, (30 + rip * 26) * s, (5 + rip * 4) * s, 0, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = "rgba(255,255,255,0.6)";
  ctx.beginPath();
  ctx.ellipse(x, waterY + 1, 28 * s, 4 * s, 0, 0, TAU);
  ctx.stroke();
}

// ---------- tamer ----------

const DIRS: Dir[] = ["down", "left", "up", "right"];

function hexRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// amt < 0 darkens toward black, amt > 0 lightens toward white.
function shade(hex: string, amt: number) {
  const [r, g, b] = hexRgb(hex);
  const to = amt < 0 ? 0 : 255;
  const k = Math.abs(amt);
  const f = (c: number) => Math.round(c + (to - c) * k);
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

function inked(ctx: CanvasRenderingContext2D, fill: string, line: string, lw = 2.4) {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = line;
  ctx.lineWidth = lw;
  ctx.stroke();
}

function limb(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, w: number, fill: string, line: string) {
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.strokeStyle = line;
  ctx.lineWidth = w + 4;
  ctx.stroke();
  ctx.strokeStyle = fill;
  ctx.lineWidth = w;
  ctx.stroke();
}

function strap(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, color: string) {
  limb(ctx, x0, y0, x1, y1, 3, color, shade(color, -0.45));
}

const HEAD_Y = -66;
const HEAD_RX = 25;
const HEAD_RY = 23.5;

function headPath(ctx: CanvasRenderingContext2D) {
  ctx.beginPath();
  ctx.ellipse(0, HEAD_Y, HEAD_RX, HEAD_RY, 0, 0, TAU);
}

function drawAccessory(ctx: CanvasRenderingContext2D, look: TamerLook, dir: Dir, layer: "back" | "front") {
  const side = dir === "left" || dir === "right";
  const up = dir === "up";
  const bg = look.background;
  if (bg === "ranger") {
    const draw = () => {
      ctx.save();
      ctx.translate(up ? 17 : side ? 11 : 22, up ? -36 : side ? -42 : -48);
      ctx.rotate(up ? 0.4 : side ? 0.35 : 0.5);
      roundRect(ctx, -5, -14, 10, 28, 3);
      inked(ctx, "#8a5a30", "#4a2e16", 2);
      for (let k = -1; k <= 1; k++) {
        ctx.beginPath();
        ctx.moveTo(k * 3, -14);
        ctx.lineTo(k * 3 - 2.5, -21);
        ctx.lineTo(k * 3 + 2.5, -21);
        ctx.closePath();
        inked(ctx, k === 0 ? "#ffffff" : "#ff6b6b", "#4a2e16", 1.2);
      }
      ctx.restore();
    };
    if (layer === "back" && !up) draw();
    if (layer === "front" && up) draw();
    if (layer === "front" && !side) strap(ctx, up ? 12 : -11, -47, up ? -13 : 14, -24, "#7a4a24");
  } else if (bg === "healer" || bg === "scholar") {
    const bagColor = bg === "healer" ? "#fff4f4" : "#9c6b3d";
    if (layer !== "front") return;
    if (!side) strap(ctx, up ? -12 : 12, -47, up ? 13 : -12, -26, bg === "healer" ? "#d9d2c8" : "#6b4424");
    if (up) return;
    const bx = side ? -2 : -15;
    if (bg === "scholar") {
      ctx.save();
      ctx.translate(bx - 3, -34);
      ctx.rotate(-0.3);
      roundRect(ctx, -2.5, -7, 5, 12, 2);
      inked(ctx, "#f4e6c4", "#8a7350", 1.4);
      ctx.restore();
    }
    roundRect(ctx, bx - 7, -31, 14, 11, 3);
    inked(ctx, bagColor, shade(bagColor, -0.5), 2);
    if (bg === "healer") {
      ctx.fillStyle = "#ff5f7e";
      ctx.fillRect(bx - 1.2, -28.5, 2.4, 6.5);
      ctx.fillRect(bx - 3.3, -26.4, 6.6, 2.4);
    }
  } else if (bg === "bard") {
    const draw = () => {
      ctx.save();
      ctx.translate(up ? 4 : side ? 10 : 2, up ? -34 : -36);
      ctx.rotate(up ? 0.45 : 0.55);
      limb(ctx, 0, 0, 0, -34, 3.5, "#9c6234", "#5a3416");
      roundRect(ctx, -3.5, -40, 7, 8, 2);
      inked(ctx, "#9c6234", "#5a3416", 1.6);
      ctx.beginPath();
      ctx.ellipse(0, 2, 10, 12, 0, 0, TAU);
      inked(ctx, "#d79a5a", "#7a4a24", 2);
      circle(ctx, 0, -1, 3, "#5a3416");
      ctx.restore();
    };
    if (layer === "back" && !up) draw();
    if (layer === "front" && up) draw();
    if (layer === "front" && !side && !up) strap(ctx, -11, -47, 14, -24, "#8a3a7a");
  } else if (bg === "knight") {
    if (layer === "back" && side) {
      ctx.beginPath();
      ctx.arc(12, -36, 12, 0, TAU);
      inked(ctx, "#3f6fb8", "#c9d1db", 3);
    }
    if (layer === "front" && up) {
      ctx.beginPath();
      ctx.arc(0, -36, 12, 0, TAU);
      inked(ctx, "#3f6fb8", "#c9d1db", 3);
      circle(ctx, 0, -36, 3.5, "#ffd166");
    }
  }
}

function drawPauldrons(ctx: CanvasRenderingContext2D, side: boolean) {
  for (const px of side ? [0] : [-14, 14]) {
    ctx.beginPath();
    ctx.ellipse(px, -46, 8, 5.5, 0, Math.PI, TAU);
    ctx.closePath();
    inked(ctx, "#c9d1db", "#6b7684", 2);
  }
}

function hairShell(ctx: CanvasRenderingContext2D, look: TamerLook, dir: Dir, hair: string, line: string, t: number) {
  const side = dir === "left" || dir === "right";
  const up = dir === "up";
  const hs = look.hairStyle;
  const cx = side ? 3 : 0;
  if (hs === "curly") {
    const [a0, a1] = side ? [1.05, 2.35] : [0.85, 2.15];
    for (let a = a0; a <= a1 + 1e-4; a += 0.15) {
      const ang = a * Math.PI;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(ang) * 25, -68 + Math.sin(ang) * 24, 7.5, 0, TAU);
      inked(ctx, hair, line, 2);
    }
  } else {
    ctx.beginPath();
    if (hs === "buzz") ctx.ellipse(cx, -67.5, 26, 24.2, 0, 0, TAU);
    else ctx.ellipse(cx, -69, 27.5, 25, 0, 0, TAU);
    inked(ctx, hair, line);
  }
  if (hs === "puffs") {
    const pts = side ? [[10, -90], [-8, -93]] : [[-19, -88], [19, -88]];
    for (const [px, py] of pts) {
      ctx.beginPath();
      ctx.arc(px, py, 11, 0, TAU);
      inked(ctx, hair, line);
    }
  }
  if (hs === "ponytail" && !up) {
    const sway = Math.sin(t * 3) * 2;
    ctx.save();
    ctx.translate(side ? 25 : 23, side ? -70 : -66);
    ctx.rotate(side ? 0.5 + sway * 0.03 : 0.25);
    ctx.beginPath();
    ctx.ellipse(side ? 3 : 2, side ? 14 : 8, 7, side ? 17 : 11, 0, 0, TAU);
    inked(ctx, hair, line);
    ctx.restore();
  }
  if (hs === "braids" && side) drawBraid(ctx, 15, -60, hair, line, look.outfit);
}

function drawBraid(ctx: CanvasRenderingContext2D, x: number, y: number, hair: string, line: string, tie: string) {
  for (let k = 0; k < 4; k++) {
    ctx.beginPath();
    ctx.ellipse(x + k * 0.6, y + k * 6.5, 5, 4.4, 0, 0, TAU);
    inked(ctx, hair, line, 1.8);
  }
  circle(ctx, x + 2, y + 25, 2.8, tie);
}

function hairFront(ctx: CanvasRenderingContext2D, look: TamerLook, dir: Dir, hair: string, line: string) {
  const side = dir === "left" || dir === "right";
  const hs = look.hairStyle;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(0, HEAD_Y, HEAD_RX + 1.4, HEAD_RY + 1.4, 0, 0, TAU);
  ctx.clip();
  ctx.beginPath();
  if (dir === "up") {
    ctx.moveTo(-30, -50);
    ctx.quadraticCurveTo(0, -45, 30, -50);
    ctx.lineTo(30, -100);
    ctx.lineTo(-30, -100);
  } else if (side) {
    const front = hs === "buzz" ? -80 : -74;
    ctx.moveTo(30, -46);
    ctx.lineTo(30, -100);
    ctx.lineTo(-30, -100);
    ctx.lineTo(-30, front);
    if (hs === "short" || hs === "curly") {
      ctx.quadraticCurveTo(-25, front + 6, -20, front);
      ctx.quadraticCurveTo(-15, front + 6, -10, front);
    } else ctx.lineTo(-10, front - 2);
    ctx.quadraticCurveTo(4, -74, 6, -64);
    ctx.quadraticCurveTo(8, -52, 14, -48);
  } else {
    ctx.moveTo(-30, -60);
    ctx.lineTo(-30, -100);
    ctx.lineTo(30, -100);
    ctx.lineTo(30, -60);
    if (hs === "short" || hs === "curly") {
      const base = hs === "curly" ? -71 : -73;
      const dip = hs === "curly" ? 7 : 5;
      ctx.lineTo(26, base);
      const n = hs === "curly" ? 6 : 5;
      const stepX = 52 / n;
      for (let k = 0; k < n; k++) {
        const x0 = 26 - k * stepX;
        ctx.quadraticCurveTo(x0 - stepX / 2, base + dip, x0 - stepX, base);
      }
    } else if (hs === "ponytail") {
      ctx.lineTo(26, -80);
      ctx.quadraticCurveTo(2, -82, -26, -64);
    } else if (hs === "buzz") {
      ctx.lineTo(26, -74);
      ctx.quadraticCurveTo(0, -86, -26, -74);
    } else {
      ctx.lineTo(26, -68);
      ctx.quadraticCurveTo(14, -82, 0, -82);
      ctx.quadraticCurveTo(-14, -82, -26, -68);
    }
    ctx.lineTo(-30, -60);
  }
  ctx.closePath();
  if (hs === "buzz") ctx.globalAlpha = 0.9;
  inked(ctx, hair, line, 2);
  ctx.globalAlpha = 1;
  if ((hs === "braids" || hs === "puffs") && dir === "down") {
    ctx.beginPath();
    ctx.moveTo(0, -82);
    ctx.lineTo(0, -92);
    ctx.strokeStyle = line;
    ctx.lineWidth = 1.6;
    ctx.stroke();
  }
  if (dir !== "up") {
    ctx.strokeStyle = "rgba(255,255,255,0.28)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(side ? 2 : -4, -70, 17, Math.PI * 1.2, Math.PI * 1.45);
    ctx.stroke();
  }
  ctx.restore();
}

function hairOver(ctx: CanvasRenderingContext2D, look: TamerLook, dir: Dir, hair: string, line: string, t: number) {
  const hs = look.hairStyle;
  if (hs === "braids" && dir === "down") {
    drawBraid(ctx, -21, -60, hair, line, look.outfit);
    drawBraid(ctx, 21, -60, hair, line, look.outfit);
  }
  if (dir !== "up") return;
  if (hs === "braids") {
    drawBraid(ctx, -12, -60, hair, line, look.outfit);
    drawBraid(ctx, 12, -60, hair, line, look.outfit);
  } else if (hs === "ponytail") {
    ctx.beginPath();
    ctx.ellipse(Math.sin(t * 3) * 1.5, -56, 7, 14, 0, 0, TAU);
    inked(ctx, hair, line);
    circle(ctx, 0, -70, 3.5, look.outfit);
  } else if (hs === "puffs") {
    circle(ctx, -15, -82, 3, look.outfit);
    circle(ctx, 15, -82, 3, look.outfit);
  }
}

function drawHat(ctx: CanvasRenderingContext2D, look: TamerLook, dir: Dir, layer: "back" | "front") {
  const side = dir === "left" || dir === "right";
  const hat = look.hat;
  if (hat === "hood") {
    const g = "#3d7a46";
    const gl = "#1f4526";
    if (layer === "back") {
      ctx.beginPath();
      ctx.ellipse(side ? 4 : 0, -68, 31, 30, 0, 0, TAU);
      inked(ctx, g, gl);
      ctx.beginPath();
      ctx.moveTo(side ? 24 : -6, -94);
      ctx.quadraticCurveTo(side ? 36 : 0, -104, side ? 30 : 6, -94);
      inked(ctx, g, gl);
      ctx.beginPath();
      ctx.ellipse(0, -46, side ? 13 : 18, 6, 0, 0, TAU);
      inked(ctx, g, gl);
      return;
    }
    if (dir === "up") {
      ctx.beginPath();
      ctx.ellipse(0, -68, 30, 29, 0, 0, TAU);
      inked(ctx, g, gl);
    } else {
      ctx.beginPath();
      if (side) {
        ctx.ellipse(4, -68, 31, 30, 0, Math.PI * 1.15, Math.PI * 2.45);
        ctx.ellipse(-2, -64, 21, 20, 0, Math.PI * 2.3, Math.PI * 1.2, true);
      } else {
        ctx.ellipse(0, -68, 31, 30, 0, Math.PI * 0.82, Math.PI * 2.18);
        ctx.ellipse(0, -64, 22, 20, 0, Math.PI * 2.1, Math.PI * 0.9, true);
      }
      ctx.closePath();
      inked(ctx, g, gl);
    }
    return;
  }
  if (layer === "back") return;
  if (hat === "wizard") {
    const c = "#5b3fa6";
    const cl = "#2c1c58";
    ctx.beginPath();
    ctx.moveTo(-18, -86);
    ctx.quadraticCurveTo(-8, -112, 12, -128);
    ctx.quadraticCurveTo(6, -108, 18, -86);
    ctx.closePath();
    inked(ctx, c, cl);
    ctx.fillStyle = "#ffd166";
    ctx.beginPath();
    for (let k = 0; k < 10; k++) {
      const r = k % 2 === 0 ? 5 : 2.2;
      const a = -Math.PI / 2 + (k * Math.PI) / 5;
      ctx.lineTo(-1 + Math.cos(a) * r, -101 + Math.sin(a) * r);
    }
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(side ? -3 : 0, -85, 31, side ? 6 : 7, 0, 0, TAU);
    inked(ctx, c, cl);
  } else if (hat === "cap") {
    const c = "#d0ad6b";
    const cl = "#7a5f2c";
    ctx.beginPath();
    ctx.ellipse(side ? 2 : 0, -78, 26.5, 17, 0, Math.PI, TAU);
    ctx.closePath();
    inked(ctx, c, cl);
    ctx.fillStyle = "#a8823f";
    ctx.fillRect(side ? -24 : -26, -82, side ? 52 : 52, 4);
    if (dir === "down") {
      ctx.beginPath();
      ctx.ellipse(0, -77, 22, 5.5, 0, 0, TAU);
      inked(ctx, "#b8924f", cl, 2);
    } else if (side) {
      ctx.beginPath();
      ctx.ellipse(-25, -78, 13, 4, -0.1, 0, TAU);
      inked(ctx, "#b8924f", cl, 2);
    }
  } else if (hat === "straw") {
    const c = "#f2d27a";
    const cl = "#9c7a2c";
    ctx.beginPath();
    ctx.ellipse(side ? -3 : 0, -84, side ? 34 : 38, side ? 7 : 9, 0, 0, TAU);
    inked(ctx, c, cl, 2);
    ctx.beginPath();
    ctx.ellipse(side ? 1 : 0, -86, 21, 15, 0, Math.PI, TAU);
    ctx.closePath();
    inked(ctx, c, cl, 2);
    ctx.fillStyle = "#e0524f";
    ctx.fillRect(side ? -19 : -20.5, -92, side ? 40 : 41, 5);
  } else if (hat === "crown") {
    const k = side ? 0.7 : 1;
    ctx.beginPath();
    ctx.moveTo(-16 * k, -86);
    ctx.lineTo(-16 * k, -99);
    ctx.lineTo(-8 * k, -92);
    ctx.lineTo(0, -103);
    ctx.lineTo(8 * k, -92);
    ctx.lineTo(16 * k, -99);
    ctx.lineTo(16 * k, -86);
    ctx.closePath();
    inked(ctx, "#ffd166", "#b07818", 2);
    circle(ctx, 0, -91, 2.6, "#ff5fa2");
    circle(ctx, -10 * k, -90, 1.9, "#5aa7ff");
    circle(ctx, 10 * k, -90, 1.9, "#5aa7ff");
  }
}

// Chibi kid. (x, y) = feet, h ≈ height without hat in px. walk = gait phase (0 when standing).
function drawTamer(ctx: CanvasRenderingContext2D, look: TamerLook, x: number, y: number, h: number, dir: Dir, walk: number, t: number) {
  const s = h / 100;
  const side = dir === "left" || dir === "right";
  const up = dir === "up";
  const skinLine = shade(look.skin, -0.42);
  const out = look.outfit;
  const outLine = shade(out, -0.5);
  const pants = shade(out, -0.55);
  const pantsLine = shade(out, -0.75);
  const hair = look.hairColor;
  const hairLine = shade(hair, -0.45);
  const sw = Math.sin(walk);
  const bob = Math.abs(sw) * 2.5;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.beginPath();
  ctx.ellipse(0, -1, 20, 5, 0, 0, TAU);
  ctx.fill();
  if (dir === "right") ctx.scale(-1, 1);
  ctx.translate(0, -bob);

  // legs
  if (side) {
    for (const k of [-1, 1]) {
      const lx = sw * 5 * k;
      const far = k === 1;
      limb(ctx, 0, -24, lx, -6, 9, far ? shade(out, -0.65) : pants, pantsLine);
      ctx.beginPath();
      ctx.ellipse(lx - 2, -3.5, 7, 4, 0, 0, TAU);
      inked(ctx, far ? "#2a1e1a" : "#3a2a24", "#1a1210", 1.6);
    }
  } else {
    for (const k of [-1, 1]) {
      const lift = Math.max(0, sw * k) * 4;
      limb(ctx, k * 7, -24, k * 7, -6 - lift, 9, pants, pantsLine);
      ctx.beginPath();
      ctx.ellipse(k * 7, -3.5 - lift, 6.5, 4, 0, 0, TAU);
      inked(ctx, "#3a2a24", "#1a1210", 1.6);
    }
  }

  drawAccessory(ctx, look, dir, "back");
  if (side) limb(ctx, 0, -45, sw * 7, -27, 7, shade(out, -0.2), outLine);

  // tunic
  ctx.beginPath();
  if (side) {
    ctx.moveTo(-8, -49);
    ctx.quadraticCurveTo(-12, -40, -13, -20);
    ctx.quadraticCurveTo(-1, -16, 11, -20);
    ctx.quadraticCurveTo(10, -40, 7, -49);
  } else {
    ctx.moveTo(-12, -49);
    ctx.quadraticCurveTo(-16, -38, -17, -20);
    ctx.quadraticCurveTo(0, -15, 17, -20);
    ctx.quadraticCurveTo(16, -38, 12, -49);
  }
  ctx.closePath();
  inked(ctx, out, outLine);
  ctx.fillStyle = shade(out, -0.3);
  ctx.fillRect(side ? -12 : -16, -28, side ? 23 : 32, 4);
  ctx.fillStyle = "rgba(255,255,255,0.18)";
  ctx.beginPath();
  ctx.ellipse(side ? -6 : -8, -40, 3, 8, 0.1, 0, TAU);
  ctx.fill();
  if (dir === "down") circle(ctx, 0, -48, 5, look.skin);

  drawAccessory(ctx, look, dir, "front");

  // arms
  const arms = side ? [-1] : [-1, 1];
  for (const k of arms) {
    const hx = side ? -sw * 7 : k * 18;
    const hy = side ? -27 : -27 + sw * k * 3;
    limb(ctx, side ? 0 : k * 13, -45, hx, hy, 7, out, outLine);
    ctx.beginPath();
    ctx.arc(hx, hy, 4.6, 0, TAU);
    inked(ctx, look.skin, skinLine, 2);
  }
  if (look.background === "knight") drawPauldrons(ctx, side);

  // head
  drawHat(ctx, look, dir, "back");
  if (look.hat !== "hood") hairShell(ctx, look, dir, hair, hairLine, t);
  if (dir === "down") {
    for (const k of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(k * 24, -63, 4, 5.5, 0, 0, TAU);
      inked(ctx, look.skin, skinLine, 2);
    }
  }
  headPath(ctx);
  inked(ctx, look.skin, skinLine);

  if (!up) {
    const blink = (t + x * 0.013) % 3.7 < 0.13;
    const eyes = side ? [-12] : [-9, 9];
    for (const ex of eyes) {
      if (blink) {
        ctx.strokeStyle = "#2a1a14";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ex, -64, 3.4, Math.PI * 0.15, Math.PI * 0.85);
        ctx.stroke();
      } else {
        ctx.fillStyle = "#2a1a14";
        ctx.beginPath();
        ctx.ellipse(ex, -63, 3.6, 4.6, 0, 0, TAU);
        ctx.fill();
        circle(ctx, ex - 1.2, -64.8, 1.5, "#ffffff");
        circle(ctx, ex + 1.2, -61.4, 0.7, "#ffffff");
      }
    }
    ctx.globalAlpha = 0.45;
    for (const bx of side ? [-15] : [-15, 15]) {
      ctx.fillStyle = "#ff7a7a";
      ctx.beginPath();
      ctx.ellipse(bx, -56.5, 4, 2.4, 0, 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = skinLine;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    if (side) ctx.arc(-18, -56, 2.6, Math.PI * 0.2, Math.PI * 0.8);
    else ctx.arc(0, -56.5, 3.4, Math.PI * 0.15, Math.PI * 0.85);
    ctx.stroke();
  }

  hairFront(ctx, look, dir, hair, hairLine);
  if (side && look.hat !== "hood") {
    ctx.beginPath();
    ctx.ellipse(7, -62, 4, 5.5, 0, 0, TAU);
    inked(ctx, look.skin, skinLine, 2);
  }
  hairOver(ctx, look, dir, hair, hairLine, t);
  drawHat(ctx, look, dir, "front");
  ctx.restore();
}

// Circular head-and-shoulders portrait used on slot cards and creator tiles.
function drawPortrait(ctx: CanvasRenderingContext2D, look: TamerLook, cx: number, cy: number, r: number, t = 0, bg = "#cfe8ff") {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.clip();
  ctx.fillStyle = bg;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  const top = look.hat === "wizard" ? 112 : look.hat === "crown" ? 104 : 94;
  const s = Math.min(0.024 * r, (1.04 * r) / (top - 66));
  drawTamer(ctx, look, cx, cy + 0.12 * r + 66 * s, 100 * s, "down", 0, t);
  ctx.restore();
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, TAU);
  ctx.stroke();
}

// ---------- title ----------

function drawTitleScene(ctx: CanvasRenderingContext2D, t: number) {
  const sky = ctx.createLinearGradient(0, 0, 0, 300);
  sky.addColorStop(0, "#7cc0f5");
  sky.addColorStop(0.7, "#bfe2f7");
  sky.addColorStop(1, "#ffe1ee");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, 300);

  const sun = ctx.createRadialGradient(790, 86, 0, 790, 86, 170);
  sun.addColorStop(0, "rgba(255,248,214,0.95)");
  sun.addColorStop(0.25, "rgba(255,240,200,0.55)");
  sun.addColorStop(1, "rgba(255,240,200,0)");
  ctx.fillStyle = sun;
  ctx.fillRect(600, 0, 360, 300);
  circle(ctx, 790, 86, 34, "#fff6d8");

  for (let i = 0; i < 5; i++) {
    const cx = ((hash1(i, 3) * (W + 300) + t * (6 + i * 2)) % (W + 300)) - 150;
    const cy = 40 + hash1(i, 5) * 110;
    const s = 0.7 + hash1(i, 9) * 0.7;
    ctx.fillStyle = "rgba(255,255,255,0.85)";
    for (const [dx, dy, r] of [[-30, 6, 20], [0, 0, 28], [30, 6, 22], [10, 12, 22]] as const) {
      ctx.beginPath();
      ctx.arc(cx + dx * s, cy + dy * s, r * s, 0, TAU);
      ctx.fill();
    }
  }

  const ranges: [string, number, number, number][] = [
    ["#a9d9a0", 238, 46, 0.004],
    ["#7cc57a", 278, 34, 0.007],
  ];
  ranges.forEach(([color, base, amp, freq], k) => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (let x = 0; x <= W; x += 8) ctx.lineTo(x, base - fbm(x * freq, 41 + k * 13) * amp * 2 + amp * 0.6);
    ctx.lineTo(W, H);
    ctx.closePath();
    ctx.fill();
  });

  const meadow = ctx.createLinearGradient(0, 300, 0, 450);
  meadow.addColorStop(0, "#68b866");
  meadow.addColorStop(1, "#4f9d56");
  ctx.fillStyle = meadow;
  ctx.fillRect(0, 300, W, 160);
  const petals = ["#ffffff", "#ffd166", "#ff9ec4", "#c8a6ff"];
  for (let i = 0; i < 70; i++) {
    const fx = hash1(i, 21) * W;
    const fy = 306 + hash1(i, 23) * 140;
    const sway = Math.sin(t * 1.5 + i) * 1.2;
    ctx.strokeStyle = "#3f8a47";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(fx, fy + 6);
    ctx.lineTo(fx + sway, fy);
    ctx.stroke();
    circle(ctx, fx + sway, fy, 2.6, petals[i % petals.length]);
  }

  const waterTop = 452;
  const water = ctx.createLinearGradient(0, waterTop, 0, H);
  water.addColorStop(0, "#7fd0de");
  water.addColorStop(1, "#3a8fb2");
  ctx.fillStyle = "#8a6a44";
  ctx.beginPath();
  ctx.moveTo(0, H);
  for (let x = 0; x <= W; x += 10) ctx.lineTo(x, waterTop - 6 + Math.sin(x * 0.02) * 3);
  ctx.lineTo(W, H);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = water;
  ctx.beginPath();
  ctx.moveTo(0, H);
  for (let x = 0; x <= W; x += 10) ctx.lineTo(x, waterTop + Math.sin(x * 0.02) * 3);
  ctx.lineTo(W, H);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 2;
  for (let i = 0; i < 9; i++) {
    const wx = ((hash1(i, 31) * W + t * 10 * (i % 2 ? 1 : -1)) % W + W) % W;
    const wy = 476 + hash1(i, 33) * 56;
    ctx.beginPath();
    ctx.moveTo(wx - 18, wy);
    ctx.quadraticCurveTo(wx, wy - 4, wx + 18, wy);
    ctx.stroke();
  }
  for (let i = 0; i < 6; i++) {
    const lx = 60 + hash1(i, 41) * (W - 120);
    const ly = 480 + hash1(i, 43) * 46;
    const r = 13 + hash1(i, 45) * 8;
    const a = hash1(i, 47) * TAU + Math.sin(t * 0.6 + i) * 0.05;
    ctx.fillStyle = "#3d8f4a";
    ctx.beginPath();
    ctx.ellipse(lx, ly, r, r * 0.5, 0, a + 0.3, a + TAU - 0.3);
    ctx.lineTo(lx, ly);
    ctx.closePath();
    ctx.fill();
    if (i % 3 === 0) circle(ctx, lx + r * 0.3, ly - 3, 4, "#ffb3cf");
  }

  for (let i = 0; i < 28; i++) {
    const sp = 0.5 + hash1(i, 51);
    const px = hash1(i, 53) * W + Math.sin(t * 0.5 + i) * 24;
    const py = ((hash1(i, 55) * H - t * 10 * sp) % H + H) % H;
    ctx.globalAlpha = 0.35 + 0.35 * Math.sin(t * 2 + i);
    circle(ctx, px, py, 1.6 + sp, "#fff6c0");
  }
  ctx.globalAlpha = 1;
}

function drawTitle(ctx: CanvasRenderingContext2D, S: Game) {
  const t = S.t;
  drawTitleScene(ctx, t);
  drawAxoPeek(ctx, MORPHS[0], 92, 500, 1.1, t, 0);
  drawAxoPeek(ctx, MORPHS[2], 870, 506, 1, t, 2.1);

  ctx.save();
  ctx.translate(W / 2, 112);
  const pop = 1 + 0.02 * Math.sin(t * 2);
  ctx.scale(pop, pop);
  ctx.font = "900 92px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.lineJoin = "round";
  ctx.lineWidth = 14;
  ctx.strokeStyle = "#b8336f";
  ctx.strokeText("Lippies", 0, 0);
  ctx.lineWidth = 7;
  ctx.strokeStyle = "#ff5fa2";
  ctx.strokeText("Lippies", 0, 0);
  ctx.fillStyle = "#ffffff";
  ctx.fillText("Lippies", 0, 0);
  ctx.restore();
  text(ctx, "Catch, battle and befriend wild Lippies!", W / 2, 156, "700 20px system-ui, sans-serif", "#ffffff", "center");

  for (let i = 0; i < SLOTS; i++) drawSlotCard(ctx, S, i);

  if (S.tip) {
    const b = TIP_BOX;
    panel(ctx, b.x, b.y, b.w, b.h, 16, "rgba(18,30,44,0.9)");
    text(ctx, "Tip: tap Share, then “Add to Home Screen”", b.x + 18, b.y + 27, "700 16px system-ui, sans-serif", "#ffe08a");
    text(ctx, "so Safari never clears your saved adventures.", b.x + 18, b.y + 49, "600 14px system-ui, sans-serif", "#e8f0f6");
    drawButton(ctx, S, buttons(S)[0], "Got it", "#334155", "800 16px system-ui, sans-serif");
  } else if (S.modeT > 0.6) {
    ctx.globalAlpha = 0.65 + 0.35 * Math.sin(t * 3);
    text(ctx, "Tap a slot to start your adventure", W / 2, 490, "800 20px system-ui, sans-serif", "#ffe08a", "center");
    ctx.globalAlpha = 1;
    text(ctx, "Keyboard: ← → to choose · Enter to play", W / 2, 514, "500 14px system-ui, sans-serif", "#e8f0f6", "center");
  }
}

function drawSlotCard(ctx: CanvasRenderingContext2D, S: Game, i: number) {
  const x = cardX(i);
  const y = CARD_Y;
  const d = S.slots[i];
  const lift = S.press === `${d ? "play" : "new"}:${i}` ? 2 : 0;
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  roundRect(ctx, x, y + 5, CARD_W, CARD_H, 18);
  ctx.fill();
  panel(ctx, x, y + lift, CARD_W, CARD_H, 18, d ? "rgba(18,30,44,0.9)" : "rgba(18,30,44,0.72)");
  if (S.kbd && S.sel === i) {
    ctx.strokeStyle = "#ff5fa2";
    ctx.lineWidth = 4;
    roundRect(ctx, x - 2, y - 2 + lift, CARD_W + 4, CARD_H + 4, 20);
    ctx.stroke();
  }
  const [b0, b1, b2] = cardButtons(S, i);

  if (!d) {
    ctx.setLineDash([8, 7]);
    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = 2;
    roundRect(ctx, x + 10, y + 10 + lift, CARD_W - 20, 136, 12);
    ctx.stroke();
    ctx.setLineDash([]);
    text(ctx, `SLOT ${i + 1}`, x + 24, y + 36 + lift, "800 12px system-ui, sans-serif", "#94a3b8");
    circle(ctx, x + CARD_W / 2, y + 74 + lift, 26, "rgba(255,95,162,0.25)");
    text(ctx, "+", x + CARD_W / 2, y + 86 + lift, "900 36px system-ui, sans-serif", "#ff8fbf", "center");
    text(ctx, "Empty slot", x + CARD_W / 2, y + 128 + lift, "700 16px system-ui, sans-serif", "#d8e6f0", "center");
    drawButton(ctx, S, b1, "New game", "#10b981");
    drawButton(ctx, S, b0, "Import", "#334155", "800 15px system-ui, sans-serif");
    return;
  }

  const bg = BACKGROUNDS.find((b) => b.id === d.tamer.background) ?? BACKGROUNDS[0];
  drawPortrait(ctx, d.tamer, x + 54, y + 58 + lift, 36, S.t);
  text(ctx, `SLOT ${i + 1}`, x + CARD_W - 16, y + 26 + lift, "800 12px system-ui, sans-serif", "#94a3b8", "right");
  text(ctx, d.tamer.name, x + 102, y + 56 + lift, "800 22px system-ui, sans-serif", "#ffffff");
  text(ctx, `${bg.icon} ${bg.name}`, x + 102, y + 80 + lift, "600 14px system-ui, sans-serif", "#d8e6f0");
  text(ctx, `📖 ${d.journal.caught.length} / ${TOTAL_LIPPIES} Lippies caught`, x + 18, y + 122 + lift, "700 15px system-ui, sans-serif", "#ffe08a");
  text(ctx, `📍 ${AREA_NAMES[d.area]} · ${formatTime(d.playTime)}`, x + 18, y + 145 + lift, "600 14px system-ui, sans-serif", "#d8e6f0");
  drawButton(ctx, S, b2, "▶ Play", "#10b981");
  drawButton(ctx, S, b0, "⤓", "#334155", "900 20px system-ui, sans-serif");
  drawButton(ctx, S, b1, "", "#334155");
  drawTrash(ctx, b1.x + b1.w / 2, b1.y + b1.h / 2 + (S.press === b1.id ? 2 : 0));
}

function drawTrash(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = "#ffffff";
  ctx.lineWidth = 2.4;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(cx - 9, cy - 7);
  ctx.lineTo(cx + 9, cy - 7);
  ctx.moveTo(cx - 3, cy - 10);
  ctx.lineTo(cx + 3, cy - 10);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - 7, cy - 4);
  ctx.lineTo(cx - 6, cy + 10);
  ctx.lineTo(cx + 6, cy + 10);
  ctx.lineTo(cx + 7, cy - 4);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - 2.5, cy - 1);
  ctx.lineTo(cx - 2.2, cy + 7);
  ctx.moveTo(cx + 2.5, cy - 1);
  ctx.lineTo(cx + 2.2, cy + 7);
  ctx.stroke();
}

// ---------- world ----------

const T = 48;
const MOVE_TIME = 0.2;
const TURN_DELAY = 0.09;
const TYPE_SPEED = 55;
const DELTA: Record<Dir, [number, number]> = { down: [0, 1], up: [0, -1], left: [-1, 0], right: [1, 0] };
const OPPOSITE: Record<Dir, Dir> = { down: "up", up: "down", left: "right", right: "left" };
const PAD = { x: 118, y: 422, r: 96 };
const A_BTN = { x: 870, y: 440, r: 46 };
const DIALOG_BOX = { x: 110, y: 398, w: 700, h: 126 };

interface Theme {
  ground: [string, string];
  path: string;
  pathEdge: string;
  tall: [string, string];
  tallKind: "grass" | "mushroom" | "fern";
  decor: "flowers" | "shells" | "crystals";
  water: [string, string];
  lava?: boolean;
  tree: "willow" | "round" | "palm";
  leaf: [string, string, string];
  trunk: string;
  wall: [string, string, string];
  rock: [string, string];
  rest: "fountain" | "shrine" | "campfire" | "crystal";
  ambient: "pollen" | "dragonflies" | "spray" | "embers" | "motes";
  dark: number;
  tint?: string;
}

const OUTDOOR_WALL: [string, string, string] = ["#7d7466", "#a39a88", "#5a5248"];

const THEMES: Record<AreaId, Theme> = {
  village: {
    ground: ["#7cc576", "#76bf70"],
    path: "#e3cb94",
    pathEdge: "#c9ab6d",
    tall: ["#4f9d4f", "#63b45e"],
    tallKind: "grass",
    decor: "flowers",
    water: ["#5fb8d6", "#8fd3e8"],
    tree: "willow",
    leaf: ["#6fbf5f", "#4f9f48", "#2f6a32"],
    trunk: "#8a5a32",
    wall: OUTDOOR_WALL,
    rock: ["#a7aab0", "#5f646c"],
    rest: "fountain",
    ambient: "pollen",
    dark: 0,
  },
  meadow: {
    ground: ["#86cf6e", "#80c968"],
    path: "#e6cf98",
    pathEdge: "#c9ab6d",
    tall: ["#4c9a43", "#68b85a"],
    tallKind: "grass",
    decor: "flowers",
    water: ["#58b4dc", "#8fd3e8"],
    tree: "round",
    leaf: ["#5cb85a", "#3f9a45", "#24602b"],
    trunk: "#7a4a24",
    wall: OUTDOOR_WALL,
    rock: ["#a7aab0", "#5f646c"],
    rest: "shrine",
    ambient: "pollen",
    dark: 0,
  },
  pond: {
    ground: ["#74c27e", "#6ebc78"],
    path: "#d9c48f",
    pathEdge: "#b9a06a",
    tall: ["#3f8f58", "#57a96a"],
    tallKind: "grass",
    decor: "flowers",
    water: ["#4aa8c8", "#7fcde0"],
    tree: "willow",
    leaf: ["#62b06a", "#43925a", "#285c3a"],
    trunk: "#7a5232",
    wall: OUTDOOR_WALL,
    rock: ["#9ea6ad", "#5a626a"],
    rest: "shrine",
    ambient: "dragonflies",
    dark: 0,
  },
  cove: {
    ground: ["#f0dca5", "#ead49a"],
    path: "#e2c98f",
    pathEdge: "rgba(170,130,80,0.35)",
    tall: ["#a3b55a", "#c2cf6e"],
    tallKind: "grass",
    decor: "shells",
    water: ["#36b0c9", "#7fdbe6"],
    tree: "palm",
    leaf: ["#58b25a", "#3c9246", "#24602b"],
    trunk: "#a8794a",
    wall: ["#b8875e", "#d9ab7e", "#8a5f3e"],
    rock: ["#b7a48c", "#74624c"],
    rest: "campfire",
    ambient: "spray",
    dark: 0,
  },
  hollow: {
    ground: ["#463a4c", "#40354a"],
    path: "#5a4c5e",
    pathEdge: "#33293a",
    tall: ["#ff9e5a", "#ffc46b"],
    tallKind: "mushroom",
    decor: "crystals",
    water: ["#e8642e", "#ffb347"],
    lava: true,
    tree: "round",
    leaf: ["#5cb85a", "#3f9a45", "#24602b"],
    trunk: "#7a4a24",
    wall: ["#2c2433", "#4a3d54", "#1c1622"],
    rock: ["#5e5266", "#2a2230"],
    rest: "crystal",
    ambient: "embers",
    dark: 0.72,
  },
  grotto: {
    ground: ["#33456a", "#2f4064"],
    path: "#4a5f8a",
    pathEdge: "#26365a",
    tall: ["#5fa3c8", "#86c6e4"],
    tallKind: "fern",
    decor: "crystals",
    water: ["#2e5aa8", "#7fb4ff"],
    tree: "round",
    leaf: ["#5cb85a", "#3f9a45", "#24602b"],
    trunk: "#7a4a24",
    wall: ["#1c2440", "#34416a", "#121830"],
    rock: ["#4a5a80", "#1e2846"],
    rest: "crystal",
    ambient: "motes",
    dark: 0,
    tint: "rgba(30,50,120,0.22)",
  },
};

interface Prop {
  kind: "house" | "ship" | "rest" | "moonstone";
  x: number;
  y: number;
  w: number;
  h: number;
  i: number;
}

interface AreaInfo {
  def: AreaDef;
  w: number;
  h: number;
  props: Prop[];
}

const areaCache = new Map<AreaId, AreaInfo>();

function areaInfo(id: AreaId): AreaInfo {
  const hit = areaCache.get(id);
  if (hit) return hit;
  const def = AREAS[id];
  const map = def.map;
  const h = map.length;
  const w = map[0].length;
  const kinds: Record<string, Prop["kind"]> = { H: "house", P: "ship", R: "rest", M: "moonstone" };
  const props: Prop[] = [];
  const seen = new Set<string>();
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ch = map[y][x];
      const kind = kinds[ch];
      if (!kind || seen.has(`${x},${y}`)) continue;
      let pw = 1;
      while (map[y][x + pw] === ch) pw++;
      let ph = 1;
      while (y + ph < h && map[y + ph][x] === ch) ph++;
      for (let dy = 0; dy < ph; dy++) for (let dx = 0; dx < pw; dx++) seen.add(`${x + dx},${y + dy}`);
      props.push({ kind, x, y, w: pw, h: ph, i: props.length });
    }
  }
  const info = { def, w, h, props };
  areaCache.set(id, info);
  return info;
}

function tileAt(info: AreaInfo, x: number, y: number) {
  if (y < 0 || y >= info.h || x < 0 || x >= info.w) return "X";
  return info.def.map[y][x];
}

interface Player {
  x: number;
  y: number;
  tx: number;
  ty: number;
  dir: Dir;
  moving: boolean;
  moveT: number;
  walk: number;
  idleT: number;
  turnT: number;
  bumpT: number;
  steps: number;
}

interface Dialog {
  speaker: string;
  lines: string[];
  i: number;
  shown: number;
  then?: () => void;
}

interface Transit {
  to: AreaId;
  x: number;
  y: number;
  dir: Dir;
  t: number;
  switched: boolean;
}

type Target =
  | { kind: "npc"; n: NpcPlacement }
  | { kind: "sign"; lines: string[] }
  | { kind: "rest" }
  | { kind: "moonstone" };

function newPlayer(x: number, y: number, dir: Dir): Player {
  return { x, y, tx: x, ty: y, dir, moving: false, moveT: 0, walk: 0, idleT: 1, turnT: 0, bumpT: 0, steps: 0 };
}

function playerPos(p: Player): [number, number] {
  return p.moving ? [lerp(p.x, p.tx, p.moveT), lerp(p.y, p.ty, p.moveT)] : [p.x, p.y];
}

function npcPos(S: Game, n: NpcPlacement): [number, number] {
  const gate = NPCS[n.id].gate;
  return gate && n.aside && S.save?.flags[gate.flag] ? n.aside : [n.x, n.y];
}

function npcAt(S: Game, x: number, y: number) {
  return areaInfo(S.area).def.npcs.find((n) => {
    const [nx, ny] = npcPos(S, n);
    return nx === x && ny === y;
  });
}

function isBlocked(S: Game, x: number, y: number) {
  return !WALKABLE.has(tileAt(areaInfo(S.area), x, y)) || !!npcAt(S, x, y);
}

function targetAt(S: Game, x: number, y: number): Target | null {
  const n = npcAt(S, x, y);
  if (n) return { kind: "npc", n };
  const info = areaInfo(S.area);
  const ch = tileAt(info, x, y);
  if (ch === "S") return { kind: "sign", lines: info.def.signs[`${x},${y}`] ?? ["The sign is too faded to read."] };
  if (ch === "R") return { kind: "rest" };
  if (ch === "M") return { kind: "moonstone" };
  return null;
}

function facingTarget(S: Game) {
  const p = S.p;
  if (p.moving) return null;
  const [dx, dy] = DELTA[p.dir];
  return targetAt(S, p.x + dx, p.y + dy);
}

function targetLabel(tg: Target) {
  if (tg.kind === "npc") return `Talk to ${NPCS[tg.n.id].name}`;
  if (tg.kind === "sign") return "Read";
  if (tg.kind === "rest") return "Rest";
  return "Look";
}

function enterWorld(S: Game, d: SaveData) {
  S.save = d;
  S.area = d.area;
  S.p = newPlayer(d.x, d.y, d.dir ?? "down");
  if (import.meta.env.DEV) {
    const params = new URLSearchParams(location.search);
    const party = params.get("party");
    if (party && !d.party.length) {
      d.party = party
        .split(",")
        .filter((f) => FORMS.some((x) => x.id === f))
        .slice(0, 4)
        .map((f) => newOwned(f, Number(params.get("lv")) || 5));
      d.flags.starter = true;
      d.flags[`starter_${STARTERS.find((s) => party.includes(s)) ?? "emberlotl"}`] = true;
      d.journal.caught.push(...d.party.map((l) => l.species).filter((id) => !d.journal.caught.includes(id)));
      d.items = { jar: 10, cake: 3, ...d.items };
    }
    const jump = params.get("area");
    if (jump && jump in AREAS) {
      const sp = AREAS[jump as AreaId].spawn;
      S.area = jump as AreaId;
      S.p = newPlayer(sp.x, sp.y, sp.dir);
    }
  }
  S.dialog = null;
  S.transit = null;
  S.npcFace = {};
  S.npcWalk = {};
  S.held = [];
  S.pad = null;
  S.banner = { text: AREA_NAMES[S.area], t0: S.t };
  S.lastSave = S.t;
  enter(S, "world");
}

function savePos(S: Game) {
  if (!S.save) return;
  S.save.area = S.area;
  S.save.x = S.p.x;
  S.save.y = S.p.y;
  S.save.dir = S.p.dir;
}

function say(S: Game, speaker: string, lines: string[], then?: () => void) {
  const name = S.save?.tamer.name ?? "";
  S.dialog = { speaker, lines: lines.map((l) => l.replace(/\{name\}/g, name)), i: 0, shown: 0, then };
}

function advanceDialog(S: Game, audio: Audio) {
  const d = S.dialog;
  if (!d) return;
  if (d.shown < d.lines[d.i].length) {
    d.shown = d.lines[d.i].length;
    return;
  }
  d.i++;
  d.shown = 0;
  if (d.i < d.lines.length) {
    audio.tap();
    return;
  }
  S.dialog = null;
  d.then?.();
}

function interact(S: Game, audio: Audio) {
  if (S.transit) return;
  if (S.dialog) {
    advanceDialog(S, audio);
    return;
  }
  const tg = facingTarget(S);
  if (!tg) return;
  audio.tap();
  if (tg.kind === "npc") talk(S, tg.n, audio);
  else if (tg.kind === "sign") say(S, "", tg.lines);
  else if (tg.kind === "moonstone") {
    if (S.save?.flags.beatOlm) say(S, "", STORY.moonstoneBright);
    else if (!partyHealthy(S)) say(S, "", [...MOONSTONE_LINES, ...tiredLines(S)]);
    else
      say(S, "", STORY.olmWake, () =>
        startBattle(S, audio, { kind: "guardian", team: [["olm", 12]], onWin: "olm", intro: ["The Elder Olm wants to test your team!"] }),
      );
  }
  else {
    audio.chime();
    healParty(S);
    const rested = S.save?.party.length ? "You and your Lippies feel rested. Your adventure is saved!" : "You feel rested. Your adventure is saved!";
    say(S, "", [...areaInfo(S.area).def.rest, rested], () => {
      if (!S.save) return;
      S.save.respawn = { area: S.area, x: S.p.x, y: S.p.y };
      savePos(S);
      saveCurrent(S);
      S.toast = { text: "Adventure saved!", t0: S.t };
    });
  }
}

function talk(S: Game, n: NpcPlacement, audio: Audio) {
  const def = NPCS[n.id];
  S.npcFace[n.id] = OPPOSITE[S.p.dir];
  const gate = def.gate;
  if (n.id === "gran" && S.save && !S.save.flags.starter) {
    say(S, def.name, STORY.granIntro, () => {
      S.starterSel = 1;
      enter(S, "starter");
    });
    return;
  }
  if (gate && n.aside && S.save && !S.save.flags[gate.flag]) {
    if (!partyHealthy(S)) {
      say(S, def.name, [gate.lines[0], ...tiredLines(S)]);
      return;
    }
    say(S, def.name, gate.lines, () =>
      startBattle(S, audio, { kind: "trainer", team: gateTeam(S, gate.team), trainer: { name: def.name, look: def.look }, onWin: n.id }),
    );
  } else say(S, def.name, def.lines);
}

function tryMove(S: Game, d: Dir, audio: Audio) {
  const p = S.p;
  const [dx, dy] = DELTA[d];
  const nx = p.x + dx;
  const ny = p.y + dy;
  if (isBlocked(S, nx, ny)) {
    p.walk = 0;
    if (p.bumpT <= 0) {
      audio.bump();
      p.bumpT = 0.4;
    }
    return;
  }
  p.tx = nx;
  p.ty = ny;
  p.moving = true;
  p.moveT = 0;
  p.idleT = 0;
  p.steps++;
  if (tileAt(areaInfo(S.area), nx, ny) === "*") audio.rustle();
  else audio.step(p.steps % 2 === 0);
}

function arrive(S: Game, audio: Audio) {
  const p = S.p;
  savePos(S);
  const ex = areaInfo(S.area).def.exits.find((e) => p.y === e.y && p.x >= e.x && p.x < e.x + (e.w ?? 1));
  if (ex && S.area === "village" && !S.save?.flags.starter) {
    // No leaving town without a Lippie: step back and hear from Gran.
    const [dx, dy] = DELTA[p.dir];
    p.x -= dx;
    p.y -= dy;
    p.dir = OPPOSITE[p.dir];
    savePos(S);
    say(S, "", STORY.noStarter);
    return;
  }
  if (ex) {
    audio.whoosh();
    S.transit = { to: ex.to, x: ex.tx + (p.x - ex.x), y: ex.ty, dir: ex.dir, t: 0, switched: false };
    return;
  }
  if (S.save?.tamer.background === "healer" && p.steps % 10 === 0) {
    for (const l of S.save.party) if (l.hp > 0) l.hp = Math.min(ownedMaxHp(l), l.hp + 1);
  }
  pickUpItem(S, audio);
  if (tileAt(areaInfo(S.area), p.x, p.y) === "*") maybeEncounter(S, audio);
}

function stepWorld(S: Game, dt: number, audio: Audio) {
  const tr = S.transit;
  if (tr) {
    tr.t += dt;
    if (!tr.switched && tr.t >= 0.25) {
      tr.switched = true;
      S.area = tr.to;
      S.p = newPlayer(tr.x, tr.y, tr.dir);
      S.npcFace = {};
      S.npcWalk = {};
      S.banner = { text: AREA_NAMES[S.area], t0: S.t };
      savePos(S);
      saveCurrent(S);
    }
    if (tr.t >= 0.5) S.transit = null;
    return;
  }
  const d = S.dialog;
  if (d && d.shown < d.lines[d.i].length) {
    const before = Math.floor(d.shown / 3);
    d.shown = Math.min(d.lines[d.i].length, d.shown + dt * TYPE_SPEED);
    if (Math.floor(d.shown / 3) > before) audio.blip();
  }
  for (const id of Object.keys(S.npcWalk)) {
    S.npcWalk[id].t += dt / 0.35;
    if (S.npcWalk[id].t >= 1) delete S.npcWalk[id];
  }

  const p = S.p;
  p.bumpT = Math.max(0, p.bumpT - dt);
  if (p.moving) {
    p.moveT += dt / MOVE_TIME;
    p.walk += (dt / MOVE_TIME) * Math.PI;
    if (p.moveT >= 1) {
      p.moving = false;
      p.x = p.tx;
      p.y = p.ty;
      p.moveT = 0;
      arrive(S, audio);
    }
  }
  if (p.moving || S.transit || S.dialog) return;
  const want = S.pad ?? S.held[S.held.length - 1] ?? null;
  if (!want) {
    p.idleT += dt;
    p.walk = 0;
    p.turnT = 0;
    return;
  }
  if (want !== p.dir) {
    p.dir = want;
    p.turnT = p.idleT > 0.05 ? TURN_DELAY : 0;
  }
  if (p.turnT > 0) {
    p.turnT -= dt;
    return;
  }
  tryMove(S, want, audio);
}

function padDir(x: number, y: number): Dir | null {
  const dx = x - PAD.x;
  const dy = y - PAD.y;
  if (Math.max(Math.abs(dx), Math.abs(dy)) < 16) return null;
  return Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? "left" : "right") : dy < 0 ? "up" : "down";
}

function camera(S: Game): [number, number] {
  const info = areaInfo(S.area);
  const [fx, fy] = playerPos(S.p);
  const mw = info.w * T;
  const mh = info.h * T;
  const cx = mw <= W ? (mw - W) / 2 : clamp(fx * T + T / 2 - W / 2, 0, mw - W);
  const cy = mh <= H ? (mh - H) / 2 : clamp(fy * T + T / 2 - H / 2, 0, mh - H);
  return [Math.round(cx), Math.round(cy)];
}

// Tapping a tile next to the player turns to face it and interacts.
function tapWorld(S: Game, x: number, y: number, audio: Audio) {
  if (S.p.moving || S.transit) return;
  const [cx, cy] = camera(S);
  const tx = Math.floor((x + cx) / T);
  const ty = Math.floor((y + cy) / T);
  const dx = tx - S.p.x;
  const dy = ty - S.p.y;
  if (Math.abs(dx) + Math.abs(dy) !== 1 || !targetAt(S, tx, ty)) return;
  S.p.dir = dx < 0 ? "left" : dx > 0 ? "right" : dy < 0 ? "up" : "down";
  interact(S, audio);
}

// ---------- world drawing ----------

function drawTall(ctx: CanvasRenderingContext2D, th: Theme, x: number, y: number, t: number, frontOnly: boolean) {
  const px = x * T;
  const py = y * T;
  if (th.tallKind === "mushroom") {
    const spots: [number, number, number][] = frontOnly
      ? [[0.25, 0.92, 0.9], [0.7, 0.96, 1]]
      : [[0.3, 0.55, 0.7], [0.72, 0.5, 0.8], [0.25, 0.92, 0.9], [0.7, 0.96, 1]];
    for (const [u, v, s] of spots) {
      const mx = px + u * T + (hash1(x * 7 + y, u * 10) - 0.5) * 8;
      const my = py + v * T;
      const glow = 0.5 + 0.5 * Math.sin(t * 2 + x + y + u * 5);
      ctx.fillStyle = `rgba(255,170,90,${0.12 + glow * 0.12})`;
      ctx.beginPath();
      ctx.arc(mx, my - 8 * s, 12 * s, 0, TAU);
      ctx.fill();
      ctx.fillStyle = "#e9d8c8";
      ctx.fillRect(mx - 1.5 * s, my - 8 * s, 3 * s, 8 * s);
      ctx.beginPath();
      ctx.arc(mx, my - 8 * s, 7 * s, Math.PI, TAU);
      ctx.closePath();
      inked(ctx, th.tall[hash1(x, y + u) > 0.5 ? 0 : 1], "#7a3a1e", 1.5);
      circle(ctx, mx - 2 * s, my - 11 * s, 1.3 * s, "#fff4d0");
    }
    return;
  }
  const rows = frontOnly ? [1] : [0, 1];
  for (const r of rows) {
    const by = py + (r === 0 ? T * 0.58 : T - 1);
    for (let k = 0; k < 5; k++) {
      const bx = px + 5 + k * 9.5 + (r === 0 ? 4 : 0) + (hash1(x * 13 + k, y * 3 + r) - 0.5) * 4;
      const hgt = (r === 0 ? 16 : 20) + hash1(x + k, y) * 6;
      const sway = Math.sin(t * 2.2 + x * 0.7 + y * 0.3 + k) * 2.5;
      ctx.fillStyle = th.tall[(k + r) % 2];
      ctx.beginPath();
      ctx.moveTo(bx - 4.5, by);
      ctx.quadraticCurveTo(bx - 1, by - hgt * 0.6, bx + sway, by - hgt);
      ctx.quadraticCurveTo(bx + 1, by - hgt * 0.5, bx + 4.5, by);
      ctx.closePath();
      ctx.fill();
      if (th.tallKind === "fern" && k % 2 === 0) circle(ctx, bx + sway, by - hgt, 2, `rgba(200,235,255,${0.5 + 0.4 * Math.sin(t * 3 + k + x)})`);
    }
  }
}

function drawDecor(ctx: CanvasRenderingContext2D, th: Theme, x: number, y: number, t: number) {
  const px = x * T;
  const py = y * T;
  for (let k = 0; k < 3; k++) {
    const fx = px + 8 + hash1(x * 5 + k, y) * (T - 16);
    const fy = py + 8 + hash1(y * 5 + k, x) * (T - 16);
    if (th.decor === "flowers") {
      const c = ["#ffffff", "#ffd166", "#ff9ec4", "#c8a6ff"][(x + y + k) % 4];
      for (let a = 0; a < 5; a++) circle(ctx, fx + Math.cos((a * TAU) / 5) * 3, fy + Math.sin((a * TAU) / 5) * 3, 2.4, c);
      circle(ctx, fx, fy, 1.8, "#f2a33a");
    } else if (th.decor === "shells") {
      ctx.fillStyle = ["#ffd9e6", "#fff1d6", "#ffd0b0"][(x + k) % 3];
      ctx.beginPath();
      ctx.arc(fx, fy, 4, Math.PI, TAU);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = "rgba(160,100,80,0.6)";
      ctx.lineWidth = 0.8;
      ctx.stroke();
    } else {
      const glow = 0.6 + 0.4 * Math.sin(t * 2.5 + x * 3 + k);
      ctx.fillStyle = k % 2 ? `rgba(170,140,255,${glow})` : `rgba(120,200,255,${glow})`;
      ctx.beginPath();
      ctx.moveTo(fx, fy - 7);
      ctx.lineTo(fx + 3, fy);
      ctx.lineTo(fx, fy + 2);
      ctx.lineTo(fx - 3, fy);
      ctx.closePath();
      ctx.fill();
    }
  }
}

function isWet(ch: string) {
  return ch === "~" || ch === "B" || ch === "K";
}

function drawTile(ctx: CanvasRenderingContext2D, info: AreaInfo, th: Theme, x: number, y: number, t: number) {
  const ch = tileAt(info, x, y);
  const px = x * T;
  const py = y * T;
  const hsh = hash1(x * 131 + y * 7, 3);

  if (isWet(ch)) {
    ctx.fillStyle = th.water[0];
    ctx.fillRect(px, py, T, T);
    if (th.lava) {
      ctx.fillStyle = `rgba(255,210,90,${0.18 + 0.14 * Math.sin(t * 2 + hsh * 6)})`;
      ctx.fillRect(px, py, T, T);
    }
    ctx.strokeStyle = th.lava ? "rgba(255,230,140,0.55)" : "rgba(255,255,255,0.35)";
    ctx.lineWidth = 2;
    for (let k = 0; k < 2; k++) {
      const wx = px + ((hsh * 40 + k * 22 + t * (th.lava ? 4 : 9)) % T);
      const wy = py + 12 + k * 20 + hsh * 6;
      ctx.beginPath();
      ctx.moveTo(wx - 7, wy);
      ctx.quadraticCurveTo(wx, wy - 3, wx + 7, wy);
      ctx.stroke();
    }
    const edges: [number, number, number, number, number, number][] = [
      [0, -1, px, py, T, 4],
      [0, 1, px, py + T - 3, T, 3],
      [-1, 0, px, py, 3, T],
      [1, 0, px + T - 3, py, 3, T],
    ];
    ctx.fillStyle = th.lava ? "rgba(60,20,10,0.55)" : `rgba(255,255,255,${0.45 + 0.15 * Math.sin(t * 2 + x + y)})`;
    for (const [dx, dy, ex, ey, ew, eh] of edges) if (!isWet(tileAt(info, x + dx, y + dy))) ctx.fillRect(ex, ey, ew, eh);
    if (ch === "B" || ch === "K") {
      const across = ch === "B" || tileAt(info, x - 1, y) === "K" || tileAt(info, x + 1, y) === "K" || !isWet(tileAt(info, x - 1, y));
      ctx.fillStyle = "#b98a55";
      ctx.fillRect(px + (across ? 0 : 6), py + (across ? 6 : 0), across ? T : T - 12, across ? T - 12 : T);
      ctx.strokeStyle = "#7a5530";
      ctx.lineWidth = 1.5;
      for (let k = 1; k < 5; k++) {
        ctx.beginPath();
        if (across) {
          ctx.moveTo(px + (k * T) / 5, py + 6);
          ctx.lineTo(px + (k * T) / 5, py + T - 6);
        } else {
          ctx.moveTo(px + 6, py + (k * T) / 5);
          ctx.lineTo(px + T - 6, py + (k * T) / 5);
        }
        ctx.stroke();
      }
      ctx.fillStyle = "#8a5f36";
      if (across) {
        ctx.fillRect(px, py + 4, T, 4);
        ctx.fillRect(px, py + T - 8, T, 4);
      } else {
        ctx.fillRect(px + 4, py, 4, T);
        ctx.fillRect(px + T - 8, py, 4, T);
      }
    }
    return;
  }

  if (ch === "X") {
    ctx.fillStyle = th.wall[0];
    ctx.fillRect(px, py, T, T);
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    for (let k = 0; k < 3; k++) ctx.fillRect(px + hash1(x + k, y) * 40, py + hash1(y + k, x) * 40, 5, 3);
    if (tileAt(info, x, y + 1) !== "X") {
      ctx.fillStyle = th.wall[2];
      ctx.fillRect(px, py + T - 16, T, 16);
      ctx.fillStyle = "rgba(0,0,0,0.25)";
      ctx.fillRect(px, py + T - 3, T, 3);
    }
    if (tileAt(info, x, y - 1) !== "X") {
      ctx.fillStyle = th.wall[1];
      ctx.fillRect(px, py, T, 4);
    }
    return;
  }

  ctx.fillStyle = th.ground[hsh > 0.55 ? 1 : 0];
  ctx.fillRect(px, py, T, T);
  if (th.tallKind === "grass" && hsh < 0.35 && ch === ".") {
    ctx.strokeStyle = th.tall[1];
    ctx.lineWidth = 1.5;
    const gx = px + 10 + hsh * 60;
    const gy = py + 14 + hsh * 50;
    ctx.beginPath();
    ctx.moveTo(gx - 3, gy);
    ctx.lineTo(gx - 1, gy - 5);
    ctx.moveTo(gx + 2, gy);
    ctx.lineTo(gx + 3, gy - 6);
    ctx.stroke();
  }
  if (ch === "=") {
    ctx.fillStyle = th.path;
    ctx.fillRect(px, py, T, T);
    ctx.fillStyle = th.pathEdge;
    const pathy = (c: string) => c === "=" || c === "B" || c === "K";
    if (!pathy(tileAt(info, x, y - 1))) ctx.fillRect(px, py, T, 3);
    if (!pathy(tileAt(info, x, y + 1))) ctx.fillRect(px, py + T - 3, T, 3);
    if (!pathy(tileAt(info, x - 1, y))) ctx.fillRect(px, py, 3, T);
    if (!pathy(tileAt(info, x + 1, y))) ctx.fillRect(px + T - 3, py, 3, T);
    for (let k = 0; k < 2; k++) circle(ctx, px + 8 + hash1(x * 3 + k, y) * 32, py + 8 + hash1(y * 3 + k, x) * 32, 2, th.pathEdge);
  } else if (ch === ",") drawDecor(ctx, th, x, y, t);
  else if (ch === "*") drawTall(ctx, th, x, y, t, false);
}

function drawTree(ctx: CanvasRenderingContext2D, th: Theme, x: number, y: number, t: number) {
  const cx = x * T + T / 2;
  const by = (y + 1) * T - 4;
  const hsh = hash1(x * 17 + y * 29, 5);
  const s = 0.92 + hsh * 0.22;
  const [leaf, leafDark, leafLine] = th.leaf;
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  ctx.beginPath();
  ctx.ellipse(cx, by, 18 * s, 6, 0, 0, TAU);
  ctx.fill();
  if (th.tree === "palm") {
    const lean = (hsh - 0.5) * 16;
    ctx.lineCap = "round";
    for (let k = 0; k < 7; k++) {
      const u = k / 6;
      circle(ctx, cx + lean * u * u, by - 4 - u * 48 * s, 5.5 - u * 1.5, k % 2 ? th.trunk : shade(th.trunk, -0.15));
    }
    const topX = cx + lean;
    const topY = by - 52 * s;
    for (let k = 0; k < 6; k++) {
      const a = -Math.PI / 2 + (k - 2.5) * 0.62 + Math.sin(t * 1.5 + x + k) * 0.05;
      const len = 30 * s;
      const ex = topX + Math.cos(a) * len;
      const ey = topY + Math.sin(a) * len * 0.6 + 10;
      ctx.beginPath();
      ctx.moveTo(topX, topY);
      ctx.quadraticCurveTo(topX + Math.cos(a) * len * 0.6, topY + Math.sin(a) * len - 8, ex, ey);
      ctx.quadraticCurveTo(topX + Math.cos(a) * len * 0.5, topY + Math.sin(a) * len * 0.3, topX, topY);
      inked(ctx, k % 2 ? leaf : leafDark, leafLine, 1.6);
    }
    circle(ctx, topX - 3, topY + 4, 3.5, "#6b4424");
    circle(ctx, topX + 3, topY + 5, 3.5, "#7a5230");
    return;
  }
  ctx.fillStyle = th.trunk;
  ctx.strokeStyle = shade(th.trunk, -0.45);
  ctx.lineWidth = 2;
  roundRect(ctx, cx - 5, by - 22, 10, 21, 3);
  ctx.fill();
  ctx.stroke();
  if (th.tree === "willow") {
    const cy = by - 36 * s;
    ctx.beginPath();
    ctx.ellipse(cx, cy, 27 * s, 21 * s, 0, 0, TAU);
    inked(ctx, leaf, leafLine);
    ctx.strokeStyle = leafDark;
    ctx.lineWidth = 3;
    for (let k = 0; k < 7; k++) {
      const sx = cx - 22 * s + k * 7.3 * s;
      const sway = Math.sin(t * 1.4 + x + k * 0.6) * 2.5;
      ctx.beginPath();
      ctx.moveTo(sx, cy + 4);
      ctx.quadraticCurveTo(sx + sway, cy + 16, sx + sway * 1.5, cy + 24 + (k % 2) * 6);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.beginPath();
    ctx.ellipse(cx - 8 * s, cy - 9 * s, 10 * s, 5 * s, -0.3, 0, TAU);
    ctx.fill();
    return;
  }
  const blobs: [number, number, number][] = [
    [0, -40, 19],
    [-13, -30, 15],
    [13, -30, 15],
    [0, -26, 15],
  ];
  ctx.strokeStyle = leafLine;
  ctx.lineWidth = 5;
  for (const [dx, dy, r] of blobs) {
    ctx.beginPath();
    ctx.arc(cx + dx * s, by + dy * s, r * s, 0, TAU);
    ctx.stroke();
  }
  for (const [dx, dy, r] of blobs) circle(ctx, cx + dx * s, by + dy * s + 2, r * s, leafDark);
  for (const [dx, dy, r] of blobs) circle(ctx, cx + dx * s, by + dy * s - 1, r * s * 0.86, leaf);
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.beginPath();
  ctx.ellipse(cx - 6 * s, by - 46 * s, 8 * s, 4 * s, -0.4, 0, TAU);
  ctx.fill();
}

function drawRock(ctx: CanvasRenderingContext2D, th: Theme, x: number, y: number) {
  const cx = x * T + T / 2;
  const by = (y + 1) * T - 6;
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ctx.beginPath();
  ctx.ellipse(cx, by + 2, 20, 6, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - 19, by);
  ctx.quadraticCurveTo(cx - 20, by - 22, cx - 4, by - 26);
  ctx.quadraticCurveTo(cx + 18, by - 26, cx + 19, by);
  ctx.closePath();
  inked(ctx, th.rock[0], th.rock[1], 2.4);
  ctx.fillStyle = "rgba(255,255,255,0.25)";
  ctx.beginPath();
  ctx.ellipse(cx - 6, by - 18, 6, 3, -0.3, 0, TAU);
  ctx.fill();
}

function drawFence(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const px = x * T;
  const by = (y + 1) * T - 8;
  ctx.fillStyle = "#a77b4f";
  ctx.strokeStyle = "#6b4a2a";
  ctx.lineWidth = 2;
  for (const fx of [px + 6, px + T - 14]) {
    roundRect(ctx, fx, by - 30, 8, 30, 2);
    ctx.fill();
    ctx.stroke();
  }
  for (const ry of [by - 24, by - 12]) {
    ctx.fillRect(px - 2, ry, T + 4, 5);
    ctx.strokeRect(px - 2, ry, T + 4, 5);
  }
}

function drawSign(ctx: CanvasRenderingContext2D, x: number, y: number) {
  const cx = x * T + T / 2;
  const by = (y + 1) * T - 6;
  ctx.fillStyle = "#7a5230";
  ctx.fillRect(cx - 3, by - 22, 6, 22);
  roundRect(ctx, cx - 18, by - 38, 36, 22, 4);
  inked(ctx, "#c99a62", "#6b4424", 2);
  ctx.strokeStyle = "#8a5f36";
  ctx.lineWidth = 2;
  for (const ly of [by - 31, by - 24]) {
    ctx.beginPath();
    ctx.moveTo(cx - 11, ly);
    ctx.lineTo(cx + 11, ly);
    ctx.stroke();
  }
}

const ROOFS = ["#d9534f", "#4f7fd9", "#5cae6a", "#c0679f"];

function drawHouse(ctx: CanvasRenderingContext2D, pr: Prop) {
  const px = pr.x * T;
  const py = pr.y * T;
  const w = pr.w * T;
  const h = pr.h * T;
  const roof = ROOFS[pr.i % ROOFS.length];
  const wallTop = py + h * 0.36;
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  ctx.fillRect(px + 6, py + h - 6, w - 4, 8);
  ctx.beginPath();
  ctx.rect(px + 6, wallTop, w - 12, py + h - wallTop - 2);
  inked(ctx, "#f4e3c3", "#8a6f4a", 2.5);
  ctx.fillStyle = shade(roof, -0.4);
  ctx.fillRect(px + w - 34, py - 4, 14, 26);
  ctx.beginPath();
  ctx.moveTo(px - 6, wallTop + 6);
  ctx.lineTo(px + 18, py - 10);
  ctx.lineTo(px + w - 18, py - 10);
  ctx.lineTo(px + w + 6, wallTop + 6);
  ctx.closePath();
  inked(ctx, roof, shade(roof, -0.5), 2.5);
  ctx.strokeStyle = "rgba(255,255,255,0.22)";
  ctx.lineWidth = 2;
  for (let k = 1; k < 4; k++) {
    const yy = py - 10 + k * ((wallTop + 6 - (py - 10)) / 4);
    ctx.beginPath();
    ctx.moveTo(px + 4, yy);
    ctx.lineTo(px + w - 4, yy);
    ctx.stroke();
  }
  const dw = 24;
  const dx = px + w / 2 - dw / 2;
  roundRect(ctx, dx, py + h - 40, dw, 38, 6);
  inked(ctx, "#8a5a32", "#4a2e16", 2);
  circle(ctx, dx + dw - 6, py + h - 20, 2, "#ffd166");
  for (const wx of [px + 18, px + w - 40]) {
    roundRect(ctx, wx, py + h - 50, 22, 20, 3);
    inked(ctx, "#bfe6ff", "#6b5a40", 2);
    ctx.strokeStyle = "#6b5a40";
    ctx.beginPath();
    ctx.moveTo(wx + 11, py + h - 50);
    ctx.lineTo(wx + 11, py + h - 30);
    ctx.moveTo(wx, py + h - 40);
    ctx.lineTo(wx + 22, py + h - 40);
    ctx.stroke();
  }
}

function drawShip(ctx: CanvasRenderingContext2D, pr: Prop, t: number) {
  const px = pr.x * T;
  const py = pr.y * T;
  const w = pr.w * T;
  const h = pr.h * T;
  ctx.save();
  ctx.translate(px + w / 2, py + h - 10);
  ctx.rotate(-0.05);
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ctx.beginPath();
  ctx.ellipse(0, 6, w / 2, 10, 0, 0, TAU);
  ctx.fill();
  const top = -h * 0.62;
  ctx.beginPath();
  ctx.moveTo(-w / 2, top + 6);
  ctx.lineTo(w / 2 + 6, top);
  ctx.quadraticCurveTo(w / 2 - 10, 0, w / 2 - 40, 2);
  ctx.lineTo(-w / 2 + 30, 2);
  ctx.quadraticCurveTo(-w / 2 + 6, -6, -w / 2, top + 6);
  ctx.closePath();
  inked(ctx, "#8a5a32", "#4a2e16", 3);
  ctx.fillStyle = "#a8794a";
  ctx.beginPath();
  ctx.moveTo(-w / 2, top + 6);
  ctx.lineTo(w / 2 + 6, top);
  ctx.lineTo(w / 2 + 2, top + 10);
  ctx.lineTo(-w / 2 + 2, top + 16);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#6b4424";
  ctx.lineWidth = 2;
  for (const u of [0.35, 0.6, 0.82]) {
    const yy = top + (0 - top) * u;
    ctx.beginPath();
    ctx.moveTo(-w / 2 + 6 + u * 10, yy);
    ctx.lineTo(w / 2 - 10 - u * 20, yy - 4);
    ctx.stroke();
  }
  for (let k = 0; k < 4; k++) circle(ctx, -w / 2 + 40 + k * 44, top * 0.48, 5.5, "#2a1a10");
  ctx.fillStyle = "#6b4424";
  ctx.fillRect(-4, -h - 40, 8, h + top + 46);
  const flap = Math.sin(t * 3) * 4;
  ctx.beginPath();
  ctx.moveTo(4, -h - 34);
  ctx.quadraticCurveTo(44 + flap, -h - 10, 4, -h + 16);
  ctx.closePath();
  inked(ctx, "#f4ead6", "#9c8a6a", 2);
  ctx.beginPath();
  ctx.moveTo(4, -h - 40);
  ctx.lineTo(30 + flap, -h - 34 + flap * 0.3);
  ctx.lineTo(4, -h - 26);
  ctx.closePath();
  inked(ctx, "#1f1a2a", "#000000", 1.5);
  circle(ctx, 14 + flap * 0.4, -h - 33, 4.5, "#ff9ec4");
  circle(ctx, 12.5 + flap * 0.4, -h - 34, 0.9, "#1f1a2a");
  circle(ctx, 15.5 + flap * 0.4, -h - 34, 0.9, "#1f1a2a");
  ctx.restore();
}

function drawRest(ctx: CanvasRenderingContext2D, th: Theme, pr: Prop, t: number) {
  const cx = pr.x * T + (pr.w * T) / 2;
  const cy = pr.y * T + (pr.h * T) / 2;
  if (th.rest === "fountain") {
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.beginPath();
    ctx.ellipse(cx, cy + 10, 44, 30, 0, 0, TAU);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(cx, cy + 4, 42, 30, 0, 0, TAU);
    inked(ctx, "#c3ccd6", "#6b7684", 3);
    ctx.beginPath();
    ctx.ellipse(cx, cy + 2, 32, 21, 0, 0, TAU);
    ctx.fillStyle = "#6fc6e0";
    ctx.fill();
    for (let k = 0; k < 3; k++) {
      const r = ((t * 0.6 + k / 3) % 1) * 30;
      ctx.strokeStyle = `rgba(255,255,255,${0.5 * (1 - r / 30)})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(cx, cy + 2, r, r * 0.65, 0, 0, TAU);
      ctx.stroke();
    }
    roundRect(ctx, cx - 6, cy - 26, 12, 28, 4);
    inked(ctx, "#c3ccd6", "#6b7684", 2);
    for (let k = 0; k < 8; k++) {
      const u = (t * 1.2 + k / 8) % 1;
      const side = k % 2 ? 1 : -1;
      circle(ctx, cx + side * u * 22, cy - 28 - u * 18 + u * u * 34, 2.2, "rgba(190,235,255,0.9)");
    }
  } else if (th.rest === "shrine") {
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.beginPath();
    ctx.ellipse(cx, cy + 20, 18, 6, 0, 0, TAU);
    ctx.fill();
    roundRect(ctx, cx - 12, cy + 4, 24, 16, 3);
    inked(ctx, "#b8c0c8", "#5f6870", 2);
    ctx.fillStyle = "#9aa4ae";
    ctx.fillRect(cx - 4, cy - 14, 8, 18);
    const glow = 0.6 + 0.4 * Math.sin(t * 2);
    ctx.fillStyle = `rgba(255,236,160,${0.25 * glow})`;
    ctx.beginPath();
    ctx.arc(cx, cy - 18, 20, 0, TAU);
    ctx.fill();
    roundRect(ctx, cx - 9, cy - 26, 18, 14, 3);
    inked(ctx, `rgba(255,230,150,${0.7 + 0.3 * glow})`, "#5f6870", 2);
    ctx.beginPath();
    ctx.moveTo(cx - 15, cy - 26);
    ctx.lineTo(cx, cy - 38);
    ctx.lineTo(cx + 15, cy - 26);
    ctx.closePath();
    inked(ctx, "#8a96a2", "#4a525a", 2);
    ctx.fillStyle = "#3d8f4a";
    ctx.beginPath();
    ctx.ellipse(cx + 14, cy + 20, 9, 4, 0, 0.4, TAU - 0.4);
    ctx.lineTo(cx + 14, cy + 20);
    ctx.fill();
    circle(ctx, cx + 16, cy + 18, 3, "#ffb3cf");
  } else if (th.rest === "campfire") {
    ctx.fillStyle = "rgba(255,150,60,0.18)";
    ctx.beginPath();
    ctx.arc(cx, cy + 8, 34 + Math.sin(t * 9) * 2, 0, TAU);
    ctx.fill();
    for (let k = 0; k < 7; k++) {
      const a = (k / 7) * TAU;
      ctx.beginPath();
      ctx.ellipse(cx + Math.cos(a) * 16, cy + 12 + Math.sin(a) * 7, 5, 4, 0, 0, TAU);
      inked(ctx, "#9a948c", "#5a544c", 1.5);
    }
    limb(ctx, cx - 12, cy + 16, cx + 12, cy + 6, 5, "#7a4a24", "#3f2410");
    limb(ctx, cx + 12, cy + 16, cx - 12, cy + 6, 5, "#8a5a32", "#3f2410");
    const fl = Math.sin(t * 11) * 2;
    const flames: [string, number, number][] = [
      ["#ff6b3a", 14, 30],
      ["#ffb347", 10, 22],
      ["#fff1a8", 5, 12],
    ];
    for (const [c, w2, h2] of flames) {
      ctx.fillStyle = c;
      ctx.beginPath();
      ctx.moveTo(cx - w2, cy + 10);
      ctx.quadraticCurveTo(cx - w2, cy + 10 - h2 * 0.6, cx + fl * 0.5, cy + 10 - h2 - fl);
      ctx.quadraticCurveTo(cx + w2, cy + 10 - h2 * 0.6, cx + w2, cy + 10);
      ctx.closePath();
      ctx.fill();
    }
    for (let k = 0; k < 4; k++) {
      const u = (t * 0.8 + k / 4) % 1;
      circle(ctx, cx + Math.sin(k * 3 + t * 2) * 8, cy - 14 - u * 34, 1.6, `rgba(255,190,90,${1 - u})`);
    }
  } else {
    const glow = 0.6 + 0.4 * Math.sin(t * 2);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 40);
    g.addColorStop(0, `rgba(150,200,255,${0.45 * glow})`);
    g.addColorStop(1, "rgba(150,200,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(cx - 40, cy - 40, 80, 80);
    const shards: [number, number, number, string][] = [
      [-10, 22, 0.25, "#8ab8ff"],
      [10, 18, -0.3, "#b79cff"],
      [0, 32, 0, "#a6d8ff"],
    ];
    for (const [dx, hh, a, c] of shards) {
      ctx.save();
      ctx.translate(cx + dx, cy + 18);
      ctx.rotate(a);
      ctx.beginPath();
      ctx.moveTo(-7, 0);
      ctx.lineTo(-6, -hh * 0.75);
      ctx.lineTo(0, -hh);
      ctx.lineTo(6, -hh * 0.75);
      ctx.lineTo(7, 0);
      ctx.closePath();
      inked(ctx, c, "#2c3a7a", 2);
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      ctx.fillRect(-3, -hh * 0.7, 2, hh * 0.55);
      ctx.restore();
    }
  }
}

function drawMoonstone(ctx: CanvasRenderingContext2D, pr: Prop, t: number, bright = false) {
  const cx = pr.x * T + (pr.w * T) / 2;
  const by = (pr.y + pr.h) * T - 6;
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(cx, by, 34, 10, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - 30, by);
  ctx.lineTo(cx - 20, by - 34);
  ctx.lineTo(cx + 20, by - 34);
  ctx.lineTo(cx + 30, by);
  ctx.closePath();
  inked(ctx, "#5d6b8f", "#262f4a", 3);
  ctx.beginPath();
  ctx.ellipse(cx, by - 34, 24, 7, 0, 0, TAU);
  inked(ctx, "#7a88ad", "#262f4a", 2);
  const pulse = 0.5 + 0.5 * Math.sin(t * 0.8);
  const gr = bright ? 120 : 46;
  const g = ctx.createRadialGradient(cx, by - 58, 0, cx, by - 58, gr);
  g.addColorStop(0, bright ? `rgba(230,240,255,${0.7 + pulse * 0.2})` : `rgba(200,220,255,${0.18 + pulse * 0.1})`);
  g.addColorStop(1, "rgba(200,220,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(cx - gr, by - 58 - gr, gr * 2, gr * 2);
  ctx.beginPath();
  ctx.arc(cx, by - 56, 20, 0, TAU);
  inked(ctx, bright ? "#f4f8ff" : "#aeb7c9", bright ? "#8fb0e0" : "#4a5470", 2.5);
  ctx.fillStyle = "rgba(255,255,255,0.3)";
  ctx.beginPath();
  ctx.ellipse(cx - 7, by - 63, 7, 4, -0.5, 0, TAU);
  ctx.fill();
}

function drawAmbient(ctx: CanvasRenderingContext2D, th: Theme, t: number, cx: number, cy: number) {
  if (th.ambient === "pollen" || th.ambient === "motes" || th.ambient === "embers") {
    const n = th.ambient === "pollen" ? 24 : 34;
    for (let i = 0; i < n; i++) {
      const sp = 0.5 + hash1(i, 71);
      const rise = th.ambient === "embers" ? 22 : th.ambient === "motes" ? 6 : 10;
      const px = (((hash1(i, 73) * W * 1.4 - cx * 0.3 + Math.sin(t * 0.5 + i) * 24) % W) + W) % W;
      const py = (((hash1(i, 75) * H - t * rise * sp - cy * 0.3) % H) + H) % H;
      const tw = 0.5 + 0.5 * Math.sin(t * 2 + i);
      const color = th.ambient === "pollen" ? `rgba(255,246,192,${0.3 + tw * 0.4})` : th.ambient === "embers" ? `rgba(255,150,70,${0.4 + tw * 0.5})` : `rgba(170,210,255,${0.3 + tw * 0.5})`;
      circle(ctx, px, py, th.ambient === "pollen" ? 1.6 + sp : 1.2 + sp, color);
    }
  } else if (th.ambient === "dragonflies") {
    for (let k = 0; k < 3; k++) {
      const px = W / 2 + Math.sin(t * 0.45 + k * 2.1) * 380;
      const py = H / 2 + Math.sin(t * 0.7 + k * 1.3) * 180 - 40;
      const dir = Math.cos(t * 0.45 + k * 2.1) > 0 ? 1 : -1;
      ctx.strokeStyle = "#2bb3b1";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(px - 10 * dir, py);
      ctx.lineTo(px + 6 * dir, py);
      ctx.stroke();
      const flap = Math.sin(t * 40 + k) * 3;
      ctx.fillStyle = "rgba(230,250,255,0.75)";
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.ellipse(px + 2 * dir, py + s * (5 + flap * 0.3), 7, 3, s * 0.3, 0, TAU);
        ctx.fill();
      }
    }
  } else if (th.ambient === "spray") {
    for (let k = 0; k < 2; k++) {
      const px = ((t * 40 + k * 520) % (W + 200)) - 100;
      const py = 70 + k * 46 + Math.sin(t * 1.3 + k) * 10;
      const flap = Math.sin(t * 6 + k) * 5;
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(px - 12, py - flap);
      ctx.quadraticCurveTo(px - 5, py - 6, px, py);
      ctx.quadraticCurveTo(px + 5, py - 6, px + 12, py - flap);
      ctx.stroke();
    }
    for (let i = 0; i < 18; i++) {
      const px = (((hash1(i, 81) * W - cx * 0.2) % W) + W) % W;
      const py = (((hash1(i, 83) * H + Math.sin(t + i) * 10 - cy * 0.2) % H) + H) % H;
      circle(ctx, px, py, 1.4, `rgba(255,255,255,${0.25 + 0.25 * Math.sin(t * 3 + i)})`);
    }
  }
}

function wrapText(ctx: CanvasRenderingContext2D, s: string, maxW: number) {
  const out: string[] = [];
  let cur = "";
  for (const word of s.split(" ")) {
    const test = cur ? `${cur} ${word}` : word;
    if (cur && ctx.measureText(test).width > maxW) {
      out.push(cur);
      cur = word;
    } else cur = test;
  }
  if (cur) out.push(cur);
  return out;
}

function drawDialog(ctx: CanvasRenderingContext2D, S: Game) {
  const d = S.dialog;
  if (!d) return;
  const b = DIALOG_BOX;
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  roundRect(ctx, b.x, b.y + 4, b.w, b.h, 18);
  ctx.fill();
  panel(ctx, b.x, b.y, b.w, b.h, 18, "rgba(18,30,44,0.95)");
  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.lineWidth = 2;
  roundRect(ctx, b.x + 1, b.y + 1, b.w - 2, b.h - 2, 17);
  ctx.stroke();
  if (d.speaker) {
    ctx.font = "800 16px system-ui, sans-serif";
    const tw = ctx.measureText(d.speaker).width + 28;
    panel(ctx, b.x + 18, b.y - 16, tw, 30, 12, "#ff5fa2");
    text(ctx, d.speaker, b.x + 32, b.y + 5, "800 16px system-ui, sans-serif", "#ffffff");
  }
  ctx.font = "600 21px system-ui, sans-serif";
  const lines = wrapText(ctx, d.lines[d.i], b.w - 56);
  let left = Math.floor(d.shown);
  lines.slice(0, 3).forEach((ln, i) => {
    if (left <= 0) return;
    text(ctx, ln.slice(0, left), b.x + 28, b.y + (d.speaker ? 46 : 40) + i * 30, "600 21px system-ui, sans-serif", "#ffffff");
    left -= ln.length + 1;
  });
  if (d.shown >= d.lines[d.i].length) {
    const bob = Math.sin(S.t * 6) * 3;
    ctx.fillStyle = "#ffe08a";
    ctx.beginPath();
    ctx.moveTo(b.x + b.w - 34, b.y + b.h - 24 + bob);
    ctx.lineTo(b.x + b.w - 20, b.y + b.h - 24 + bob);
    ctx.lineTo(b.x + b.w - 27, b.y + b.h - 15 + bob);
    ctx.closePath();
    ctx.fill();
  }
}

function drawTouchControls(ctx: CanvasRenderingContext2D, S: Game, tg: Target | null) {
  if (!S.dialog) {
    ctx.fillStyle = "rgba(10,20,30,0.28)";
    ctx.beginPath();
    ctx.arc(PAD.x, PAD.y, PAD.r, 0, TAU);
    ctx.fill();
    for (const d of DIRS) {
      const [dx, dy] = DELTA[d];
      const bx = PAD.x + dx * 54;
      const by = PAD.y + dy * 54;
      const on = S.pad === d;
      ctx.fillStyle = on ? "rgba(255,95,162,0.9)" : "rgba(255,255,255,0.28)";
      roundRect(ctx, bx - 28, by - 28, 56, 56, 14);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.moveTo(bx + dx * 12, by + dy * 12);
      ctx.lineTo(bx - dx * 6 + dy * 10, by - dy * 6 + dx * 10);
      ctx.lineTo(bx - dx * 6 - dy * 10, by - dy * 6 - dx * 10);
      ctx.closePath();
      ctx.fill();
    }
  }
  const pressed = S.t - S.aDown < 0.15;
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.arc(A_BTN.x, A_BTN.y + 4, A_BTN.r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = pressed ? "#d94a8a" : tg || S.dialog ? "#ff5fa2" : "rgba(255,255,255,0.3)";
  ctx.beginPath();
  ctx.arc(A_BTN.x, A_BTN.y + (pressed ? 3 : 0), A_BTN.r, 0, TAU);
  ctx.fill();
  text(ctx, "A", A_BTN.x, A_BTN.y + 11 + (pressed ? 3 : 0), "900 32px system-ui, sans-serif", "#ffffff", "center");
}

function drawWorld(ctx: CanvasRenderingContext2D, S: Game) {
  const t = S.t;
  const info = areaInfo(S.area);
  const th = THEMES[S.area];
  const [cx, cy] = camera(S);
  ctx.fillStyle = th.wall[0];
  ctx.fillRect(0, 0, W, H);
  const x0 = Math.max(0, Math.floor(cx / T));
  const x1 = Math.min(info.w - 1, Math.floor((cx + W) / T));
  const y0 = Math.max(0, Math.floor(cy / T));
  const y1 = Math.min(info.h - 1, Math.floor((cy + H) / T));

  ctx.save();
  ctx.translate(-cx, -cy);
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) drawTile(ctx, info, th, x, y, t);

  const objs: { y: number; draw: () => void }[] = [];
  for (let y = y0; y <= Math.min(info.h - 1, y1 + 2); y++) {
    for (let x = Math.max(0, x0 - 1); x <= Math.min(info.w - 1, x1 + 1); x++) {
      const ch = info.def.map[y][x];
      const by = (y + 1) * T;
      if (ch === "#") objs.push({ y: by, draw: () => drawTree(ctx, th, x, y, t) });
      else if (ch === "o") objs.push({ y: by, draw: () => drawRock(ctx, th, x, y) });
      else if (ch === "F") objs.push({ y: by, draw: () => drawFence(ctx, x, y) });
      else if (ch === "S") objs.push({ y: by, draw: () => drawSign(ctx, x, y) });
    }
  }
  for (const pr of info.props) {
    if ((pr.x + pr.w) * T < cx - T || pr.x * T > cx + W + T || pr.y * T > cy + H + T || (pr.y + pr.h) * T < cy - 2 * T) continue;
    const by = (pr.y + pr.h) * T;
    if (pr.kind === "house") objs.push({ y: by, draw: () => drawHouse(ctx, pr) });
    else if (pr.kind === "ship") objs.push({ y: by, draw: () => drawShip(ctx, pr, t) });
    else if (pr.kind === "rest") objs.push({ y: by, draw: () => drawRest(ctx, th, pr, t) });
    else objs.push({ y: by, draw: () => drawMoonstone(ctx, pr, t, !!S.save?.flags.beatOlm) });
  }
  for (const it of info.def.items ?? []) {
    if (S.save?.flags[itemFlag(S.area, it.x, it.y)]) continue;
    objs.push({ y: (it.y + 1) * T - 20, draw: () => drawPickup(ctx, it.x, it.y, it.item, t) });
  }
  if (S.area === "grotto" && !S.save?.flags.beatOlm) {
    // The Elder Olm sleeps in the left pool until it is woken at the Moonstone.
    objs.push({
      y: 6 * T,
      draw: () => {
        ctx.save();
        ctx.globalAlpha = 0.85;
        drawLippie(ctx, "olm", 5 * T, 6 * T + 10, 70, { t, face: "sleepy" });
        ctx.restore();
        ctx.strokeStyle = `rgba(200,230,255,${0.4 + 0.2 * Math.sin(t * 2)})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(5 * T, 6 * T + 12, 90 + Math.sin(t) * 6, 14, 0, 0, TAU);
        ctx.stroke();
      },
    });
  }
  const standers: [number, number][] = [];
  for (const n of info.def.npcs) {
    const def = NPCS[n.id];
    let [nx, ny] = npcPos(S, n);
    const walk = S.npcWalk[n.id];
    if (walk) {
      nx = lerp(walk.fx, nx, walk.t);
      ny = lerp(walk.fy, ny, walk.t);
    }
    const dir = S.npcFace[n.id] ?? n.dir;
    standers.push([Math.round(nx), Math.round(ny)]);
    objs.push({
      y: (ny + 1) * T,
      draw: () => drawTamer(ctx, def.look, nx * T + T / 2, (ny + 1) * T - 3, 54, dir, walk ? walk.t * 6 : 0, t + n.x),
    });
  }
  const [px, py] = playerPos(S.p);
  standers.push([Math.round(px), Math.round(py)]);
  if (S.save) {
    const look = S.save.tamer;
    const p = S.p;
    objs.push({ y: (py + 1) * T + 0.5, draw: () => drawTamer(ctx, look, px * T + T / 2, (py + 1) * T - 3, 54, p.dir, p.walk, t) });
  }
  objs.sort((a, b) => a.y - b.y);
  for (const o of objs) o.draw();
  for (const [sx, sy] of standers) if (tileAt(info, sx, sy) === "*") drawTall(ctx, th, sx, sy, t, true);
  ctx.restore();

  drawAmbient(ctx, th, t, cx, cy);
  if (th.tint) {
    ctx.fillStyle = th.tint;
    ctx.fillRect(0, 0, W, H);
  }
  if (th.dark > 0) {
    const lx = px * T + T / 2 - cx;
    const ly = py * T + T / 2 - cy;
    const flicker = Math.sin(t * 7) * 4 + Math.sin(t * 13) * 2;
    const g = ctx.createRadialGradient(lx, ly, 50, lx, ly, 250 + flicker);
    g.addColorStop(0, "rgba(10,6,18,0)");
    g.addColorStop(1, `rgba(10,6,18,${th.dark})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = "lighter";
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const ch = info.def.map[y][x];
        if (ch !== "~" && ch !== "R") continue;
        const gx = x * T + T / 2 - cx;
        const gy = y * T + T / 2 - cy;
        const lg = ctx.createRadialGradient(gx, gy, 0, gx, gy, 60);
        lg.addColorStop(0, ch === "~" ? "rgba(255,120,40,0.22)" : "rgba(120,170,255,0.3)");
        lg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = lg;
        ctx.fillRect(gx - 60, gy - 60, 120, 120);
      }
    }
    ctx.globalCompositeOperation = "source-over";
  }

  // HUD
  ctx.font = "800 18px system-ui, sans-serif";
  const nameW = ctx.measureText(AREA_NAMES[S.area]).width + 32;
  panel(ctx, 14, 14, nameW, 40);
  text(ctx, AREA_NAMES[S.area], 30, 41, "800 18px system-ui, sans-serif", "#ffffff");
  if (!S.dialog) drawButton(ctx, S, buttons(S)[0], "Save & quit", "#334155");

  const tg = S.dialog || S.transit ? null : facingTarget(S);
  if (S.touch) {
    drawTouchControls(ctx, S, tg);
    if (tg) {
      ctx.font = "800 15px system-ui, sans-serif";
      const lw = ctx.measureText(targetLabel(tg)).width + 24;
      panel(ctx, A_BTN.x - lw / 2, A_BTN.y - A_BTN.r - 40, lw, 30, 12, "rgba(18,30,44,0.9)");
      text(ctx, targetLabel(tg), A_BTN.x, A_BTN.y - A_BTN.r - 20, "800 15px system-ui, sans-serif", "#ffe08a", "center");
    }
  } else if (tg) {
    ctx.font = "800 16px system-ui, sans-serif";
    const label = `Space: ${targetLabel(tg)}`;
    const lw = ctx.measureText(label).width + 28;
    panel(ctx, W / 2 - lw / 2, H - 52, lw, 34, 12, "rgba(18,30,44,0.88)");
    text(ctx, label, W / 2, H - 29, "800 16px system-ui, sans-serif", "#ffe08a", "center");
  }

  if (S.banner && S.t - S.banner.t0 < 2.6) {
    const age = S.t - S.banner.t0;
    ctx.globalAlpha = clamp(Math.min(age * 4, (2.6 - age) * 2), 0, 1);
    text(ctx, "Now entering", W / 2, 96, "600 16px system-ui, sans-serif", "#ffffff", "center");
    text(ctx, S.banner.text, W / 2, 132, "900 38px system-ui, sans-serif", "#ffffff", "center");
    ctx.globalAlpha = 1;
  }
  if (S.toast && S.t - S.toast.t0 < 1.8) {
    const age = S.t - S.toast.t0;
    ctx.globalAlpha = clamp(Math.min(age * 6, (1.8 - age) * 3), 0, 1);
    text(ctx, S.toast.text, W / 2, 180 - age * 16, "900 26px system-ui, sans-serif", "#7ee0a0", "center");
    ctx.globalAlpha = 1;
  }
  drawDialog(ctx, S);
  if (S.transit) {
    const tt = S.transit.t;
    ctx.fillStyle = `rgba(8,14,24,${clamp(tt < 0.25 ? tt / 0.25 : (0.5 - tt) / 0.25, 0, 1)})`;
    ctx.fillRect(0, 0, W, H);
  }
}

// ---------- battle ----------

const ENCOUNTER_RATE = 0.1;
const ENCOUNTER_COOLDOWN = 6;
const PARTY_MAX = 4;
const FOE_SPOT = { x: 690, y: 232 };
const YOU_SPOT = { x: 255, y: 400 };
const BOX = { x: 16, y: 404, w: 928, h: 124 };

type BPhase = "intro" | "play" | "menu" | "moves" | "snack" | "swap" | "forceSwap" | "done";

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  t0: number;
  color: string;
}

interface BattleView {
  b: Battle;
  phase: BPhase;
  queue: BEvent[];
  cur: BEvent | null;
  curT: number;
  msg: string;
  msgShown: number;
  show: Record<Side, Fighter>;
  disp: Record<Side, number>;
  hpTo: Record<Side, number>;
  roll: (Extract<BEvent, { k: "roll" }> & { t0: number }) | null;
  dmg: (Extract<BEvent, { k: "dice" }> & { t0: number }) | null;
  jar: (Extract<BEvent, { k: "jar" }> & { t0: number }) | null;
  floats: { text: string; color: string; side: Side; t0: number }[];
  sparks: Spark[];
  hurtT: Record<Side, number>;
  lungeT: Record<Side, number>;
  faintT: Record<Side, number>;
  critT: number;
  shakeT: number;
  t: number;
  focus: number;
  // Trainer battles: who is battling and which Lippies they still have to send out.
  trainer: { name: string; look: TamerLook } | null;
  team: [string, number][];
  // What happens in the world after a win: a gate NPC id or "olm".
  onWin: string | null;
  // What to do when the queue runs dry in the "done" phase.
  exit: "world" | "respawn";
}

interface BBtn extends Btn {
  label: string;
  sub?: string;
  color: string;
  disabled?: boolean;
}

function partyHealthy(S: Game) {
  return !!S.save?.party.some((l) => l.hp > 0);
}

function fastDice(S: Game) {
  return !!S.save?.fastDice;
}

function encounterTable(area: AreaId) {
  return SPECIES.filter((s) => s.catchDC !== null && s.areas.includes(area) && s.id !== "olm");
}

function maybeEncounter(S: Game, audio: Audio) {
  if (!S.save || !partyHealthy(S)) return false;
  S.stepsSinceBattle++;
  if (S.stepsSinceBattle < ENCOUNTER_COOLDOWN || Math.random() > ENCOUNTER_RATE) return false;
  const table = encounterTable(S.area);
  if (!table.length) return false;
  const weights = table.map((s) => (s.rare ? 1 : 4));
  let pick = Math.random() * weights.reduce((a, b) => a + b, 0);
  let sp = table[0];
  for (let i = 0; i < table.length; i++) {
    pick -= weights[i];
    if (pick <= 0) {
      sp = table[i];
      break;
    }
  }
  const level = sp.levels[0] + Math.floor(Math.random() * (sp.levels[1] - sp.levels[0] + 1));
  startBattle(S, audio, { kind: "wild", team: [[sp.id, level]] });
  return true;
}

function firstHealthySlot(S: Game) {
  return S.save ? S.save.party.findIndex((l) => l.hp > 0) : -1;
}

function markSeen(S: Game, form: string) {
  const id = speciesOf(form).id;
  if (S.save && !S.save.journal.seen.includes(id)) S.save.journal.seen.push(id);
}

function foeFighter(kind: Battle["kind"], form: string, level: number) {
  return makeFighter("foe", form, level, { hpScale: kind === "wild" ? WILD_HP : kind === "guardian" ? BOSS_HP : 1 });
}

function startBattle(
  S: Game,
  audio: Audio,
  opts: { kind: Battle["kind"]; team: [string, number][]; trainer?: { name: string; look: TamerLook }; onWin?: string; intro?: string[] },
) {
  const save = S.save;
  const slot = firstHealthySlot(S);
  if (!save || slot < 0 || !opts.team.length) return;
  const knight = save.tamer.background === "knight";
  const you = fighterFromOwned(save.party[slot], slot, knight);
  const [first, ...rest] = opts.team;
  const foe = foeFighter(opts.kind, first[0], first[1]);
  const b = newBattle(opts.kind, you, foe);
  markSeen(S, foe.form);
  S.held = [];
  S.pad = null;
  S.stepsSinceBattle = 0;
  const queue: BEvent[] = [];
  for (const s of opts.intro ?? []) queue.push({ k: "text", s });
  if (opts.kind === "wild") queue.push({ k: "text", s: `A wild ${foe.name} appeared!` });
  else if (opts.trainer) queue.push({ k: "text", s: `${opts.trainer.name} wants to battle!` }, { k: "text", s: `${opts.trainer.name} sends out ${foe.name}!` });
  queue.push({ k: "text", s: `Go, ${you.name}!` });
  S.bv = {
    b,
    phase: "intro",
    queue,
    cur: null,
    curT: 0,
    msg: "",
    msgShown: 0,
    show: { you, foe },
    disp: { you: you.hp, foe: foe.hp },
    hpTo: { you: you.hp, foe: foe.hp },
    roll: null,
    dmg: null,
    jar: null,
    floats: [],
    sparks: [],
    hurtT: { you: -9, foe: -9 },
    lungeT: { you: -9, foe: -9 },
    faintT: { you: -9, foe: -9 },
    critT: -9,
    shakeT: -9,
    t: 0,
    focus: 0,
    trainer: opts.trainer ?? null,
    team: rest,
    onWin: opts.onWin ?? null,
    exit: "world",
  };
  audio.encounter();
  enter(S, "battle");
}

function jarLength(wobbles: number) {
  return 0.85 + wobbles * 0.55 + 0.6;
}

function eventLength(S: Game, e: BEvent) {
  const fast = fastDice(S);
  switch (e.k) {
    case "text":
      return e.s.length / 60 + (fast ? 0.5 : 0.95);
    case "roll":
      return fast ? 0.75 : 1.5;
    case "dice":
      return fast ? 0.3 : 0.6;
    case "hit":
    case "heal":
      return 0.55;
    case "lunge":
      return 0.25;
    case "sleep":
      return 0.3;
    case "faint":
      return 0.8;
    case "swap":
      return 0.45;
    case "jar":
      return jarLength(e.wobbles);
    default:
      return 0;
  }
}

function syncParty(S: Game) {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return;
  const you = bv.b.you;
  if (you.slot >= 0 && save.party[you.slot]) save.party[you.slot].hp = you.hp;
}

function burst(bv: BattleView, x: number, y: number, n: number, colors: string[]) {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * TAU;
    const sp = 80 + Math.random() * 220;
    bv.sparks.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 80, t0: bv.t, color: colors[i % colors.length] });
  }
}

function beginEvent(S: Game, e: BEvent, audio: Audio) {
  const bv = S.bv;
  if (!bv) return;
  const t = bv.t;
  switch (e.k) {
    case "text":
      bv.msg = e.s;
      bv.msgShown = 0;
      break;
    case "roll":
      bv.roll = { ...e, t0: t };
      bv.dmg = null;
      audio.dice();
      break;
    case "dice":
      bv.dmg = { ...e, t0: t };
      break;
    case "hit": {
      bv.hpTo[e.target] = e.hp;
      bv.hurtT[e.target] = e.amount > 0 ? t : -9;
      bv.floats.push({ text: e.amount > 0 ? `-${e.amount}` : "No effect!", color: e.mult > 1 ? "#ffd166" : "#ff8a8a", side: e.target, t0: t });
      if (e.crit || e.mult > 1) bv.shakeT = t;
      if (e.crit) {
        bv.critT = t;
        const spot = e.target === "you" ? YOU_SPOT : FOE_SPOT;
        burst(bv, spot.x, spot.y - 70, 26, ["#ffd166", "#fff1a8", "#ffffff"]);
      }
      if (e.amount > 0) audio.hit(e.crit || e.mult > 1);
      break;
    }
    case "heal":
      bv.hpTo[e.target] = e.hp;
      if (e.amount > 0) bv.floats.push({ text: `+${e.amount}`, color: "#7ee0a0", side: e.target, t0: t });
      audio.heal();
      break;
    case "lunge":
      bv.lungeT[e.who] = t;
      break;
    case "faint":
      bv.faintT[e.target] = t;
      audio.faint();
      break;
    case "swap":
      bv.show[e.side] = e.fighter;
      bv.disp[e.side] = e.fighter.hp;
      bv.hpTo[e.side] = e.fighter.hp;
      bv.faintT[e.side] = -9;
      bv.hurtT[e.side] = -9;
      bv.roll = null;
      bv.dmg = null;
      if (e.side === "foe") bv.jar = null;
      break;
    case "jar":
      bv.jar = { ...e, t0: t };
      audio.whoosh();
      break;
    case "seen": {
      const save = S.save;
      if (save) {
        save.matchups = save.matchups ?? [];
        if (!save.matchups.includes(e.key)) save.matchups.push(e.key);
      }
      break;
    }
    case "sfx":
      if (e.name === "levelUp") audio.levelUp();
      else if (e.name === "evolve") audio.evolve();
      else audio.whoosh();
      break;
  }
}

// XP for every awake Lippie after a foe is beaten or caught. Evolutions are saved for the evolution scene.
function grantXp(S: Game, foeLevel: number) {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return;
  const b = bv.b;
  const xp = winXp(foeLevel, b.kind !== "wild");
  const knight = save.tamer.background === "knight";
  save.party.forEach((l, i) => {
    if (l.hp <= 0) return;
    const active = i === b.you.slot;
    const before = l.form;
    const g = gainXp(l, active ? xp : Math.ceil(xp / 2));
    for (const s of g.lines) {
      if (/evolved into/.test(s)) continue;
      if (/grew to level/.test(s)) bv.queue.push({ k: "sfx", name: "levelUp" });
      if (/is evolving/.test(s)) bv.queue.push({ k: "sfx", name: "evolve" });
      bv.queue.push({ k: "text", s });
    }
    if (g.evolvedTo) S.evoQueue.push({ from: before, to: g.evolvedTo, name: l.nickname, slot: i });
    else if (active && g.leveled) bv.queue.push({ k: "swap", side: "you", fighter: fighterFromOwned(l, i, knight) });
  });
}

function addLippie(S: Game, l: OwnedLippie): string {
  const save = S.save;
  if (!save) return "";
  const id = speciesOf(l.form).id;
  if (!save.journal.caught.includes(id)) save.journal.caught.push(id);
  if (!save.journal.seen.includes(id)) save.journal.seen.push(id);
  if (save.party.length < PARTY_MAX) {
    save.party.push(l);
    return `${l.nickname} joined your team!`;
  }
  save.pond.push(l);
  return `Your team is full, so ${l.nickname} went to swim in Gran's pond.`;
}

// Called when every queued event has played.
function afterQueue(S: Game, audio: Audio) {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return;
  const b = bv.b;
  syncParty(S);
  if (bv.phase === "done") {
    exitBattle(S, audio);
    return;
  }
  bv.roll = null;
  bv.dmg = null;
  if (b.result === "ran") {
    bv.phase = "done";
    return;
  }
  if (b.result === "caught") {
    const foe = b.foe;
    const owned = newOwned(foe.form, foe.level);
    owned.hp = Math.max(1, Math.round((foe.hp / foe.maxHp) * owned.hp));
    grantXp(S, foe.level);
    bv.queue.push({ k: "text", s: addLippie(S, owned) });
    bv.phase = "done";
    burst(bv, FOE_SPOT.x, FOE_SPOT.y - 30, 30, ["#ffd166", "#7fd8ff", "#ff9ec4", "#ffffff"]);
    audio.chime();
    checkJournalDone(S);
    return;
  }
  if (b.result === "won") {
    grantXp(S, b.foe.level);
    const next = bv.team.shift();
    if (next && bv.trainer) {
      const foe = foeFighter(b.kind, next[0], next[1]);
      markSeen(S, foe.form);
      b.foe = foe;
      b.result = null;
      bv.queue.push({ k: "text", s: `${bv.trainer.name} sends out ${foe.name}!` }, { k: "swap", side: "foe", fighter: foe });
      bv.phase = "play";
      return;
    }
    bv.phase = "done";
    return;
  }
  if (b.you.hp <= 0) {
    if (partyHealthy(S)) {
      bv.phase = "forceSwap";
      bv.msg = "Who will you send out next?";
      bv.msgShown = 99;
      bv.focus = 0;
      return;
    }
    bv.queue.push(
      { k: "text", s: "All your Lippies are too sleepy to keep going!" },
      { k: "text", s: "You hurry back to rest. Everyone feels better after a nap!" },
    );
    bv.exit = "respawn";
    bv.onWin = null;
    bv.phase = "done";
    return;
  }
  bv.phase = "menu";
  bv.msg = `What will ${b.you.name} do?`;
  bv.msgShown = 99;
  bv.focus = 0;
}

function stepBattle(S: Game, dt: number, audio: Audio) {
  const bv = S.bv;
  if (!bv) return;
  bv.t += dt;
  for (const side of ["you", "foe"] as Side[]) {
    const d = bv.hpTo[side] - bv.disp[side];
    bv.disp[side] += Math.sign(d) * Math.min(Math.abs(d), dt * Math.max(14, bv.show[side].maxHp * 1.4));
  }
  bv.floats = bv.floats.filter((f) => bv.t - f.t0 < 1.2);
  bv.sparks = bv.sparks.filter((s) => bv.t - s.t0 < 1.1);
  for (const s of bv.sparks) {
    s.x += s.vx * dt;
    s.y += s.vy * dt;
    s.vy += 420 * dt;
  }
  const jar = bv.jar;
  if (jar && bv.cur?.k === "jar") {
    // Wobble clicks and the final pop or burst happen on the jar's own timeline.
    const age = bv.curT;
    const prev = age - dt;
    for (let k = 0; k < jar.wobbles; k++) {
      const at = 0.85 + k * 0.55;
      if (prev < at && age >= at) audio.wobble();
    }
    const end = 0.85 + jar.wobbles * 0.55;
    if (prev < end && age >= end) {
      if (jar.caught) {
        audio.jarPop();
        burst(bv, FOE_SPOT.x, FOE_SPOT.y - 20, 18, ["#ffd166", "#ffffff"]);
      } else {
        audio.jarBurst();
        burst(bv, FOE_SPOT.x, FOE_SPOT.y - 20, 16, ["#bfe6ff", "#ffffff"]);
      }
    }
  }
  if (bv.msgShown < bv.msg.length) bv.msgShown = Math.min(bv.msg.length, bv.msgShown + dt * 70);
  if (bv.phase !== "intro" && bv.phase !== "play" && bv.phase !== "done") return;
  if (bv.phase === "intro" && bv.t < 0.7) return;
  if (bv.cur) {
    bv.curT += dt;
    if (bv.curT < eventLength(S, bv.cur)) return;
    bv.cur = null;
  }
  while (!bv.cur && bv.queue.length) {
    const e = bv.queue.shift() as BEvent;
    beginEvent(S, e, audio);
    if (eventLength(S, e) > 0) {
      bv.cur = e;
      bv.curT = 0;
    }
  }
  if (!bv.cur && !bv.queue.length) {
    if (bv.phase === "intro") bv.phase = "play";
    afterQueue(S, audio);
  }
}

// A tap during playback finishes the current text or skips the current animation.
function skipBattle(S: Game) {
  const bv = S.bv;
  if (!bv || !bv.cur) return;
  if (bv.cur.k === "text" && bv.msgShown < bv.msg.length) {
    bv.msgShown = bv.msg.length;
    return;
  }
  // Let the jar finish wobbling: that suspense is the fun part.
  if (bv.cur.k === "jar") return;
  bv.curT = 99;
}

function runAction(S: Game, action: Action) {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return;
  const healer = save.tamer.background === "healer";
  playTurn(bv.b, action, Math.random, (item) => {
    if (item === "cone") return 999;
    const amount = roll(2, 8, Math.random).reduce((a, c) => a + c, 0);
    return healer ? Math.round(amount * 1.5) : amount;
  });
  bv.queue.push(...bv.b.ev.splice(0));
  bv.phase = "play";
  bv.roll = null;
  bv.dmg = null;
}

function battleAct(S: Game, id: string, audio: Audio) {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return;
  const [, what, arg] = id.split(":");
  audio.tap();
  if (what === "fast") {
    save.fastDice = !save.fastDice;
    return;
  }
  if (what === "back") {
    bv.phase = "menu";
    bv.focus = 0;
  } else if (what === "fight") {
    bv.phase = "moves";
    bv.focus = 0;
  } else if (what === "snack") {
    bv.phase = "snack";
    bv.focus = 0;
  } else if (what === "swap") {
    bv.phase = "swap";
    bv.focus = 0;
  } else if (what === "run") runAction(S, { k: "run" });
  else if (what === "jar") {
    if ((save.items.jar ?? 0) <= 0) return;
    save.items.jar--;
    const dc = speciesOf(bv.b.foe.form).catchDC ?? 99;
    runAction(S, { k: "jar", name: "Bubble Jar", bonus: save.tamer.background === "ranger" ? 2 : 0, dc });
  } else if (what === "inspire") {
    bv.b.inspireUsed = true;
    bv.b.you.inspired = true;
    bv.queue.push({ k: "text", s: `${save.tamer.name} plays an inspiring tune! ${bv.b.you.name}'s next roll gets advantage.` });
    bv.phase = "play";
  } else if (what === "move") runAction(S, { k: "move", id: arg });
  else if (what === "item") {
    if ((save.items[arg] ?? 0) <= 0) return;
    save.items[arg]--;
    runAction(S, { k: "snack", item: arg });
  } else if (what === "to") {
    const slot = Number(arg);
    const l = save.party[slot];
    if (!l || l.hp <= 0 || slot === bv.b.you.slot) return;
    const f = fighterFromOwned(l, slot, save.tamer.background === "knight");
    if (bv.phase === "forceSwap") {
      bv.b.you = f;
      bv.queue.push({ k: "swap", side: "you", fighter: f }, { k: "text", s: `Go, ${f.name}!` });
      bv.phase = "play";
    } else runAction(S, { k: "swap", slot, fighter: f });
  }
}

function exitBattle(S: Game, audio: Audio) {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return;
  syncParty(S);
  const won = bv.b.result === "won" || bv.b.result === "caught";
  if (bv.exit === "respawn") {
    healParty(S);
    const r = save.respawn;
    S.area = r.area;
    S.p = newPlayer(r.x, r.y, "down");
    S.npcFace = {};
    S.npcWalk = {};
    S.banner = { text: AREA_NAMES[S.area], t0: S.t };
    savePos(S);
  }
  if (won && bv.onWin) S.pendingWin = bv.onWin;
  S.bv = null;
  S.stepsSinceBattle = 0;
  audio.whoosh();
  saveCurrent(S);
  enter(S, "world");
  resumeWorld(S, audio);
}

function healParty(S: Game) {
  for (const l of S.save?.party ?? []) l.hp = ownedMaxHp(l);
}

function matchupHint(S: Game, atk: ElementId | null, def: ElementId) {
  if (!atk || !S.save) return "";
  const known = S.save.tamer.background === "scholar" || (S.save.matchups ?? []).includes(`${atk}>${def}`);
  if (!known) return "";
  const m = effectiveness(atk, def);
  return m === 0 ? "🚫" : m > 1 ? "⬆️" : m < 1 ? "⬇️" : "";
}

function battleButtons(S: Game): BBtn[] {
  const bv = S.bv;
  const save = S.save;
  if (!bv || !save) return [];
  const out: BBtn[] = [{ id: "bt:fast", x: W - 132, y: 14, w: 118, h: 40, label: fastDice(S) ? "⏩ Fast" : "▶ Normal", color: fastDice(S) ? "#ff5fa2" : "#334155" }];
  const b = bv.b;
  const right = BOX.x + BOX.w - 12;
  if (bv.phase === "menu") {
    const wild = b.kind === "wild";
    const items: [string, string, string, boolean][] = [["bt:fight", "⚔️ Fight", "#e0524f", false]];
    if (wild) items.push(["bt:jar", `🫧 Jar ×${save.items.jar ?? 0}`, "#2bb3b1", (save.items.jar ?? 0) <= 0]);
    items.push(
      ["bt:snack", "🍰 Snack", "#f28c38", !Object.entries(save.items).some(([k, n]) => k !== "jar" && n > 0)],
      ["bt:swap", "🔁 Swap", "#3f7fd8", save.party.filter((l, i) => l.hp > 0 && i !== b.you.slot).length === 0],
    );
    if (wild) items.push(["bt:run", "🏃 Run", "#6b7280", false]);
    if (save.tamer.background === "bard" && !b.inspireUsed) items.push(["bt:inspire", "🎻 Inspire", "#8a5cd6", false]);
    const bw = 150;
    const bh = 48;
    const cols = 3;
    items.forEach(([id, label, color, disabled], i) => {
      const c = i % cols;
      const r = Math.floor(i / cols);
      out.push({ id, x: right - (cols - c) * (bw + 10), y: BOX.y + 12 + r * (bh + 8), w: bw, h: bh, label, color, disabled });
    });
  } else if (bv.phase === "moves") {
    b.you.moves.forEach((mid, i) => {
      const m = MOVES[mid];
      const el = moveElement(m, b.you);
      const usesLeft = m.uses ? b.you.uses[mid] ?? 0 : null;
      const hint = m.kind === "attack" ? matchupHint(S, el, b.foe.element) : "";
      const icon = el ? ELEMENTS[el].icon : m.kind === "heal" ? "💚" : m.kind === "lullaby" ? "💤" : "✊";
      const dice = m.kind === "lullaby" ? "sleep" : `${m.dice[0]}d${m.dice[1]}${m.kind === "heal" ? " heal" : ""}`;
      out.push({
        id: `bt:move:${mid}`,
        x: BOX.x + 12 + (i % 2) * 330,
        y: BOX.y + 10 + Math.floor(i / 2) * 56,
        w: 320,
        h: 50,
        label: `${icon} ${m.name} ${hint}`,
        sub: usesLeft !== null ? `${dice} · ${usesLeft} left` : dice,
        color: el ? shade(ELEMENTS[el].color, -0.35) : "#334155",
        disabled: usesLeft === 0,
      });
    });
    out.push({ id: "bt:back", x: right - 130, y: BOX.y + 66, w: 130, h: 50, label: "Back", color: "#6b7280" });
  } else if (bv.phase === "snack") {
    const snacks = Object.entries(save.items).filter(([k, n]) => k !== "jar" && n > 0);
    snacks.forEach(([k, n], i) => {
      out.push({ id: `bt:item:${k}`, x: BOX.x + 12 + i * 230, y: BOX.y + 12, w: 220, h: 56, label: `${ITEMS[k].icon} ${ITEMS[k].name} ×${n}`, sub: ITEMS[k].desc, color: "#334155" });
    });
    out.push({ id: "bt:back", x: right - 130, y: BOX.y + 66, w: 130, h: 50, label: "Back", color: "#6b7280" });
  } else if (bv.phase === "swap" || bv.phase === "forceSwap") {
    save.party.forEach((l, i) => {
      const here = i === b.you.slot;
      out.push({
        id: `bt:to:${i}`,
        x: BOX.x + 12 + (i % 3) * 232,
        y: BOX.y + 10 + Math.floor(i / 3) * 56,
        w: 222,
        h: 50,
        label: `${l.nickname} Lv ${l.level}`,
        sub: here ? "Battling now" : `HP ${l.hp}/${ownedMaxHp(l)}`,
        color: "#334155",
        disabled: here || l.hp <= 0,
      });
    });
    if (bv.phase === "swap") out.push({ id: "bt:back", x: right - 130, y: BOX.y + 66, w: 130, h: 50, label: "Back", color: "#6b7280" });
  }
  return out;
}

// ---------- battle drawing ----------

const BATTLE_SKY: Record<AreaId, [string, string, string]> = {
  village: ["#8fcaf2", "#dff1fb", "#7cc576"],
  meadow: ["#86c8f5", "#e4f4ff", "#86cf6e"],
  pond: ["#7fc0e8", "#d8eef8", "#74c27e"],
  cove: ["#6fc0ea", "#e6f6ff", "#f0dca5"],
  hollow: ["#1a1220", "#3a2a40", "#463a4c"],
  grotto: ["#0f1830", "#24345a", "#33456a"],
};

function drawD20(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number, value: number, color: string, dim = false) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.globalAlpha = dim ? 0.45 : 1;
  ctx.beginPath();
  for (let k = 0; k < 6; k++) {
    const a = -Math.PI / 2 + (k * TAU) / 6;
    ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  ctx.closePath();
  inked(ctx, color, shade(color, -0.5), 3);
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  const tri = [0, 2, 4].map((k) => {
    const a = -Math.PI / 2 + (k * TAU) / 6;
    return [Math.cos(a) * r * 0.62, Math.sin(a) * r * 0.62];
  });
  ctx.moveTo(tri[0][0], tri[0][1]);
  ctx.lineTo(tri[1][0], tri[1][1]);
  ctx.lineTo(tri[2][0], tri[2][1]);
  ctx.closePath();
  ctx.stroke();
  ctx.rotate(-rot);
  text(ctx, String(value), 0, r * 0.32, `900 ${Math.round(r * 0.8)}px system-ui, sans-serif`, "#ffffff", "center");
  ctx.restore();
}

function drawSmallDie(ctx: CanvasRenderingContext2D, x: number, y: number, sides: number, value: number) {
  const s = 30;
  ctx.save();
  ctx.translate(x, y);
  if (sides === 6 || sides === 4 || sides === 2) {
    roundRect(ctx, -s / 2, -s / 2, s, s, 7);
  } else {
    ctx.beginPath();
    ctx.moveTo(0, -s * 0.62);
    ctx.lineTo(s * 0.55, 0);
    ctx.lineTo(0, s * 0.62);
    ctx.lineTo(-s * 0.55, 0);
    ctx.closePath();
  }
  inked(ctx, "#ffffff", "#334155", 2.5);
  text(ctx, String(value), 0, 6, "900 16px system-ui, sans-serif", "#1e293b", "center");
  ctx.restore();
}

// A glass jar with a pink lid. (x, y) = bottom center.
function drawJar(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, rot: number, t: number, glow = false) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(s, s);
  if (glow) {
    ctx.fillStyle = "rgba(255,230,140,0.35)";
    ctx.beginPath();
    ctx.arc(0, -22, 36, 0, TAU);
    ctx.fill();
  }
  roundRect(ctx, -17, -40, 34, 40, 10);
  ctx.fillStyle = "rgba(190,235,255,0.75)";
  ctx.fill();
  ctx.strokeStyle = "#4a8fb0";
  ctx.lineWidth = 2.5;
  ctx.stroke();
  for (let k = 0; k < 3; k++) {
    const u = (t * 0.7 + k / 3) % 1;
    circle(ctx, -8 + k * 8, -6 - u * 28, 2.5 + k * 0.6, "rgba(255,255,255,0.8)");
  }
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  roundRect(ctx, -12, -34, 5, 26, 3);
  ctx.fill();
  roundRect(ctx, -20, -50, 40, 12, 5);
  inked(ctx, "#ff5fa2", "#a8406a", 2.5);
  ctx.restore();
}

function drawPlate(ctx: CanvasRenderingContext2D, S: Game, side: Side, x: number, y: number) {
  const bv = S.bv;
  if (!bv) return;
  const f = bv.show[side];
  const w = 340;
  const h = side === "you" ? 92 : 78;
  panel(ctx, x, y, w, h, 16, "rgba(18,30,44,0.9)");
  const el = ELEMENTS[f.element];
  const sp = speciesOf(f.form);
  const showClass = side === "you" || S.save?.tamer.background === "scholar" || S.save?.journal.caught.includes(sp.id);
  text(ctx, `${f.name}`, x + 16, y + 30, "800 21px system-ui, sans-serif", "#ffffff");
  text(ctx, `Lv ${f.level}`, x + w - 16, y + 30, "800 17px system-ui, sans-serif", "#ffe08a", "right");
  text(ctx, `${el.icon} ${el.name}${showClass ? `  ${CLASSES[sp.cls].icon} ${CLASSES[sp.cls].name}` : ""}`, x + 16, y + 52, "600 14px system-ui, sans-serif", el.color);
  if (side === "foe" && S.save?.journal.caught.includes(sp.id)) text(ctx, "🫧", x + w - 18, y + 52, "600 14px system-ui, sans-serif", "#ffffff", "right");
  const frac = clamp(bv.disp[side] / f.maxHp, 0, 1);
  const bx = x + 16;
  const by = y + 60;
  const bw = w - 32;
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  roundRect(ctx, bx, by, bw, 10, 5);
  ctx.fill();
  ctx.fillStyle = frac > 0.5 ? "#7ee0a0" : frac > 0.2 ? "#ffd166" : "#ff6b6b";
  roundRect(ctx, bx, by, Math.max(frac > 0 ? 6 : 0, bw * frac), 10, 5);
  ctx.fill();
  if (side === "you") text(ctx, `HP ${Math.round(bv.disp[side])} / ${f.maxHp}`, x + w - 16, y + 86, "700 13px system-ui, sans-serif", "#d8e6f0", "right");
  const tags: string[] = [];
  if (f.sleepy > 0) tags.push("💤 Asleep");
  if (f.slowed > 0) tags.push("🐢 Slowed");
  if (f.marked) tags.push("🎯 Marked");
  if (f.inspired) tags.push("🎻 Inspired");
  if (f.spicy) tags.push("🌶️ Spicy");
  if (tags.length) text(ctx, tags.join("  "), x + 16, y + (side === "you" ? 86 : 96), "700 13px system-ui, sans-serif", "#ffe08a");
}

function ownedMorph(S: Game, side: Side) {
  const bv = S.bv;
  if (!bv || side !== "you") return undefined;
  const l = S.save?.party[bv.show.you.slot];
  return l?.midnight ? MORPHS[3] : undefined;
}

function drawFighter(ctx: CanvasRenderingContext2D, S: Game, side: Side) {
  const bv = S.bv;
  if (!bv) return;
  const f = bv.show[side];
  const spot = side === "you" ? YOU_SPOT : FOE_SPOT;
  const t = bv.t;
  const enterT = clamp(t / 0.6, 0, 1);
  const slide = (1 - enterT) * (side === "foe" ? 420 : -420);
  const lu = t - bv.lungeT[side];
  const lunge = lu < 0.25 ? Math.sin((lu / 0.25) * Math.PI) * 34 * (side === "you" ? 1 : -1) : 0;
  const hu = t - bv.hurtT[side];
  const shakeX = hu < 0.45 ? Math.sin(hu * 60) * 7 * (1 - hu / 0.45) : 0;
  const fa = t - bv.faintT[side];
  const fainted = bv.faintT[side] > -9 && fa >= 0;
  const sink = fainted ? clamp(fa / 0.7, 0, 1) : 0;
  const won = (bv.b.result === "won" || bv.b.result === "caught") && side === "you";
  const face: Face = fainted ? "sleepy" : hu < 0.7 ? "ouch" : f.sleepy > 0 ? "sleepy" : won ? "happy" : "normal";
  const size = (side === "you" ? 175 : 150) * (f.form === "olm" ? 0.6 : 1);
  let scale = 1;
  let alpha = 1 - sink;
  let whiteout = hu < 0.12 || t - bv.critT < 0.1;
  const jar = side === "foe" ? bv.jar : null;
  if (jar) {
    // The Lippie shrinks into the jar, and pops back out if the catch fails.
    const age = t - jar.t0;
    const end = 0.85 + jar.wobbles * 0.55;
    if (age >= 0.5 && (jar.caught || age < end)) {
      scale = clamp(1 - (age - 0.5) / 0.3, 0, 1);
      whiteout = true;
      if (scale <= 0) alpha = 0;
    } else if (!jar.caught && age >= end) {
      scale = clamp((age - end) / 0.25, 0, 1) * (1 + 0.15 * Math.sin(clamp((age - end) / 0.25, 0, 1) * Math.PI));
    }
  }
  if (alpha > 0 && scale > 0) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.rect(0, 0, W, spot.y + 6);
    ctx.clip();
    const x = spot.x + slide + lunge + shakeX;
    const y = spot.y + sink * size * 0.9;
    const morph = ownedMorph(S, side);
    drawLippie(ctx, f.form, x, y, size * scale, { t, face, flip: side === "you", morph });
    if (whiteout) drawLippie(ctx, f.form, x, y, size * scale, { t, face, flip: side === "you", mono: "rgba(255,255,255,0.85)" });
    ctx.restore();
  }
  for (const fl of bv.floats.filter((q) => q.side === side)) {
    const age = t - fl.t0;
    ctx.globalAlpha = clamp((1.2 - age) * 3, 0, 1);
    text(ctx, fl.text, spot.x, spot.y - size * 0.95 - age * 40, "900 30px system-ui, sans-serif", fl.color, "center");
    ctx.globalAlpha = 1;
  }
}

function drawJarAnim(ctx: CanvasRenderingContext2D, S: Game) {
  const bv = S.bv;
  const jar = bv?.jar;
  if (!bv || !jar) return;
  const age = bv.t - jar.t0;
  const end = 0.85 + jar.wobbles * 0.55;
  if (!jar.caught && age > end + 0.05) return;
  if (age < 0.5) {
    const u = age / 0.5;
    const x = lerp(YOU_SPOT.x + 40, FOE_SPOT.x, u);
    const y = lerp(YOU_SPOT.y - 120, FOE_SPOT.y - 60, u) - Math.sin(u * Math.PI) * 120;
    drawJar(ctx, x, y, 0.9, u * TAU * 1.5, bv.t);
    return;
  }
  const land = clamp((age - 0.5) / 0.35, 0, 1);
  const y = lerp(FOE_SPOT.y - 60, FOE_SPOT.y, land * land);
  let rot = 0;
  if (age >= 0.85 && age < end) {
    const k = (age - 0.85) / 0.55;
    rot = Math.sin((k % 1) * TAU) * 0.35 * (1 - (k % 1));
  }
  drawJar(ctx, FOE_SPOT.x, y, 1.2, rot, bv.t, jar.caught && age >= end);
}

function drawBattle(ctx: CanvasRenderingContext2D, S: Game) {
  const bv = S.bv;
  if (!bv) return;
  const t = bv.t;
  const sh = t - bv.shakeT;
  ctx.save();
  if (sh < 0.35) ctx.translate(Math.sin(sh * 70) * 8 * (1 - sh / 0.35), Math.cos(sh * 55) * 5 * (1 - sh / 0.35));
  const [top, mid, ground] = BATTLE_SKY[S.area];
  const sky = ctx.createLinearGradient(0, 0, 0, 300);
  sky.addColorStop(0, top);
  sky.addColorStop(1, mid);
  ctx.fillStyle = sky;
  ctx.fillRect(-20, -20, W + 40, H + 40);
  ctx.fillStyle = ground;
  ctx.beginPath();
  ctx.moveTo(-20, 280);
  for (let x = -20; x <= W + 20; x += 20) ctx.lineTo(x, 270 + Math.sin(x * 0.012) * 10);
  ctx.lineTo(W + 20, H + 20);
  ctx.lineTo(-20, H + 20);
  ctx.closePath();
  ctx.fill();
  const th = THEMES[S.area];
  for (const [p, rx, ry] of [[FOE_SPOT, 130, 28], [YOU_SPOT, 160, 34]] as const) {
    ctx.fillStyle = shade(th.ground[0], -0.18);
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + 4, rx, ry, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = shade(th.ground[0], 0.12);
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, rx * 0.92, ry * 0.8, 0, 0, TAU);
    ctx.fill();
  }
  drawAmbient(ctx, th, S.t, 0, 0);
  if (bv.trainer) {
    const slideIn = clamp(t / 0.6, 0, 1);
    drawTamer(ctx, bv.trainer.look, 880 + (1 - slideIn) * 200, 250, 92, "left", 0, t);
  }

  drawFighter(ctx, S, "foe");
  drawJarAnim(ctx, S);
  drawFighter(ctx, S, "you");
  for (const s of bv.sparks) {
    const age = t - s.t0;
    ctx.globalAlpha = clamp(1.1 - age, 0, 1);
    circle(ctx, s.x, s.y, 3.5, s.color);
  }
  ctx.globalAlpha = 1;
  if (t > 0.5) {
    drawPlate(ctx, S, "foe", 24, 24);
    drawPlate(ctx, S, "you", 590, 296);
  }

  // dice
  const r = bv.roll;
  if (r) {
    const age = t - r.t0;
    const tumble = fastDice(S) ? 0.35 : 0.8;
    const u = clamp(age / tumble, 0, 1);
    const fromX = r.who === "you" ? 300 : 660;
    const dx = lerp(fromX, 480, u);
    const dy = 176 - Math.abs(Math.sin(u * Math.PI * 2.5)) * 60 * (1 - u);
    const rolling = u < 1;
    const shown = rolling ? 1 + Math.floor(hash1(Math.floor(age * 16), 7) * 20) : r.nat;
    const glowCol = !rolling && r.nat === 20 ? "#ffd166" : !rolling && r.nat === 1 ? "#6b7280" : r.label ? "#2bb3b1" : "#8a5cd6";
    if (!rolling && r.nat === 20) {
      ctx.fillStyle = "rgba(255,209,102,0.35)";
      ctx.beginPath();
      ctx.arc(dx, dy, 70 + Math.sin(t * 8) * 6, 0, TAU);
      ctx.fill();
    }
    if (r.other !== undefined) drawD20(ctx, dx + 64, dy + 8, 26, rolling ? age * 9 : 0, rolling ? 1 + Math.floor(hash1(Math.floor(age * 16), 9) * 20) : r.other, "#8a5cd6", !rolling);
    drawD20(ctx, dx, dy, 40, rolling ? age * 12 : 0, shown, glowCol);
    const who = r.label ? `${S.save?.tamer.name ?? "You"} rolls to catch` : `${bv.show[r.who].name} rolls`;
    text(ctx, who, dx, dy - 50, "800 15px system-ui, sans-serif", "#ffffff", "center");
    if (!rolling) {
      const total = r.nat + r.mod;
      // Catch rolls leave the verdict to the wobbling jar.
      const verdict = r.label ? "" : r.nat === 1 ? "OOPS!" : r.nat === 20 ? "CRIT!" : r.hit ? "HIT!" : "MISS";
      const vc = r.hit ? (r.nat === 20 ? "#ffd166" : "#7ee0a0") : "#ff8a8a";
      panel(ctx, 480 - 160, dy + 50, 320, verdict ? 64 : 40, 14, "rgba(18,30,44,0.9)");
      text(ctx, `${r.nat} + ${r.mod} = ${total}  vs  ${r.label ?? "Guard"} ${r.vs}`, 480, dy + 76, "800 18px system-ui, sans-serif", "#ffffff", "center");
      if (verdict) text(ctx, verdict, 480, dy + 104, "900 22px system-ui, sans-serif", vc, "center");
    }
  }
  const d = bv.dmg;
  if (d && t - d.t0 < 3) {
    const n = d.rolls.length;
    const startX = 480 - ((n - 1) * 38) / 2;
    const y = r ? 176 + 140 : 220;
    d.rolls.forEach((v, i) => drawSmallDie(ctx, startX + i * 38, y, d.sides, v));
    if (d.bonus) text(ctx, `+${d.bonus}`, startX + n * 38 - 6, y + 7, "900 18px system-ui, sans-serif", "#ffe08a");
  }
  ctx.restore();

  // box
  panel(ctx, BOX.x, BOX.y, BOX.w, BOX.h, 18, "rgba(18,30,44,0.95)");
  const btns = battleButtons(S);
  const menuOpen = bv.phase !== "intro" && bv.phase !== "play" && bv.phase !== "done";
  const focusId = btns.slice(1).filter((b) => !b.disabled)[bv.focus]?.id;
  if (!menuOpen || bv.phase === "menu" || bv.phase === "forceSwap") {
    ctx.font = "600 22px system-ui, sans-serif";
    const maxW = bv.phase === "menu" ? 400 : BOX.w - 60;
    const lines = wrapText(ctx, bv.msg, maxW);
    let left = Math.floor(bv.msgShown);
    lines.slice(0, 3).forEach((ln, i) => {
      if (left <= 0) return;
      text(ctx, ln.slice(0, left), BOX.x + 26, BOX.y + 40 + i * 30, "600 22px system-ui, sans-serif", "#ffffff");
      left -= ln.length + 1;
    });
    if (!menuOpen && bv.cur?.k === "text" && bv.msgShown >= bv.msg.length) {
      const bob = Math.sin(t * 6) * 3;
      ctx.fillStyle = "#ffe08a";
      ctx.beginPath();
      ctx.moveTo(BOX.x + BOX.w - 34, BOX.y + BOX.h - 26 + bob);
      ctx.lineTo(BOX.x + BOX.w - 20, BOX.y + BOX.h - 26 + bob);
      ctx.lineTo(BOX.x + BOX.w - 27, BOX.y + BOX.h - 17 + bob);
      ctx.closePath();
      ctx.fill();
    }
  }
  btns.forEach((b) => {
    const focused = S.kbd && menuOpen && b.id === focusId;
    ctx.globalAlpha = b.disabled ? 0.38 : 1;
    drawButton(ctx, S, b, "", b.color);
    ctx.globalAlpha = 1;
    if (b.sub) {
      text(ctx, b.label, b.x + 14, b.y + 22, "800 17px system-ui, sans-serif", "#ffffff");
      text(ctx, b.sub, b.x + 14, b.y + 41, "600 12px system-ui, sans-serif", "#e2e8f0");
    } else text(ctx, b.label, b.x + b.w / 2, b.y + b.h / 2 + 7, "800 17px system-ui, sans-serif", "#ffffff", "center");
    if (focused) {
      ctx.strokeStyle = "#ffe08a";
      ctx.lineWidth = 3;
      roundRect(ctx, b.x - 3, b.y - 3, b.w + 6, b.h + 6, 16);
      ctx.stroke();
    }
  });
  if (bv.t < 0.35) {
    ctx.fillStyle = `rgba(255,255,255,${1 - bv.t / 0.35})`;
    ctx.fillRect(0, 0, W, H);
  }
}

// ---------- story ----------

const itemFlag = (area: AreaId, x: number, y: number) => `item:${area}:${x},${y}`;

function itemsLine(items: Record<string, number>) {
  return Object.entries(items)
    .map(([k, n]) => `${n} ${ITEMS[k].name}${n > 1 && !ITEMS[k].name.endsWith("s") ? "s" : ""}`)
    .join(" and ");
}

function giveItems(S: Game, items: Record<string, number>) {
  if (!S.save) return;
  for (const [k, n] of Object.entries(items)) S.save.items[k] = (S.save.items[k] ?? 0) + n;
}

function checkJournalDone(S: Game) {
  const save = S.save;
  if (!save || save.flags.midnight) return;
  if (SPECIES.every((sp) => save.journal.caught.includes(sp.id))) save.flags.midnight = true;
}

// After a battle or cutscene: play any evolutions, then any story that follows a win.
function resumeWorld(S: Game, audio: Audio) {
  const save = S.save;
  if (!save) return;
  const evo = S.evoQueue.shift();
  if (evo) {
    S.evo = { ...evo, t: 0 };
    audio.evolve();
    enter(S, "evolve");
    return;
  }
  const win = S.pendingWin;
  if (win) {
    S.pendingWin = null;
    handleWin(S, win, audio);
    return;
  }
  if (save.flags.midnight && !save.flags.midnightTold) {
    save.flags.midnightTold = true;
    audio.chime();
    say(S, "", STORY.journalDone.map((l) => l.replace("{total}", String(TOTAL_LIPPIES))));
  }
}

function handleWin(S: Game, id: string, audio: Audio) {
  const save = S.save;
  if (!save) return;
  if (id === "olm") {
    save.flags.beatOlm = true;
    saveCurrent(S);
    audio.chime();
    say(S, "", STORY.olmWin, () => {
      const msg = addLippie(S, newOwned("olm", 11));
      checkJournalDone(S);
      say(S, "", [msg], () => {
        saveCurrent(S);
        S.ending = { t: 0 };
        enter(S, "ending");
      });
    });
    return;
  }
  const place = areaInfo(S.area).def.npcs.find((n) => n.id === id);
  const def = NPCS[id];
  const gate = def.gate;
  if (!place || !gate) return;
  save.flags[gate.flag] = true;
  if (place.aside) {
    S.npcWalk[id] = { fx: place.x, fy: place.y, t: 0 };
    S.npcFace[id] = place.aside[0] < place.x ? "left" : place.aside[0] > place.x ? "right" : place.aside[1] < place.y ? "up" : "down";
  }
  const rewards: string[] = [];
  const reward = gate.reward;
  if (reward?.items) {
    giveItems(S, reward.items);
    rewards.push(`You got ${itemsLine(reward.items)}!`);
  }
  if (reward?.lippie) {
    rewards.push(addLippie(S, newOwned(reward.lippie[0], reward.lippie[1])));
    checkJournalDone(S);
  }
  audio.confirm();
  saveCurrent(S);
  say(S, def.name, gate.winLines, () => {
    if (!rewards.length) return resumeWorld(S, audio);
    audio.pickup();
    say(S, "", rewards, () => resumeWorld(S, audio));
  });
}

function gateTeam(S: Game, team: [string, number][] | "rival"): [string, number][] {
  if (team !== "rival") return team;
  const starter = STARTERS.find((s) => S.save?.flags[`starter_${s}`]) ?? "emberlotl";
  return [[RIVAL_STARTER[starter], 4]];
}

function tiredLines(S: Game) {
  return S.save?.party.length ? ["Your Lippies are too tired to battle. Rest at a rest spot first!"] : ["You need a Lippie partner first!"];
}

function pickUpItem(S: Game, audio: Audio) {
  const save = S.save;
  if (!save) return;
  const it = areaInfo(S.area).def.items?.find((i) => i.x === S.p.x && i.y === S.p.y);
  if (!it || save.flags[itemFlag(S.area, it.x, it.y)]) return;
  save.flags[itemFlag(S.area, it.x, it.y)] = true;
  giveItems(S, { [it.item]: it.count });
  S.toast = { text: `Found ${itemsLine({ [it.item]: it.count })}!`, t0: S.t };
  audio.pickup();
}

function drawPickup(ctx: CanvasRenderingContext2D, x: number, y: number, item: string, t: number) {
  const cx = x * T + T / 2;
  const cy = y * T + T / 2 + Math.sin(t * 3 + x) * 3;
  ctx.fillStyle = "rgba(255,240,170,0.35)";
  ctx.beginPath();
  ctx.arc(cx, cy, 18 + Math.sin(t * 4) * 2, 0, TAU);
  ctx.fill();
  if (item === "jar") drawJar(ctx, cx, cy + 14, 0.5, 0, t);
  else {
    roundRect(ctx, cx - 13, cy - 10, 26, 22, 5);
    inked(ctx, "#ff8fbf", "#a8406a", 2);
    ctx.fillStyle = "#ffd166";
    ctx.fillRect(cx - 2.5, cy - 10, 5, 22);
    ctx.fillRect(cx - 13, cy - 1, 26, 5);
  }
  for (let k = 0; k < 3; k++) {
    const a = t * 2 + (k * TAU) / 3;
    circle(ctx, cx + Math.cos(a) * 20, cy + Math.sin(a) * 12, 1.8, "#ffffff");
  }
}

// ---------- music ----------

// Gentle looping tunes: one note per beat, 0 = rest.
const MUSIC: Record<AreaId | "battle", { notes: number[]; beat: number }> = {
  village: { notes: [523, 0, 659, 0, 784, 0, 659, 0, 587, 0, 698, 0, 659, 0, 0, 0], beat: 0.42 },
  meadow: { notes: [587, 740, 880, 0, 740, 0, 659, 0, 587, 659, 740, 0, 659, 0, 0, 0], beat: 0.36 },
  pond: { notes: [440, 0, 523, 0, 659, 0, 0, 0, 587, 0, 523, 0, 494, 0, 0, 0], beat: 0.5 },
  cove: { notes: [523, 659, 784, 659, 523, 0, 587, 0, 659, 784, 880, 784, 659, 0, 0, 0], beat: 0.3 },
  hollow: { notes: [330, 0, 0, 392, 0, 0, 349, 0, 0, 294, 0, 0, 330, 0, 0, 0], beat: 0.45 },
  grotto: { notes: [659, 0, 988, 0, 880, 0, 0, 0, 784, 0, 659, 0, 587, 0, 0, 0], beat: 0.6 },
  battle: { notes: [262, 392, 330, 392, 294, 440, 349, 440, 262, 392, 330, 392, 247, 392, 294, 392], beat: 0.17 },
};

function stepMusic(S: Game, dt: number, audio: Audio) {
  const track = S.mode === "battle" ? MUSIC.battle : S.mode === "world" ? MUSIC[S.area] : null;
  if (!track) return;
  S.musicT += dt;
  while (S.musicT >= track.beat) {
    S.musicT -= track.beat;
    const f = track.notes[S.musicStep % track.notes.length];
    S.musicStep++;
    if (f) audio.note(f, S.mode === "battle" ? "triangle" : "sine");
  }
}

// ---------- starter pick ----------

function starterButtons(S: Game): BBtn[] {
  const out: BBtn[] = STARTERS.map((_, i) => ({ id: `st:${i}`, x: 40 + i * 300, y: 70, w: 280, h: 360, label: "", color: "#000" }));
  out.push({ id: "st:go", x: W / 2 - 150, y: 452, w: 300, h: 60, label: `Choose ${formName(STARTERS[S.starterSel])}!`, color: "#10b981" });
  return out;
}

function drawStarter(ctx: CanvasRenderingContext2D, S: Game) {
  const t = S.t;
  drawTitleScene(ctx, t);
  ctx.fillStyle = "rgba(8,14,24,0.45)";
  ctx.fillRect(0, 0, W, H);
  text(ctx, "Choose your partner!", W / 2, 50, "900 32px system-ui, sans-serif", "#ffffff", "center");
  const blurbs: Record<string, string> = {
    splashlotl: "A gentle healer. Blows bubbles that make friends feel better.",
    emberlotl: "A brave fighter with flame-tipped gills. Never gives up.",
    sproutlotl: "A sharp-eyed ranger. Its leafy gills love sunshine.",
  };
  STARTERS.forEach((id, i) => {
    const sel = S.starterSel === i;
    const x = 40 + i * 300;
    const y = 70;
    panel(ctx, x, y, 280, 360, 20, sel ? "rgba(255,95,162,0.28)" : "rgba(18,30,44,0.85)");
    if (sel) {
      ctx.strokeStyle = "#ff5fa2";
      ctx.lineWidth = 4;
      roundRect(ctx, x, y, 280, 360, 20);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.beginPath();
    ctx.ellipse(x + 140, y + 210, 90, 18, 0, 0, TAU);
    ctx.fill();
    drawLippie(ctx, id, x + 140, y + 210, sel ? 170 : 150, { t, face: sel ? "happy" : "normal" });
    const sp = speciesOf(id);
    text(ctx, sp.name, x + 140, y + 250, "900 24px system-ui, sans-serif", "#ffffff", "center");
    text(ctx, `${ELEMENTS[sp.element].icon} ${ELEMENTS[sp.element].name}   ${CLASSES[sp.cls].icon} ${CLASSES[sp.cls].name}`, x + 140, y + 276, "700 15px system-ui, sans-serif", ELEMENTS[sp.element].color, "center");
    ctx.font = "600 15px system-ui, sans-serif";
    wrapText(ctx, blurbs[id], 240).forEach((ln, k) => text(ctx, ln, x + 140, y + 306 + k * 20, "600 15px system-ui, sans-serif", "#e8f0f6", "center"));
  });
  const b = starterButtons(S)[3];
  drawButton(ctx, S, b, b.label, b.color, "900 20px system-ui, sans-serif");
}

function chooseStarter(S: Game, nickname: string, audio: Audio) {
  const save = S.save;
  if (!save) return;
  const id = STARTERS[S.starterSel];
  const l = newOwned(id, 5);
  l.nickname = nickname.trim().slice(0, NAME_MAX) || l.nickname;
  addLippie(S, l);
  save.flags.starter = true;
  save.flags[`starter_${id}`] = true;
  giveItems(S, { jar: 5, cake: 2 });
  saveCurrent(S);
  audio.confirm();
  enter(S, "world");
  S.npcFace.gran = OPPOSITE[S.p.dir];
  say(S, "Gran Olive", STORY.granGift.map((s) => s.replace("{lippie}", l.nickname)));
}

// ---------- party ----------

const PARTY_CARD = (i: number) => ({ x: 24, y: 64 + i * 104, w: 440, h: 96 });
const POND_COLS = 5;
const POND_ROWS = 2;
const POND_CELL = (i: number) => ({ x: 490 + (i % POND_COLS) * 89, y: 92 + Math.floor(i / POND_COLS) * 112, w: 83, h: 104 });

function partySelected(S: Game) {
  const save = S.save;
  const sel = S.partySel;
  if (!save || !sel) return null;
  const list = sel.kind === "party" ? save.party : save.pond;
  return list[sel.i] ? { ...sel, l: list[sel.i] } : null;
}

function partyButtons(S: Game): BBtn[] {
  const save = S.save;
  if (!save) return [];
  const out: BBtn[] = [{ id: "pt:back", x: W - 134, y: 10, w: 120, h: 44, label: "Back", color: "#6b7280" }];
  save.party.forEach((_, i) => out.push({ id: `pt:sel:party:${i}`, ...PARTY_CARD(i), label: "", color: "" }));
  const pageSize = POND_COLS * POND_ROWS;
  const pages = Math.max(1, Math.ceil(save.pond.length / pageSize));
  S.pondPage = clamp(S.pondPage, 0, pages - 1);
  save.pond.slice(S.pondPage * pageSize, (S.pondPage + 1) * pageSize).forEach((_, k) => {
    out.push({ id: `pt:sel:pond:${S.pondPage * pageSize + k}`, ...POND_CELL(k), label: "", color: "" });
  });
  if (pages > 1) {
    out.push({ id: "pt:page:-1", x: 490, y: 322, w: 60, h: 40, label: "◀", color: "#334155", disabled: S.pondPage === 0 });
    out.push({ id: "pt:page:1", x: 860, y: 322, w: 60, h: 40, label: "▶", color: "#334155", disabled: S.pondPage >= pages - 1 });
  }
  const sel = partySelected(S);
  if (sel) {
    const actions: [string, string, string, boolean][] = [];
    const axo = /lotl$/.test(sel.l.form);
    if (sel.kind === "party") {
      actions.push(["pt:lead", "⭐ Lead", "#f2c230", sel.i === 0]);
      actions.push(["pt:feed", `🍰 Feed (${save.items.cake ?? 0})`, "#f28c38", !(save.items.cake > 0) || sel.l.hp >= ownedMaxHp(sel.l)]);
      actions.push(["pt:topond", "🏞 To pond", "#3f7fd8", save.party.length <= 1]);
    } else actions.push(["pt:toparty", "➕ To team", "#10b981", save.party.length >= PARTY_MAX]);
    if (axo && save.flags.midnight) actions.push(["pt:colors", sel.l.midnight ? "🌸 Normal" : "🌙 Midnight", "#5b3fa6", false]);
    actions.forEach(([id, label, color, disabled], k) => out.push({ id, x: 490 + (k % 2) * 220, y: 404 + Math.floor(k / 2) * 60, w: 210, h: 52, label, color, disabled }));
  }
  return out;
}

function drawHpBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, frac: number) {
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  roundRect(ctx, x, y, w, 9, 4.5);
  ctx.fill();
  ctx.fillStyle = frac > 0.5 ? "#7ee0a0" : frac > 0.2 ? "#ffd166" : "#ff6b6b";
  roundRect(ctx, x, y, Math.max(frac > 0 ? 5 : 0, w * frac), 9, 4.5);
  ctx.fill();
}

function drawParty(ctx: CanvasRenderingContext2D, S: Game) {
  const save = S.save;
  if (!save) return;
  const t = S.t;
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#1e293b");
  bg.addColorStop(1, "#0f172a");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  text(ctx, "Your team", 24, 44, "900 26px system-ui, sans-serif", "#ffffff");
  text(ctx, `🫧 ${save.items.jar ?? 0}   🍰 ${save.items.cake ?? 0}   🍦 ${save.items.cone ?? 0}   🌶️ ${save.items.pepper ?? 0}`, 200, 42, "700 17px system-ui, sans-serif", "#ffe08a");
  const btns = partyButtons(S);
  drawButton(ctx, S, btns[0], "Back", "#6b7280", "800 16px system-ui, sans-serif");
  const sel = S.partySel;
  for (let i = 0; i < PARTY_MAX; i++) {
    const c = PARTY_CARD(i);
    const l = save.party[i];
    const on = sel?.kind === "party" && sel.i === i;
    panel(ctx, c.x, c.y, c.w, c.h, 16, on ? "rgba(255,95,162,0.25)" : l ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.03)");
    if (on) {
      ctx.strokeStyle = "#ff5fa2";
      ctx.lineWidth = 3;
      roundRect(ctx, c.x, c.y, c.w, c.h, 16);
      ctx.stroke();
    }
    if (!l) {
      text(ctx, "Empty spot", c.x + c.w / 2, c.y + 54, "600 15px system-ui, sans-serif", "#64748b", "center");
      continue;
    }
    const sp = speciesOf(l.form);
    drawLippie(ctx, l.form, c.x + 60, c.y + c.h - 10, l.form === "olm" ? 40 : 76, { t, face: l.hp <= 0 ? "sleepy" : "normal", morph: l.midnight ? MORPHS[3] : undefined });
    text(ctx, `${i === 0 ? "⭐ " : ""}${l.nickname}`, c.x + 124, c.y + 30, "800 19px system-ui, sans-serif", "#ffffff");
    text(ctx, `Lv ${l.level}`, c.x + c.w - 16, c.y + 30, "800 16px system-ui, sans-serif", "#ffe08a", "right");
    text(ctx, `${ELEMENTS[sp.element].icon} ${ELEMENTS[sp.element].name}  ${CLASSES[sp.cls].icon} ${CLASSES[sp.cls].name}`, c.x + 124, c.y + 52, "600 13px system-ui, sans-serif", ELEMENTS[sp.element].color);
    const max = ownedMaxHp(l);
    drawHpBar(ctx, c.x + 124, c.y + 62, 220, l.hp / max);
    text(ctx, l.hp <= 0 ? "Sleepy" : `${l.hp}/${max}`, c.x + c.w - 16, c.y + 72, "700 13px system-ui, sans-serif", "#d8e6f0", "right");
    const xpFrac = l.level >= LEVEL_CAP ? 1 : l.xp / xpToNext(l.level);
    ctx.fillStyle = "rgba(127,216,255,0.25)";
    ctx.fillRect(c.x + 124, c.y + 80, 220, 4);
    ctx.fillStyle = "#7fd8ff";
    ctx.fillRect(c.x + 124, c.y + 80, 220 * xpFrac, 4);
  }
  text(ctx, `Gran's pond (${save.pond.length})`, 490, 80, "800 18px system-ui, sans-serif", "#ffffff");
  const pageSize = POND_COLS * POND_ROWS;
  if (!save.pond.length) text(ctx, "Extra Lippies you catch swim here.", 490, 130, "600 15px system-ui, sans-serif", "#94a3b8");
  save.pond.slice(S.pondPage * pageSize, (S.pondPage + 1) * pageSize).forEach((l, k) => {
    const c = POND_CELL(k);
    const idx = S.pondPage * pageSize + k;
    const on = sel?.kind === "pond" && sel.i === idx;
    panel(ctx, c.x, c.y, c.w, c.h, 12, on ? "rgba(255,95,162,0.25)" : "rgba(80,160,220,0.12)");
    if (on) {
      ctx.strokeStyle = "#ff5fa2";
      ctx.lineWidth = 3;
      roundRect(ctx, c.x, c.y, c.w, c.h, 12);
      ctx.stroke();
    }
    drawLippie(ctx, l.form, c.x + c.w / 2, c.y + 72, l.form === "olm" ? 30 : 56, { t, morph: l.midnight ? MORPHS[3] : undefined });
    text(ctx, l.nickname.length > 10 ? `${l.nickname.slice(0, 9)}…` : l.nickname, c.x + c.w / 2, c.y + 90, "700 11px system-ui, sans-serif", "#ffffff", "center");
    text(ctx, `Lv ${l.level}`, c.x + c.w / 2, c.y + 101, "700 10px system-ui, sans-serif", "#ffe08a", "center");
  });
  const selected = partySelected(S);
  if (selected) {
    panel(ctx, 490, 372, 446, 152, 16, "rgba(255,255,255,0.05)");
    text(ctx, speciesOf(selected.l.form).fact, 500, 394, "600 12px system-ui, sans-serif", "#cbd5e1");
  } else text(ctx, "Tap a Lippie to see what you can do.", 490, 420, "600 15px system-ui, sans-serif", "#94a3b8");
  btns.slice(1).forEach((b) => {
    if (!b.label) return;
    ctx.globalAlpha = b.disabled ? 0.38 : 1;
    drawButton(ctx, S, b, b.label, b.color, "800 16px system-ui, sans-serif");
    ctx.globalAlpha = 1;
  });
}

function partyAct(S: Game, id: string, audio: Audio) {
  const save = S.save;
  if (!save) return;
  const [, what, a, b] = id.split(":");
  audio.tap();
  const sel = partySelected(S);
  if (what === "back") {
    S.partySel = null;
    enter(S, "world");
  } else if (what === "sel") S.partySel = { kind: a as "party" | "pond", i: Number(b) };
  else if (what === "page") {
    S.pondPage += Number(a);
    S.partySel = null;
  } else if (!sel) return;
  else if (what === "lead" && sel.kind === "party") {
    save.party.unshift(...save.party.splice(sel.i, 1));
    S.partySel = { kind: "party", i: 0 };
  } else if (what === "feed" && sel.kind === "party" && save.items.cake > 0) {
    const max = ownedMaxHp(sel.l);
    const amount = roll(2, 8, Math.random).reduce((x, y) => x + y, 0) * (save.tamer.background === "healer" ? 1.5 : 1);
    const before = sel.l.hp;
    sel.l.hp = Math.min(max, Math.round(sel.l.hp + amount));
    save.items.cake--;
    S.toast = { text: `${sel.l.nickname} healed ${sel.l.hp - before} HP!`, t0: S.t };
    audio.heal();
  } else if (what === "topond" && sel.kind === "party" && save.party.length > 1) {
    save.pond.push(...save.party.splice(sel.i, 1));
    S.partySel = null;
  } else if (what === "toparty" && sel.kind === "pond" && save.party.length < PARTY_MAX) {
    save.party.push(...save.pond.splice(sel.i, 1));
    S.partySel = { kind: "party", i: save.party.length - 1 };
  } else if (what === "colors") sel.l.midnight = !sel.l.midnight;
  saveCurrent(S);
}

// ---------- journal ----------

const JOURNAL_COLS = 7;
const JOURNAL_CELL = (i: number) => ({ x: 24 + (i % JOURNAL_COLS) * 131, y: 62 + Math.floor(i / JOURNAL_COLS) * 154, w: 123, h: 146 });

function journalButtons(S: Game): BBtn[] {
  const out: BBtn[] = [{ id: "jr:back", x: W - 134, y: 10, w: 120, h: 44, label: "Back", color: "#6b7280" }];
  if (S.journalSel !== null) out.push({ id: "jr:close", x: 0, y: 60, w: W, h: H - 60, label: "", color: "" });
  else SPECIES.forEach((_, i) => out.push({ id: `jr:sel:${i}`, ...JOURNAL_CELL(i), label: "", color: "" }));
  return out;
}

// The form to show for a species: the evolved form once the player owns one.
function journalForm(S: Game, sp: SpeciesDef) {
  const owned = [...(S.save?.party ?? []), ...(S.save?.pond ?? [])];
  return sp.evolved && owned.some((l) => l.form === sp.evolved?.id) ? sp.evolved.id : sp.id;
}

function drawJournal(ctx: CanvasRenderingContext2D, S: Game) {
  const save = S.save;
  if (!save) return;
  const t = S.t;
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#3a2a1e");
  bg.addColorStop(1, "#24180f");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const caught = save.journal.caught.length;
  text(ctx, "📖 Lippie Journal", 24, 44, "900 26px system-ui, sans-serif", "#fff3d6");
  text(ctx, `${caught} / ${TOTAL_LIPPIES} caught · ${save.journal.seen.length} seen`, 290, 42, "700 17px system-ui, sans-serif", "#ffe08a");
  const btns = journalButtons(S);
  drawButton(ctx, S, btns[0], "Back", "#6b7280", "800 16px system-ui, sans-serif");
  if (S.journalSel !== null) {
    const sp = SPECIES[S.journalSel];
    const isCaught = save.journal.caught.includes(sp.id);
    const form = journalForm(S, sp);
    panel(ctx, 24, 62, W - 48, H - 86, 20, "rgba(255,243,214,0.08)");
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.beginPath();
    ctx.ellipse(270, 440, 170, 26, 0, 0, TAU);
    ctx.fill();
    drawLippie(ctx, form, 270, 440, (form === "olm" ? 0.5 : 1) * 280, { t, mono: isCaught ? undefined : "#120c08" });
    const el = ELEMENTS[sp.element];
    text(ctx, sp.evolved && form === sp.evolved.id ? `${sp.name} → ${sp.evolved.name}` : sp.name, 520, 130, "900 34px system-ui, sans-serif", "#ffffff");
    text(ctx, `${el.icon} ${el.name}${el.seaFruit ? " (Sea Fruit)" : ""}${isCaught ? `   ${CLASSES[sp.cls].icon} ${CLASSES[sp.cls].name}` : ""}`, 520, 166, "700 18px system-ui, sans-serif", el.color);
    text(ctx, `Found at: ${sp.where}`, 520, 196, "600 16px system-ui, sans-serif", "#e8dcc4");
    ctx.font = "600 18px system-ui, sans-serif";
    wrapText(ctx, isCaught ? sp.fact : "Catch it to learn more!", 380).forEach((ln, i) => text(ctx, ln, 520, 240 + i * 28, "600 18px system-ui, sans-serif", "#fff3d6"));
    text(ctx, "Tap anywhere to go back", 520, 480, "600 14px system-ui, sans-serif", "#a8987c");
    return;
  }
  SPECIES.forEach((sp, i) => {
    const c = JOURNAL_CELL(i);
    const seen = save.journal.seen.includes(sp.id);
    const isCaught = save.journal.caught.includes(sp.id);
    panel(ctx, c.x, c.y, c.w, c.h, 14, isCaught ? "rgba(255,243,214,0.14)" : "rgba(0,0,0,0.25)");
    text(ctx, `#${i + 1}`, c.x + 10, c.y + 20, "700 12px system-ui, sans-serif", "#a8987c");
    if (isCaught) text(ctx, ELEMENTS[sp.element].icon, c.x + c.w - 14, c.y + 22, "600 14px system-ui, sans-serif", "#ffffff", "right");
    const form = journalForm(S, sp);
    if (seen) drawLippie(ctx, form, c.x + c.w / 2, c.y + c.h - 34, (form === "olm" ? 0.45 : 1) * 86, { t, mono: isCaught ? undefined : "#120c08" });
    else text(ctx, "?", c.x + c.w / 2, c.y + 82, "900 46px system-ui, sans-serif", "rgba(255,243,214,0.18)", "center");
    text(ctx, seen ? sp.name : "???", c.x + c.w / 2, c.y + c.h - 12, "700 13px system-ui, sans-serif", isCaught ? "#fff3d6" : "#a8987c", "center");
  });
}

function journalAct(S: Game, id: string, audio: Audio) {
  const save = S.save;
  if (!save) return;
  const [, what, a] = id.split(":");
  audio.tap();
  if (what === "back") {
    if (S.journalSel !== null) S.journalSel = null;
    else enter(S, "world");
  } else if (what === "close") S.journalSel = null;
  else if (what === "sel") {
    const sp = SPECIES[Number(a)];
    if (save.journal.seen.includes(sp.id)) S.journalSel = Number(a);
  }
}

// ---------- evolution ----------

function drawEvolve(ctx: CanvasRenderingContext2D, S: Game) {
  const e = S.evo;
  if (!e) return;
  const t = e.t;
  const g = ctx.createRadialGradient(W / 2, 260, 20, W / 2, 260, 520);
  g.addColorStop(0, "#3a2f6b");
  g.addColorStop(1, "#0b0a1a");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 40; i++) {
    const a = (i / 40) * TAU + t * 0.4;
    const r = 120 + ((t * 80 + i * 37) % 260);
    circle(ctx, W / 2 + Math.cos(a) * r, 260 + Math.sin(a) * r * 0.7, 2, `rgba(255,240,200,${0.3 + 0.3 * Math.sin(t * 5 + i)})`);
  }
  const reveal = 3.2;
  const morph = S.save?.party[e.slot]?.midnight ? MORPHS[3] : undefined;
  if (t < reveal) {
    // Flicker between the old and new silhouettes, faster and faster.
    const speed = 2 + t * t * 3;
    const showNew = Math.sin(t * speed * Math.PI) > 0 && t > 0.8;
    const glowA = clamp(t / reveal, 0, 1);
    ctx.fillStyle = `rgba(255,255,255,${glowA * 0.3})`;
    ctx.beginPath();
    ctx.arc(W / 2, 250, 140 + glowA * 60, 0, TAU);
    ctx.fill();
    drawLippie(ctx, showNew ? e.to : e.from, W / 2, 380, 230, { t, mono: "#ffffff" });
    text(ctx, `What? ${e.name} is evolving!`, W / 2, 470, "800 26px system-ui, sans-serif", "#ffffff", "center");
  } else {
    const flash = clamp(1 - (t - reveal) / 0.6, 0, 1);
    drawLippie(ctx, e.to, W / 2, 380, 250, { t, face: "happy", morph });
    if (flash > 0) {
      ctx.fillStyle = `rgba(255,255,255,${flash})`;
      ctx.fillRect(0, 0, W, H);
    }
    text(ctx, `${e.name} evolved into ${formName(e.to)}!`, W / 2, 460, "900 30px system-ui, sans-serif", "#ffe08a", "center");
    if (t > reveal + 1) {
      ctx.globalAlpha = 0.65 + 0.35 * Math.sin(t * 3);
      text(ctx, "Tap to continue", W / 2, 500, "700 16px system-ui, sans-serif", "#ffffff", "center");
      ctx.globalAlpha = 1;
    }
  }
}

// ---------- ending ----------

const CREDITS = [
  "LIPPIES",
  "",
  "The Moonstone shines again!",
  "",
  "Starring",
  "{name}",
  "and their Lippies",
  "",
  "Made with love on axolotl.games",
  "",
  "Thanks for playing!",
  "Keep exploring to fill your Lippie Journal.",
];

function drawEnding(ctx: CanvasRenderingContext2D, S: Game) {
  const save = S.save;
  const e = S.ending;
  if (!save || !e) return;
  const t = e.t;
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#0b1230");
  g.addColorStop(1, "#24345a");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 60; i++) circle(ctx, hash1(i, 3) * W, hash1(i, 5) * 300, 1 + hash1(i, 7), `rgba(255,255,255,${0.4 + 0.4 * Math.sin(t * 2 + i)})`);
  const mg = ctx.createRadialGradient(780, 110, 0, 780, 110, 140);
  mg.addColorStop(0, "rgba(220,235,255,0.9)");
  mg.addColorStop(0.3, "rgba(200,220,255,0.35)");
  mg.addColorStop(1, "rgba(200,220,255,0)");
  ctx.fillStyle = mg;
  ctx.fillRect(640, 0, 300, 280);
  circle(ctx, 780, 110, 44, "#eef4ff");
  ctx.fillStyle = "#1c2a4a";
  ctx.fillRect(0, 430, W, 110);
  const all = [...save.party, ...save.pond];
  all.forEach((l, i) => {
    const x = ((t * 60 + i * 140) % (W + 280)) - 140;
    drawLippie(ctx, l.form, x, 470, l.form === "olm" ? 50 : 90, { t: t + i, face: "happy", flip: true, morph: l.midnight ? MORPHS[3] : undefined });
  });
  drawTamer(ctx, save.tamer, ((t * 60 - 140) % (W + 280)) - 140, 476, 70, "right", t * 9, t);
  CREDITS.forEach((ln, i) => {
    const y = 470 - t * 38 + i * 40;
    if (y < -20 || y > 440) return;
    const s = ln.replace("{name}", save.tamer.name);
    text(ctx, s, W / 2, y, i === 0 ? "900 44px system-ui, sans-serif" : "700 22px system-ui, sans-serif", i === 0 ? "#ffd6ea" : "#ffffff", "center");
  });
  if (t > 6) {
    ctx.globalAlpha = 0.65 + 0.35 * Math.sin(t * 3);
    text(ctx, "Tap to keep exploring", W / 2, 420, "700 16px system-ui, sans-serif", "#ffe08a", "center");
    ctx.globalAlpha = 1;
  }
}

// ---------- gallery (dev only) ----------

const GAL_COLS = 8;
const GAL_FACES: Face[] = ["normal", "happy", "ouch", "sleepy"];
const galCell = (i: number) => ({ x: 24 + (i % GAL_COLS) * 115, y: 64 + Math.floor(i / GAL_COLS) * 158, w: 107, h: 150 });
// The Elder Olm is long and low, so it is drawn smaller to fit the same frame.
const galScale = (id: string) => (id === "olm" ? 0.45 : 1);

function drawGallery(ctx: CanvasRenderingContext2D, S: Game) {
  const g = S.gal;
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#1e293b");
  bg.addColorStop(1, "#0f172a");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  text(ctx, "Lippie gallery (dev)", 24, 38, "800 22px system-ui, sans-serif", "#ffffff");
  text(ctx, `Face: ${g.face}  ·  1-4 faces · F flip · M silhouette · N Midnight · ←→ pick · Enter zoom`, 270, 37, "600 13px system-ui, sans-serif", "#cbd5e1");
  drawButton(ctx, S, buttons(S)[0], "Back", "#6b7280", "800 16px system-ui, sans-serif");
  const opts = (id: string) => ({
    t: S.t,
    face: g.face,
    flip: g.flip,
    mono: g.mono ? "#0b1220" : undefined,
    morph: g.midnight && /lotl$/.test(id) ? MORPHS[3] : undefined,
  });
  if (g.big) {
    const f = FORMS[g.sel];
    const sp = f.species;
    panel(ctx, 20, 64, W - 40, H - 84, 18, "rgba(255,255,255,0.06)");
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.beginPath();
    ctx.ellipse(330, 470, 170, 26, 0, 0, TAU);
    ctx.fill();
    drawLippie(ctx, f.id, 330, 470, 300 * galScale(f.id), opts(f.id));
    const el = ELEMENTS[sp.element];
    const cl = CLASSES[sp.cls];
    text(ctx, f.name, 620, 150, "900 40px system-ui, sans-serif", "#ffffff");
    text(ctx, `${el.icon} ${el.name}${el.seaFruit ? " (Sea Fruit)" : ""}   ${cl.icon} ${cl.name}`, 620, 188, "700 18px system-ui, sans-serif", el.color);
    text(ctx, `${sp.where} · Lv ${sp.levels[0]}${sp.levels[1] !== sp.levels[0] ? `-${sp.levels[1]}` : ""}`, 620, 218, "600 15px system-ui, sans-serif", "#cbd5e1");
    ctx.font = "600 17px system-ui, sans-serif";
    wrapText(ctx, sp.fact, 300).forEach((ln, i) => text(ctx, ln, 620, 262 + i * 26, "600 17px system-ui, sans-serif", "#e8f0f6"));
    text(ctx, `${g.sel + 1} / ${FORMS.length}`, 620, 470, "700 14px system-ui, sans-serif", "#94a3b8");
    return;
  }
  FORMS.forEach((f, i) => {
    const c = galCell(i);
    panel(ctx, c.x, c.y, c.w, c.h, 14, i === g.sel ? "rgba(255,95,162,0.25)" : "rgba(255,255,255,0.06)");
    if (i === g.sel) {
      ctx.strokeStyle = "#ff5fa2";
      ctx.lineWidth = 3;
      roundRect(ctx, c.x, c.y, c.w, c.h, 14);
      ctx.stroke();
    }
    drawLippie(ctx, f.id, c.x + c.w / 2, c.y + c.h - 34, 92 * galScale(f.id), opts(f.id));
    text(ctx, f.name, c.x + c.w / 2, c.y + c.h - 12, "700 12px system-ui, sans-serif", "#ffffff", "center");
    text(ctx, ELEMENTS[f.species.element].icon, c.x + 16, c.y + 22, "600 14px system-ui, sans-serif", "#ffffff", "center");
  });
}

// ---------- render ----------

function render(ctx: CanvasRenderingContext2D, S: Game, dpr: number) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  if (S.mode === "world") drawWorld(ctx, S);
  else if (S.mode === "title") drawTitle(ctx, S);
  else if (S.mode === "gallery") drawGallery(ctx, S);
  else if (S.mode === "battle") drawBattle(ctx, S);
  else if (S.mode === "starter") drawStarter(ctx, S);
  else if (S.mode === "party") drawParty(ctx, S);
  else if (S.mode === "journal") drawJournal(ctx, S);
  else if (S.mode === "evolve") drawEvolve(ctx, S);
  else if (S.mode === "ending") drawEnding(ctx, S);
  else {
    drawTitleScene(ctx, S.t);
    ctx.fillStyle = "rgba(8,14,24,0.35)";
    ctx.fillRect(0, 0, W, H);
  }
  if (S.fade > 0) {
    ctx.fillStyle = `rgba(8,14,24,${S.fade})`;
    ctx.fillRect(0, 0, W, H);
  }
}

// ---------- DOM overlays ----------

type Modal = { kind: "export" | "import" | "delete"; slot: number };

const btn: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 16,
  border: "none",
  background: "#334155",
  color: "#f1f5f9",
  cursor: "pointer",
  fontSize: 16,
  fontWeight: 600,
  touchAction: "manipulation",
};

const overlay: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "rgba(8,14,24,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const card: React.CSSProperties = {
  background: "rgba(18,30,44,0.97)",
  borderRadius: 18,
  padding: 22,
  width: "min(100%, 520px)",
  maxHeight: "100%",
  overflowY: "auto",
  boxSizing: "border-box",
  boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const field: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  borderRadius: 12,
  border: "2px solid #334155",
  background: "#0f172a",
  color: "#f1f5f9",
  padding: "10px 12px",
  fontSize: 16,
  userSelect: "text",
  WebkitUserSelect: "text",
};

const row: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" };

function ExportPanel({ save, slot, onClose }: { save: SaveData; slot: number; onClose: () => void }) {
  const code = encodeSave(save);
  const [copied, setCopied] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      areaRef.current?.select();
    }
  };
  const download = () => {
    const blob = new Blob([JSON.stringify(save, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lippies-${save.tamer.name.replace(/[^\w-]+/g, "_") || "save"}-slot${slot + 1}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div style={card}>
      <h2 style={{ margin: 0, fontSize: 22 }}>Back up {save.tamer.name}'s adventure</h2>
      <p style={{ margin: 0, color: "#cbd5e1", fontSize: 15 }}>
        Download a save file, or copy this code somewhere safe. Use Import on an empty slot to load it again, even on a different tablet.
      </p>
      <textarea ref={areaRef} readOnly value={code} rows={4} style={{ ...field, fontFamily: "ui-monospace, monospace", fontSize: 12, resize: "none" }} onFocus={(e) => e.currentTarget.select()} />
      <div style={row}>
        <button style={btn} onClick={copy}>
          {copied ? "Copied!" : "Copy code"}
        </button>
        <button style={btn} onClick={download}>
          Download file
        </button>
        <button style={{ ...btn, background: "#10b981" }} onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

function ImportPanel({ onImport, onClose }: { onImport: (d: SaveData) => void; onClose: () => void }) {
  const [raw, setRaw] = useState("");
  const [error, setError] = useState("");
  const tryLoad = (s: string) => {
    const d = decodeSave(s);
    if (d) onImport(d);
    else setError("That didn't work. Make sure you pasted the whole code or picked a Lippies save file.");
  };
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    f.text().then(tryLoad, () => setError("Couldn't read that file."));
    e.target.value = "";
  };
  return (
    <div style={card}>
      <h2 style={{ margin: 0, fontSize: 22 }}>Import an adventure</h2>
      <p style={{ margin: 0, color: "#cbd5e1", fontSize: 15 }}>Paste a save code, or choose a save file you downloaded.</p>
      <textarea
        value={raw}
        onChange={(e) => {
          setRaw(e.target.value);
          setError("");
        }}
        placeholder="LIPPIES1:…"
        rows={4}
        style={{ ...field, fontFamily: "ui-monospace, monospace", fontSize: 12, resize: "none" }}
      />
      {error && <p style={{ margin: 0, color: "#ff8a8a", fontSize: 14 }}>{error}</p>}
      <div style={row}>
        <label style={{ ...btn, display: "inline-block" }}>
          Choose file
          <input type="file" accept=".json,application/json,text/plain" onChange={onFile} style={{ display: "none" }} />
        </label>
        <button style={btn} onClick={onClose}>
          Cancel
        </button>
        <button style={{ ...btn, background: "#10b981", opacity: raw.trim() ? 1 : 0.5 }} disabled={!raw.trim()} onClick={() => tryLoad(raw)}>
          Load code
        </button>
      </div>
    </div>
  );
}

function DeletePanel({ save, onDelete, onClose }: { save: SaveData; onDelete: () => void; onClose: () => void }) {
  return (
    <div style={card}>
      <h2 style={{ margin: 0, fontSize: 22 }}>Delete {save.tamer.name}'s adventure?</h2>
      <p style={{ margin: 0, color: "#cbd5e1", fontSize: 15 }}>
        This erases {save.journal.caught.length} caught Lippies and {formatTime(save.playTime)} of play. It can't be undone unless you
        made a backup.
      </p>
      <div style={row}>
        <button style={{ ...btn, background: "#10b981" }} onClick={onClose}>
          Keep it
        </button>
        <button style={{ ...btn, background: "#dc2626" }} onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
}

// ---------- character creator ----------

const pick = <T,>(arr: readonly T[]) => arr[Math.floor(Math.random() * arr.length)];

function TamerThumb({ look, size }: { look: TamerLook; size: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    c.width = size * dpr;
    c.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    drawPortrait(ctx, look, size / 2, size / 2, size / 2 - 2, 1);
  }, [look, size]);
  return <canvas ref={ref} style={{ width: size, height: size, display: "block" }} />;
}

const PREVIEW_W = 300;
const PREVIEW_H = 330;

function TamerPreview({ look }: { look: TamerLook }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const lookRef = useRef(look);
  lookRef.current = look;
  const [dirIdx, setDirIdx] = useState(0);
  const [walking, setWalking] = useState(false);
  const viewRef = useRef({ dirIdx, walking });
  viewRef.current = { dirIdx, walking };

  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    c.width = PREVIEW_W * dpr;
    c.height = PREVIEW_H * dpr;
    let raf = 0;
    const t0 = performance.now();
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const t = (now - t0) / 1000;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const sky = ctx.createLinearGradient(0, 0, 0, PREVIEW_H);
      sky.addColorStop(0, "#9fd3f7");
      sky.addColorStop(0.62, "#e4f4ff");
      sky.addColorStop(0.63, "#7cc57a");
      sky.addColorStop(1, "#4f9d56");
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H);
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.beginPath();
      ctx.ellipse(PREVIEW_W / 2, PREVIEW_H - 46, 90, 18, 0, 0, TAU);
      ctx.fill();
      const v = viewRef.current;
      drawTamer(ctx, lookRef.current, PREVIEW_W / 2, PREVIEW_H - 44, 210, DIRS[v.dirIdx], v.walking ? t * 9 : 0, t);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  const small: React.CSSProperties = { ...btn, minWidth: 52, padding: "10px 14px" };
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "100%" }}>
      <canvas ref={ref} style={{ width: "100%", maxWidth: PREVIEW_W, aspectRatio: `${PREVIEW_W} / ${PREVIEW_H}`, borderRadius: 14, display: "block" }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button style={small} onClick={() => setDirIdx((d) => (d + 3) % 4)} aria-label="Turn left">
          ↺
        </button>
        <button style={{ ...small, background: walking ? "#ff5fa2" : "#334155" }} onClick={() => setWalking((w) => !w)}>
          {walking ? "Walking" : "Walk"}
        </button>
        <button style={small} onClick={() => setDirIdx((d) => (d + 1) % 4)} aria-label="Turn right">
          ↻
        </button>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#cbd5e1" }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{children}</div>
    </div>
  );
}

const swatch = (color: string, active: boolean): React.CSSProperties => ({
  width: 44,
  height: 44,
  borderRadius: 22,
  padding: 0,
  background: color,
  border: `3px solid ${active ? "#ff5fa2" : "rgba(255,255,255,0.18)"}`,
  boxShadow: active ? "0 0 0 2px #0f172a inset" : "none",
  cursor: "pointer",
  touchAction: "manipulation",
});

const tile = (active: boolean): React.CSSProperties => ({
  width: 76,
  padding: "6px 4px",
  borderRadius: 14,
  border: `3px solid ${active ? "#ff5fa2" : "transparent"}`,
  background: "#1e293b",
  color: "#f1f5f9",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  fontWeight: 600,
  touchAction: "manipulation",
});

function CreatorPanel({ onStart, onCancel, onTap }: { onStart: (look: TamerLook) => void; onCancel: () => void; onTap: () => void }) {
  const [look, setLook] = useState<TamerLook>(() => ({ ...DEFAULT_TAMER, name: pick(TAMER_NAMES) }));
  const [tab, setTab] = useState<"look" | "background">("look");
  const set = <K extends keyof TamerLook>(k: K, v: TamerLook[K]) => {
    onTap();
    setLook((l) => ({ ...l, [k]: v }));
  };
  const surprise = () => {
    onTap();
    setLook((l) => ({
      ...l,
      skin: pick(SKIN_TONES),
      hairStyle: pick(HAIR_STYLES).id,
      hairColor: pick(HAIR_COLORS),
      outfit: pick(OUTFIT_COLORS),
      hat: pick(HATS).id,
    }));
  };
  const start = () => onStart({ ...look, name: look.name.trim() || pick(TAMER_NAMES) });
  const bg = BACKGROUNDS.find((b) => b.id === look.background) ?? BACKGROUNDS[0];
  const tabBtn = (id: "look" | "background", label: string) => (
    <button
      style={{ ...btn, flex: 1, background: tab === id ? "#ff5fa2" : "#1e293b" }}
      onClick={() => {
        onTap();
        setTab(id);
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, width: "100%", maxWidth: 1120, margin: "auto", alignItems: "stretch" }}>
      <div style={{ ...card, width: "auto", flex: "1 1 400px", maxHeight: "none" }}>
        <h2 style={{ margin: 0, fontSize: 24 }}>Create your Tamer</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={look.name}
            maxLength={NAME_MAX}
            placeholder="Your name"
            aria-label="Name"
            onChange={(e) => setLook((l) => ({ ...l, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            style={{ ...field, fontSize: 18, fontWeight: 700 }}
          />
          <button style={{ ...btn, fontSize: 20, padding: "8px 14px" }} onClick={() => set("name", pick(TAMER_NAMES))} aria-label="Random name">
            🎲
          </button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {tabBtn("look", "Look")}
          {tabBtn("background", `Background: ${bg.icon} ${bg.name}`)}
        </div>

        {tab === "look" ? (
          <>
            <Section label="Skin">
              {SKIN_TONES.map((c) => (
                <button key={c} style={swatch(c, look.skin === c)} onClick={() => set("skin", c)} aria-label={`Skin tone ${c}`} />
              ))}
            </Section>
            <Section label="Hair">
              {HAIR_STYLES.map((h) => (
                <button key={h.id} style={tile(look.hairStyle === h.id)} onClick={() => set("hairStyle", h.id)}>
                  <TamerThumb look={{ ...look, hairStyle: h.id, hat: "none" }} size={52} />
                  {h.name}
                </button>
              ))}
            </Section>
            <Section label="Hair color">
              {HAIR_COLORS.map((c) => (
                <button key={c} style={swatch(c, look.hairColor === c)} onClick={() => set("hairColor", c)} aria-label={`Hair color ${c}`} />
              ))}
            </Section>
            <Section label="Outfit">
              {OUTFIT_COLORS.map((c) => (
                <button key={c} style={swatch(c, look.outfit === c)} onClick={() => set("outfit", c)} aria-label={`Outfit color ${c}`} />
              ))}
            </Section>
            <Section label="Hat">
              {HATS.map((h) => (
                <button key={h.id} style={tile(look.hat === h.id)} onClick={() => set("hat", h.id)}>
                  <TamerThumb look={{ ...look, hat: h.id }} size={52} />
                  {h.name}
                </button>
              ))}
            </Section>
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <p style={{ margin: 0, color: "#cbd5e1", fontSize: 15 }}>Your background gives you a small bonus. Every choice is a good one!</p>
            {BACKGROUNDS.map((b) => (
              <button
                key={b.id}
                onClick={() => set("background", b.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  textAlign: "left",
                  padding: "10px 14px",
                  borderRadius: 14,
                  border: `3px solid ${look.background === b.id ? "#ff5fa2" : "transparent"}`,
                  background: "#1e293b",
                  color: "#f1f5f9",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                <span style={{ fontSize: 30 }}>{b.icon}</span>
                <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 17, fontWeight: 800 }}>{b.name}</span>
                  <span style={{ fontSize: 14, color: "#cbd5e1" }}>{b.perk}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ ...card, width: "auto", flex: "1 1 280px", maxWidth: 380, maxHeight: "none", alignItems: "center" }}>
        <TamerPreview look={look} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{look.name.trim() || "Your name"}</div>
          <div style={{ fontSize: 15, color: "#cbd5e1" }}>
            {bg.icon} {bg.name} · {bg.perk}
          </div>
        </div>
        <button style={{ ...btn, width: "100%" }} onClick={surprise}>
          🎲 Surprise me
        </button>
        <button style={{ ...btn, width: "100%", background: "#10b981", fontSize: 19, fontWeight: 800, padding: "14px 18px" }} onClick={start}>
          Start adventure ▶
        </button>
        <button style={{ ...btn, width: "100%", background: "#6b7280" }} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function NamePanel({ initial, onDone, onBack }: { initial: string; onDone: (name: string) => void; onBack: () => void }) {
  const [name, setName] = useState(initial);
  return (
    <div style={card}>
      <h2 style={{ margin: 0, fontSize: 24 }}>Give your partner a name!</h2>
      <p style={{ margin: 0, color: "#cbd5e1", fontSize: 15 }}>Keep {initial}, or make up your own name.</p>
      <input
        autoFocus
        value={name}
        maxLength={NAME_MAX}
        aria-label="Lippie name"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onDone(name)}
        style={{ ...field, fontSize: 20, fontWeight: 700 }}
      />
      <div style={row}>
        <button style={btn} onClick={onBack}>
          Back
        </button>
        <button style={{ ...btn, background: "#10b981", fontSize: 18, fontWeight: 800 }} onClick={() => onDone(name)}>
          That's the one!
        </button>
      </div>
    </div>
  );
}

// ---------- component ----------

export default function AxolotlLippies({ onBack }: { onBack: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const gameRef = useRef<Game>(newGame());
  const audioRef = useRef<Audio>(makeAudio());
  const [mode, setMode] = useState<Mode>("title");
  const [muted, setMuted] = useState(false);
  const [modal, setModal] = useState<Modal | null>(null);
  const modalRef = useRef<Modal | null>(null);
  modalRef.current = modal;
  const [naming, setNaming] = useState(false);
  const namingRef = useRef(false);
  namingRef.current = naming;
  const actRef = useRef<(id: string) => void>(() => {});

  actRef.current = (id: string) => {
    const S = gameRef.current;
    const audio = audioRef.current;
    if (id.startsWith("bt:")) {
      battleAct(S, id, audio);
      return;
    }
    if (id.startsWith("pt:")) {
      partyAct(S, id, audio);
      return;
    }
    if (id.startsWith("jr:")) {
      journalAct(S, id, audio);
      return;
    }
    if (id === "st:go") {
      audio.confirm();
      setNaming(true);
      return;
    }
    if (id.startsWith("st:")) {
      audio.tap();
      S.starterSel = Number(id.split(":")[1]);
      return;
    }
    if (id === "ev:next") {
      S.evo = null;
      enter(S, "world");
      resumeWorld(S, audio);
      return;
    }
    if (id === "end:next") {
      S.ending = null;
      if (S.save) S.save.flags.endingSeen = true;
      saveCurrent(S);
      enter(S, "world");
      resumeWorld(S, audio);
      return;
    }
    if (id === "open:party" || id === "open:journal") {
      if (S.mode !== "world" || S.dialog || S.transit) return;
      audio.tap();
      S.partySel = null;
      S.journalSel = null;
      enter(S, id === "open:party" ? "party" : "journal");
      return;
    }
    const [kind, n] = id.split(":");
    const i = Number(n);
    if (kind === "new") {
      audio.confirm();
      S.slot = i;
      S.sel = i;
      enter(S, "create");
    } else if (kind === "play") {
      const d = S.slots[i];
      if (!d) return;
      audio.confirm();
      S.slot = i;
      S.sel = i;
      enterWorld(S, d);
    } else if (kind === "export" || kind === "import" || kind === "delete") {
      audio.tap();
      S.sel = i;
      setModal({ kind, slot: i });
    } else if (kind === "tip") {
      audio.tap();
      S.tip = false;
      try {
        localStorage.setItem(TIP_KEY, "1");
      } catch {
        // tip just shows again next time
      }
    } else if (kind === "galBack") {
      audio.back();
      enter(S, "title");
    } else if (kind === "gal") {
      audio.tap();
      if (S.gal.sel === i) S.gal.big = !S.gal.big;
      S.gal.sel = i;
    } else if (kind === "quit") {
      audio.back();
      savePos(S);
      saveCurrent(S);
      S.save = null;
      S.slot = -1;
      S.dialog = null;
      S.held = [];
      S.pad = null;
      enter(S, "title");
    }
  };

  const startNew = (look: TamerLook) => {
    const S = gameRef.current;
    const d = newSave(look);
    S.save = d;
    saveCurrent(S);
    audioRef.current.confirm();
    enterWorld(S, d);
  };

  const closeModal = () => {
    audioRef.current.back();
    setModal(null);
  };

  const leave = () => {
    saveCurrent(gameRef.current);
    onBack();
  };

  useEffect(() => {
    audioRef.current.setMuted(muted);
  }, [muted]);

  useEffect(() => {
    const cvs = canvasRef.current;
    const ctx = cvs?.getContext("2d");
    if (!cvs || !ctx) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    cvs.width = Math.floor(W * dpr);
    cvs.height = Math.floor(H * dpr);
    const audio = audioRef.current;
    let pressPointer = -1;
    // Dev-only handle for poking at game state from the console while testing.
    if (import.meta.env.DEV) {
      const w = window as unknown as { __lippies?: () => Game; __lippiesArt?: typeof drawLippie };
      w.__lippies = () => gameRef.current;
      w.__lippiesArt = drawLippie;
      (w as unknown as Record<string, unknown>).__lippiesDraw = { drawTamer, drawTitleScene, drawD20, drawJar };
      (w as unknown as { __lippiesBattle?: (form: string, level: number) => void }).__lippiesBattle = (form, level) =>
        startBattle(gameRef.current, audioRef.current, { kind: "wild", team: [[form, level]] });
    }

    const toLogical = (e: PointerEvent) => {
      const r = cvs.getBoundingClientRect();
      return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
    };
    const blocked = () => modalRef.current !== null || namingRef.current || gameRef.current.mode === "create";

    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault();
      audio.unlock();
      const S = gameRef.current;
      if (blocked()) return;
      S.kbd = false;
      if (e.pointerType !== "mouse") S.touch = true;
      const p = toLogical(e);
      if (S.mode === "world") {
        if (S.dialog) {
          interact(S, audio);
          return;
        }
        if (S.touch) {
          if (Math.hypot(p.x - A_BTN.x, p.y - A_BTN.y) <= A_BTN.r + 10) {
            S.aDown = S.t;
            interact(S, audio);
            return;
          }
          if (Math.hypot(p.x - PAD.x, p.y - PAD.y) <= PAD.r + 12) {
            S.pad = padDir(p.x, p.y);
            S.padId = e.pointerId;
            return;
          }
        }
      }
      if (S.mode === "battle" && S.bv && ["intro", "play", "done"].includes(S.bv.phase) && !hitTest(S, p.x, p.y)) {
        skipBattle(S);
        return;
      }
      if (pressPointer !== -1) return;
      const b = hitTest(S, p.x, p.y);
      if (!b) {
        if (S.mode === "world") tapWorld(S, p.x, p.y, audio);
        return;
      }
      S.press = b.id;
      pressPointer = e.pointerId;
    };
    const onPointerMove = (e: PointerEvent) => {
      const S = gameRef.current;
      if (e.pointerId !== S.padId) return;
      const p = toLogical(e);
      S.pad = padDir(p.x, p.y);
    };
    const onPointerUp = (e: PointerEvent) => {
      const G = gameRef.current;
      if (e.pointerId === G.padId) {
        G.pad = null;
        G.padId = -1;
      }
      if (e.pointerId !== pressPointer) return;
      pressPointer = -1;
      const S = gameRef.current;
      const id = S.press;
      S.press = null;
      if (!id || blocked()) return;
      const p = toLogical(e);
      if (hitTest(S, p.x, p.y)?.id === id) actRef.current(id);
    };
    const onPointerCancel = () => {
      pressPointer = -1;
      const S = gameRef.current;
      S.press = null;
      S.pad = null;
      S.padId = -1;
      S.held = [];
    };
    const keyDir: Record<string, Dir> = {
      arrowup: "up",
      w: "up",
      arrowdown: "down",
      s: "down",
      arrowleft: "left",
      a: "left",
      arrowright: "right",
      d: "right",
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (blocked() || target?.closest("input, textarea, button")) return;
      const S = gameRef.current;
      const k = e.key.toLowerCase();
      audio.unlock();
      if (S.mode === "gallery") {
        const g = S.gal;
        if (k === "arrowleft" || k === "arrowright") g.sel = (g.sel + (k === "arrowleft" ? -1 : 1) + FORMS.length) % FORMS.length;
        else if (k === "arrowup" || k === "arrowdown") g.sel = clamp(g.sel + (k === "arrowup" ? -GAL_COLS : GAL_COLS), 0, FORMS.length - 1);
        else if (k >= "1" && k <= "4") g.face = GAL_FACES[Number(k) - 1];
        else if (k === "f") g.flip = !g.flip;
        else if (k === "m") g.mono = !g.mono;
        else if (k === "n") g.midnight = !g.midnight;
        else if (k === "enter" || k === " ") g.big = !g.big;
        else if (k === "escape") actRef.current("galBack");
        else return;
        e.preventDefault();
        return;
      }
      if (S.mode === "title") {
        if (import.meta.env.DEV && k === "g") {
          enter(S, "gallery");
          return;
        }
        if (k === "arrowleft" || k === "a" || k === "arrowright" || k === "d") {
          e.preventDefault();
          const dir = k === "arrowleft" || k === "a" ? -1 : 1;
          if (S.kbd) S.sel = (S.sel + dir + SLOTS) % SLOTS;
          S.kbd = true;
          audio.tap();
        } else if (k === "enter" || k === " ") {
          e.preventDefault();
          if (!e.repeat) actRef.current(`${S.slots[S.sel] ? "play" : "new"}:${S.sel}`);
        }
      } else if (S.mode === "battle" && S.bv) {
        const bv = S.bv;
        const list = battleButtons(S).slice(1).filter((b) => !b.disabled);
        const busy = bv.phase === "intro" || bv.phase === "play" || bv.phase === "done";
        if (k === " " || k === "enter" || k === "z") {
          e.preventDefault();
          if (e.repeat) return;
          if (busy) skipBattle(S);
          else if (list[bv.focus]) actRef.current(list[bv.focus].id);
        } else if (k.startsWith("arrow") && !busy && list.length) {
          e.preventDefault();
          S.kbd = true;
          const d = k === "arrowleft" || k === "arrowup" ? -1 : 1;
          bv.focus = (bv.focus + d + list.length) % list.length;
        } else if ((k === "escape" || k === "backspace" || k === "x") && ["moves", "snack", "swap"].includes(bv.phase)) {
          e.preventDefault();
          actRef.current("bt:back");
        }
      } else if (S.mode === "starter") {
        if (k === "arrowleft" || k === "a" || k === "arrowright" || k === "d") {
          e.preventDefault();
          S.starterSel = (S.starterSel + (k === "arrowleft" || k === "a" ? 2 : 1)) % 3;
          audio.tap();
        } else if ((k === " " || k === "enter") && !e.repeat) {
          e.preventDefault();
          actRef.current("st:go");
        }
      } else if (S.mode === "party" || S.mode === "journal") {
        if (k === "escape" || k === "backspace" || (S.mode === "party" ? k === "p" : k === "j")) {
          e.preventDefault();
          actRef.current(S.mode === "party" ? "pt:back" : "jr:back");
        }
      } else if (S.mode === "evolve" || S.mode === "ending") {
        if ((k === " " || k === "enter" || k === "escape") && !e.repeat) {
          e.preventDefault();
          const b = buttons(S)[0];
          if (b) actRef.current(b.id);
        }
      } else if (S.mode === "world") {
        const dir = keyDir[k];
        if (k === "p" || k === "j") {
          e.preventDefault();
          actRef.current(k === "p" ? "open:party" : "open:journal");
          return;
        }
        if (dir) {
          e.preventDefault();
          if (!S.held.includes(dir)) S.held.push(dir);
        } else if (k === " " || k === "enter" || k === "z") {
          e.preventDefault();
          if (!e.repeat) interact(S, audio);
        } else if (k === "escape" && !S.dialog) {
          e.preventDefault();
          actRef.current("quit");
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const dir = keyDir[e.key.toLowerCase()];
      const S = gameRef.current;
      if (dir) S.held = S.held.filter((h) => h !== dir);
    };
    const onVisibility = () => {
      if (!document.hidden) return;
      const S = gameRef.current;
      if (S.mode === "world") savePos(S);
      saveCurrent(S);
    };

    cvs.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("blur", onPointerCancel);
    document.addEventListener("visibilitychange", onVisibility);

    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let lastMode: Mode = gameRef.current.mode;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
      last = now;
      acc += dt;
      while (acc >= STEP) {
        step(gameRef.current, STEP, audio);
        acc -= STEP;
      }
      const S = gameRef.current;
      render(ctx, S, dpr);
      if (S.mode !== lastMode) {
        lastMode = S.mode;
        setMode(S.mode);
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      cvs.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("blur", onPointerCancel);
      document.removeEventListener("visibilitychange", onVisibility);
      saveCurrent(gameRef.current);
    };
  }, []);

  const S = gameRef.current;
  const modalSave = modal ? S.slots[modal.slot] : null;

  return (
    <div
      style={{
        minHeight: "100dvh",
        width: "100%",
        background: "#0f172a",
        color: "#f1f5f9",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 12,
        gap: 10,
        boxSizing: "border-box",
        fontFamily: "system-ui, sans-serif",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    >
      <div style={{ width: "100%", maxWidth: 1280, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Lippies</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {mode === "world" && !!gameRef.current.save?.flags.starter && (
            <>
              <button style={{ ...btn, background: "#ff5fa2" }} onClick={() => actRef.current("open:party")}>
                🐾 Team
              </button>
              <button style={{ ...btn, background: "#8a5a32" }} onClick={() => actRef.current("open:journal")}>
                📖 Journal
              </button>
            </>
          )}
          <button style={btn} onClick={() => setMuted((v) => !v)}>
            {muted ? "Sound off" : "Sound on"}
          </button>
          <button style={{ ...btn, background: "#6b7280" }} onClick={leave}>
            Back to Home
          </button>
        </div>
      </div>
      <div
        style={{
          position: "relative",
          width: "min(100%, 1280px, calc((100dvh - 120px) * 16 / 9))",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
        }}
      >
        <canvas ref={canvasRef} style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }} />
        {mode === "create" && (
          <div
            style={{ ...overlay, alignItems: "flex-start", overflowY: "auto", background: "rgba(8,14,24,0.45)" }}
            onKeyDown={(e) => e.key === "Escape" && actRef.current("quit")}
          >
            <CreatorPanel onStart={startNew} onCancel={() => actRef.current("quit")} onTap={() => audioRef.current.tap()} />
          </div>
        )}
        {naming && (
          <div style={overlay}>
            <NamePanel
              initial={formName(STARTERS[gameRef.current.starterSel])}
              onDone={(name) => {
                setNaming(false);
                chooseStarter(gameRef.current, name, audioRef.current);
              }}
              onBack={() => setNaming(false)}
            />
          </div>
        )}
        {modal && (
          <div style={overlay} onPointerDown={(e) => e.target === e.currentTarget && closeModal()}>
            {modal.kind === "export" && modalSave && <ExportPanel save={modalSave} slot={modal.slot} onClose={closeModal} />}
            {modal.kind === "delete" && modalSave && (
              <DeletePanel
                save={modalSave}
                onClose={closeModal}
                onDelete={() => {
                  deleteSlot(modal.slot);
                  S.slots[modal.slot] = null;
                  audioRef.current.back();
                  setModal(null);
                }}
              />
            )}
            {modal.kind === "import" && (
              <ImportPanel
                onClose={closeModal}
                onImport={(d) => {
                  writeSlot(modal.slot, d);
                  S.slots[modal.slot] = d;
                  audioRef.current.confirm();
                  setModal(null);
                }}
              />
            )}
          </div>
        )}
      </div>
      <p style={{ margin: 0, fontSize: 14, color: "#cbd5e1", maxWidth: 1280, textAlign: "center" }}>
        Adventures save in this browser on this device. Tap ⤓ on a slot to back one up.
      </p>
    </div>
  );
}

